// PRD-010 §T2.3 wiring note: @o2d/history `History<TState>` exposes only
// `push/undo/redo/clear/onChange`. `onChange` is parameter-less and fires for
// undo/redo too, so it cannot identify the applied command. There is no
// `afterPush(cmd)` hook in the shared package. We therefore wrap the inner
// `History.push` via {@link HistoryDispatchAdapter}, installed in
// `connectedCallback()` and torn down in `disconnectedCallback()` (no
// constructor-time monkey-patch — fixes the PRD-009 SPA-remount leak).
// Wrapping the INNER history (not the outer `HistoryController`) is uniform:
// every controller (sticky/rectangle/ellipse/checkbox/person/smiley/image/
// line/textbox placement+lifecycle/editor) is constructed with
// `this.history.history` and calls `history.push(cmd, state)` directly, so
// the wrapper fires for all paths. Element call sites that need typed extras
// (Duplicate: sourceUids) use `this._historyAdapter.push(cmd, state, extras)`
// — extras are passed as an immutable parameter (Pattern A) instead of via
// the race-prone `_pendingExtras` field that PRD-009 used. ZOrder's
// `toIndex` is computed inside the adapter's `_onPush` callback (post-push
// read from `boardState`).
import {
  AbstractO2dElement,
  html,
  customElement,
  property,
  state,
  generateShortId,
  type InjectedButton,
  type TemplateResult,
  type PropertyValues,
} from '@o2d/shared';
import { nothing } from 'lit';
import { HistoryController, registerUndoRedoShortcuts, CompositeCommand, type Command } from '@o2d/history';
import Konva from 'konva';
import { debounceTime, takeUntil } from 'rxjs';
import { BoardState } from '../core/state/board-state.js';
import { wireHoverCursor, wireHoverFill } from '../core/konva/hover-cursor.js';
import { ViewportController } from '../core/viewport/viewport.controller.js';
import { BoardFocusController } from '../core/focus/board-focus.controller.js';
import { WhiteboardBus } from '../core/bus/whiteboard-bus.js';
import { SelectionBusAdapter } from '../core/bus/selection-bus-adapter.js';
import { BoardStateBusAdapter } from '../core/bus/board-state-bus-adapter.js';
import { ToolBusAdapter } from '../core/bus/tool-bus-adapter.js';
import { resolveProperty } from '../optionsbar/optionsbar-config.js';
import { ViewportBusAdapter } from '../core/bus/viewport-bus-adapter.js';
import { KeyboardShortcutsController } from '../core/keyboard/keyboard-shortcuts.controller.js';
import { ToolController } from '../core/tool/tool.controller.js';
import { SelectionController } from '../core/selection/selection.controller.js';
import { InteractionController, type WorldBox } from '../core/interaction/interaction.controller.js';
import { ObjectRegistry, type SliceRenderContext } from '../core/object-registry.js';
import { applyBaseGeometry } from '../core/render/apply-base-geometry.js';
import { EditModeController } from '../core/edit/edit-mode.controller.js';
import { stickySlice } from '../features/sticky/sticky.slice.js';
import { StickyPlacementController } from '../features/sticky/sticky-placement.controller.js';
import { rectangleSlice } from '../features/rectangle/rectangle.slice.js';
import { RectanglePlacementController } from '../features/rectangle/rectangle-placement.controller.js';
import { ellipseSlice } from '../features/ellipse/ellipse.slice.js';
import { EllipsePlacementController } from '../features/ellipse/ellipse-placement.controller.js';
import { checkboxSlice } from '../features/checkbox/checkbox.slice.js';
import { CheckboxPlacementController } from '../features/checkbox/checkbox-placement.controller.js';
import { personSlice } from '../features/person/person.slice.js';
import { PersonPlacementController } from '../features/person/person-placement.controller.js';
import { smileySlice } from '../features/smiley/smiley.slice.js';
import { SmileyPlacementController } from '../features/smiley/smiley-placement.controller.js';
import { imageSlice } from '../features/image/image.slice.js';
import { fitImageWorldSize } from '../features/image/image-defaults.factory.js';
import { ImagePlacementController } from '../features/image/image-placement.controller.js';
import { LineLifecycleController } from '../features/line/line-lifecycle.controller.js';
import { TextboxLifecycleController } from '../features/textbox/textbox-lifecycle.controller.js';
import { textboxSlice } from '../features/textbox/textbox.slice.js';
import { lineSlice } from '../features/line/line.slice.js';
import { isLine } from '../features/line/line.types.js';
import type { LineObject } from '../features/line/line.interface.js';
import { ScaleLineCommand, type LineScaleSnapshot } from '../features/line/line.commands.js';
import { LinePathStrategyRegistry } from '../features/line/strategies/line-path-strategy.interface.js';
import { ImagePasteHandler } from '../features/image/image-paste.handler.js';
import { pngBlobForClipboard } from '../features/image/image-clipboard-blob.js';
import { planClipboardImage } from '../core/state/clipboard-image-plan.js';
import { writeImageWithFallback } from '../core/state/clipboard-write-fallback.js';
import {
  WHITEBOARD_CLIPBOARD_IMAGE_SKIPPED_EVENT,
  type WhiteboardClipboardImageSkippedEventDetail,
} from '../types/whiteboard-clipboard-image-skipped-event.types.js';
import type { ImageUploadHandler, PasteChoiceHandler } from '../features/image/image.interface.js';
import { StickyEditorController } from '../features/sticky/sticky-editor.controller.js';
import { TextboxEditorController } from '../features/textbox/textbox-editor.controller.js';
import { PlainEditController } from '../core/edit/plain-edit.controller.js';
import { objectId } from '../core/bus/branded-types.js';
import { AddObjectCommand } from '../core/command/add-object.command.js';
import {
  GroupObjectsCommand,
  UngroupObjectsCommand,
  captureMemberships,
  directMemberIds,
  groupsOf,
} from '../core/command/group-objects.command.js';
import {
  groupingCandidates,
  ungroupTargets,
  canGroup,
  canUngroup,
  boundingBox,
  expandToMembers,
  expandForTransfer,
  clickSelectionFor,
  toggleUnitSelection,
  cloneWithGroups,
  isGroup,
  groupMembers,
} from '../core/state/group.helper.js';
import { createTextBoxDefaults } from '../features/textbox/textbox-defaults.factory.js';
import type { TextDocument } from '../features/textbox/text-document.type.js';
import { RemoveObjectWithAnchorsCommand } from '../core/command/remove-object-with-anchors.command.js';
import { MoveObjectsCommand, type Move } from '../core/command/move-objects.command.js';
import { computeAlignMoves, type AlignMode } from '../core/command/align-objects.helper.js';
import { NudgeObjectsCommand } from '../core/command/nudge-objects.command.js';
import { ResizeObjectCommand, type ResizeSnapshot } from '../core/command/resize-object.command.js';
import { UpdatePropertyCommand, type MutableObjectKey } from '../core/command/update-property.command.js';
import { DuplicateObjectsCommand } from '../core/command/duplicate-objects.command.js';
import { ZOrderCommand } from '../core/command/z-order.command.js';
import { serializeCommand, type SerializeCommandExtras } from '../core/command/serialize-command.helper.js';
import type { WhiteboardCommandEventDetail } from './whiteboard-command.event.js';
import { WHITEBOARD_CHANGED_EVENT, type WhiteboardChangedEventDetail } from '../types/whiteboard-changed-event.types.js';
import { HistoryDispatchAdapter } from './history-dispatch-adapter.js';
import { SetListTypeCommand } from '../features/textbox/textbox.commands.js';
import { reshapeDocumentListType, EMPTY_PARAGRAPH_DOC } from '../features/textbox/text-document-migration.js';
import { applyWholeDocumentFormat, type WholeDocFormat } from '../features/textbox/text-document-format.js';
import { ApplyWholeTextFormatCommand } from '../core/command/apply-whole-text-format.command.js';
import { clipboard } from '../core/state/clipboard.service.js';
import {
  encodeClipboardPayload,
  decodeClipboardPayload,
} from '../core/state/clipboard-payload.js';
import {
  PERSON_ICON_RATIO,
  PERSON_ICON_TEXT_GAP,
  PERSON_NAME_TEXT,
} from '../features/person/person.renderer.js';
import { PERSON_FONT_SIZE, PERSON_DEFAULT_FONT_WEIGHT } from '../features/person/person.types.js';
import { creationDefaults, fontIndexToWorldUnits } from '../core/viewport/zoom-cluster.service.js';
import { DEFAULT_ZOOM, zoomToScaleAt } from '../core/viewport/viewport-math.helper.js';
import type { SelectionFormatKey } from '../features/textbox/textbox-formats.js';
import type { TextBoxGroup } from '../features/textbox/textbox.renderer.js';
import {
  SNAP_SCREEN_PX,
  HOVER_CORNER_SCREEN_PX,
  DUPLICATE_OFFSET_PX,
  HANDLE_SIZE_PX,
  HANDLE_FILL,
  HANDLE_STROKE,
  HANDLE_HOVER_FILL,
  BOARD_TEXT_LINE_HEIGHT,
} from '../constants/whiteboard-config.constants.js';
import { isRotateAnchor, styleRotateAnchor } from '../core/konva/rotate-anchor.js';
import { isRatioLocked, selectionAllowsAxisResize } from '../core/konva/ratio-lock.js';
import { NormaliseZoomCommand } from '../core/command/normalise-zoom.command.js';
import { buildBoardSvg } from '../core/export/board-to-svg.js';
import { GridController } from '../core/grid/grid.renderer.js';
import { snapPointToGrid, snapValueToGrid, snapSizeToGrid } from '../core/grid/grid-snap.helper.js';
import { findSnap, isAnchorTarget, anchorBoundsOf } from '../objects/line-anchor.js';
import { AnchorSyncController } from '../core/anchor/anchor-sync.controller.js';
import { LineEndpointHandlesController } from '../features/line/line-endpoint-handles.controller.js';
import { LineMidSegmentHandleController } from '../features/line/line-mid-segment-handle.controller.js';
import { LineCurveHandleController } from '../features/line/line-curve-handle.controller.js';
import { ObjectConnectorHandlesController } from '../objects/object-connector-handles.controller.js';
import type { BoardObject, TextBox, LineAnchor, ListType, PersonObject, GroupObject } from '../core/interfaces/board-object.interface.js';
import type { Tool } from '../core/tool/tool.types.js';

type TransformerAnchor = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

/**
 * The four corner anchors — proportional-capable resize, and the only set a
 * hover preview or a multi-selection ever offers.
 */
const CORNER_ANCHORS: readonly string[] = [
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
];

/**
 * Corners PLUS the four edge-midpoint anchors (Anke, 2026-07-18): dragging a
 * middle anchor changes ONLY that axis — the side ones the width, the top/bottom
 * ones the height. Konva constrains a middle anchor to its own axis natively, and
 * `onTransformEnd` already commits width and height independently, so this needs
 * no special resize maths.
 *
 * They sit ON the edge like the corner handles, which puts them right beside the
 * round line-docking handles — those float `CONNECTOR_HANDLE_OFFSET_PX` OUTSIDE
 * the same edge midpoint, so the two are neighbours, not overlaps.
 *
 * Deliberately NOT used for:
 *   - multi-selection — group scaling stays proportional on the corners, because
 *     squashing a group distorts every object in it (fonts, stroke widths) and is
 *     near-impossible to undo by eye;
 *   - the smiley — it is round and locked to 1:1, so an axis-only handle would
 *     either do nothing or turn it into an ellipse;
 *   - the hover-corner preview — that is a single-corner affordance by design.
 */
const CORNER_AND_EDGE_ANCHORS: readonly string[] = [
  ...CORNER_ANCHORS,
  'middle-left',
  'middle-right',
  'top-center',
  'bottom-center',
];

// O2D.W-PRD-038 — read-only edit-overlay layout metrics returned by the
// `__editOverlayMetrics` E2E hook (real-Chromium line-box measurement).
interface EditOverlayMetrics {
  readonly scrollHeight: number;
  readonly clientHeight: number;
  readonly lineAdvance: number;
  readonly lineCount: number;
}
import { TOOL_CHANGED_EVENT } from '../core/tool/tool.types.js';
import { SELECTION_CHANGED_EVENT, type SelectionChangedDetail } from '../core/selection/selection.types.js';

// PRD-H §T11: Side-effect imports register the wrapper sub-elements as
// custom elements so the wrapper render-tree (T13) can instantiate them
// via Lit templates without requiring the host application to import
// them separately.
import './o2d-whiteboard-toolbar.element.js';
import '../optionsbar/o2d-svg-optionsbar.element.js';
import type { O2dWhiteboardToolbarElement } from './o2d-whiteboard-toolbar.element.js';
import type { O2dSvgOptionsbarElement } from '../optionsbar/o2d-svg-optionsbar.element.js';

// PRD-F6 §T20: Whitelist of MutableObjectKey strings that map onto the
// SelectionFormatKey discriminated arm. Used to decide whether
// updateSelectedProperty should additionally dispatch
// `textbox/apply-format` so TextboxEditorController can apply the format
// to the current contentEditable selection.
const SELECTION_FORMAT_KEYS: ReadonlySet<string> = new Set([
  'color',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'textDecoration',
  'textTransform',
  'verticalScript',
]);

function isSelectionFormatKey(key: string): key is SelectionFormatKey {
  return SELECTION_FORMAT_KEYS.has(key);
}

/**
 * Map an optionsbar property key + value to the whole-document format transform
 * plus the matching container-default property (so freshly typed text inherits
 * it and the options-circle highlight matches). Returns `null` for keys that
 * have no whole-box document representation. `fontSize` is handled by
 * `applyFontLevelToSelection` (it needs the level→world-unit conversion), not here.
 */
function wholeDocFormatSpec(
  key: string,
  value: unknown,
): { fmt: WholeDocFormat; containerKey?: string; containerValue?: unknown } | null {
  switch (key) {
    // color / hAlign are genuine container defaults (used by rendering AND the
    // options-circle highlight), so they are written alongside the document.
    case 'color':
      return { fmt: { kind: 'textStyle', attrs: { color: String(value) } }, containerKey: 'color', containerValue: value };
    case 'hAlign':
      return { fmt: { kind: 'blockAttr', key: 'textAlign', value: String(value) }, containerKey: 'hAlign', containerValue: value };
    // The inline mark formats live ONLY as per-run document marks (DD3): an
    // object-level write would double-store the format. We push them onto the
    // whole document but deliberately do NOT touch the container property.
    case 'fontWeight':
      return { fmt: { kind: 'textStyle', attrs: { fontWeight: String(value) } } };
    case 'textTransform':
      return { fmt: { kind: 'textStyle', attrs: { textTransform: String(value) } } };
    case 'fontStyle':
      return value === 'italic' ? { fmt: { kind: 'mark', mark: 'italic' } } : null;
    case 'textDecoration':
      if (value === 'underline') return { fmt: { kind: 'mark', mark: 'underline' } };
      if (value === 'line-through') return { fmt: { kind: 'mark', mark: 'strike' } };
      return null;
    default:
      return null;
  }
}

/**
 * `<o2d-whiteboard>` — Miro-like infinite board.
 *
 * M4 scope on top of M3:
 *   - Sticky-note placement when the sticky tool is active
 *   - Single-object selection (click in select mode); click empty to clear
 *   - Konva.Transformer shows selection bbox and four proportional corner handles
 *   - Drag on a selected object → MoveObjectsCommand
 *   - Resize on a corner handle → ResizeObjectCommand (keepRatio)
 *   - Delete/Backspace removes all selected objects
 *   - Multi-selection via Shift/Ctrl-click still lives as data, but drag &
 *     resize are single-object in M4; M8 will turn on real multi-select.
 */
@customElement('o2d-whiteboard')
export class O2dWhiteboardElement extends AbstractO2dElement {
  // PRD-X §TR4 / AC4: top-level readonly switch. Reflects to the HTML
  // attribute so downstream tooling (e.g. the optionsbar in TR8) can
  // observe the state without subscribing to the bus. Lock-path wiring
  // for controllers (toolCtrl, editModeCtrl, …) lands incrementally in
  // T6.4 / T7.2 / T8.5 once their `setReadonly()` APIs exist.
  @property({ type: Boolean, reflect: true }) readonly = false;

  /**
   * Anke, 2026-07-16 — Raster ein/aus. Solange es AN ist, rasten neu erstellte,
   * verschobene und skalierte Objekte am Raster ein (siehe #snapWorldPoint und
   * die Dreh-/Skalier-Nahtstellen). Als reflektierte Property nach dem Vorbild
   * von `readonly`: umschaltbar von aussen, und `updated()` reagiert darauf.
   */
  @property({ type: Boolean, reflect: true }) gridEnabled = false;


  /**
   * O2D.W-PRD-042 §TR4: optional upload seam for pasted images.
   *
   * When set, a pasted image is handed to this callback and the board places
   * the returned asset URL as `src` instead of embedding a base64 data-URL.
   * When unset, the paste falls back to the data-URL path (back-compat).
   *
   * `attribute: false` because a function cannot round-trip through an HTML
   * attribute. The element never inspects the callback — endpoint, auth scheme
   * and response shape beyond `{ src }` stay entirely with the host app.
   */
  @property({ attribute: false }) imageUploadHandler?: ImageUploadHandler;

  /**
   * Optional seam asking how to paste a clipboard that holds BOTH text and an image
   * (Anke, 2026-10-02). Resolves `'image'`, `'text'` or `null` (paste nothing). When
   * unset, the image wins as before. The host owns the dialog; the board only asks.
   */
  @property({ attribute: false }) pasteChoiceHandler?: PasteChoiceHandler;

  /**
   * O2D.P-PRD-021 §TR6: host-supplied toolbar buttons, forwarded declaratively
   * to the child `<o2d-whiteboard-toolbar>`. Clicking one emits
   * `injected-button-click` on the toolbar, which bubbles out of this wrapper.
   *
   * O2D.P-PRD-021 §TR7 reactivity contract: assign a NEW array to trigger a
   * re-render. Lit compares this property by identity, so mutating the existing
   * array in place (`push`, index assignment, changing a descriptor field) does
   * NOT re-render the toolbar.
   */
  @property({ attribute: false }) injectedButtons: InjectedButton[] = [];

  /**
   * Same contract as `injectedButtons` above, but passed through to the
   * toolbar's `injectedButtonsStart` — rendered at the toolbar's left edge,
   * before the internal tools, instead of grouped with the right-hand
   * injected buttons (Anke, 2026-08-17).
   */
  @property({ attribute: false }) injectedButtonsStart: InjectedButton[] = [];

  /**
   * Anke, 2026-09-14: the file name of the KnowledgeObject this board
   * belongs to, shown in a dedicated box directly below the toolbar — the
   * host (`knowledge-whiteboard.component.ts` in horstplan) passes the
   * object's own title in. `undefined`/empty renders no box at all (e.g. the
   * demo app, which never sets it) rather than an empty one.
   */
  @property({ type: String, attribute: 'file-name' }) fileName?: string;

  /**
   * Anke, 2026-09-15: whether `fileName` should be shown as unsaved — the
   * filename box then renders the name italic with a trailing superscript
   * asterisk (the word-processor convention: "Dokument.docx*"), instead of
   * a separate text hint. Only affects the box's own presentation; the host
   * still owns its actual save/dirty state and just reflects it here.
   */
  @property({ type: Boolean, attribute: 'file-name-unsaved' }) fileNameUnsaved = false;

  /**
   * PRD-G §T07: monotonically increasing counter that bumps every time
   * `config` is replaced via the setter. Plain `@state()` field so Lit
   * triggers a re-render whenever the board content is swapped wholesale.
   * Internal — exposed only as a render trigger, not as part of the public API.
   */
  @state() private _configVersion = 0;

  /**
   * PRD-009 §T5.2 / §Version Counter: per-element monotonic counter
   * incremented on each dispatched `whiteboard-command` CustomEvent.
   * Starts at 0 — first dispatched event carries `version: 1`. NOT reset
   * on `disconnectedCallback` (re-attach continues the sequence). NOT
   * related to backend `KnowledgeObject.version`.
   */
  private _commandVersion = 0;

  readonly boardState = new BoardState();
  private readonly history = new HistoryController<BoardState>(this, { state: this.boardState });

  /**
   * PRD-010 §T2.3 — Adapter that wraps `inner.push` for lifecycle-managed
   * dispatch. Replaces the constructor-time `_wireHistoryDispatch()` monkey
   * patch from PRD-009 (which leaked on remount) and the race-prone
   * `_pendingExtras` field (extras now flow as an explicit immutable
   * parameter via `adapter.push(cmd, state, extras?)` — Pattern A).
   *
   * `attach()` runs in `connectedCallback()`, `detach()` in
   * `disconnectedCallback()`. See ADR-003.
   *
   * The `_onPush` callback enriches `ZOrderCommand` extras with the
   * post-apply `toIndex` read from `boardState` — this enrichment must
   * happen for BOTH adapter-driven element pushes AND controller-driven
   * `inner.push(cmd, state)` calls (placement controllers etc.), which is
   * why it lives in the callback and not at the element call sites.
   */
  private readonly _historyAdapter = new HistoryDispatchAdapter<
    Command<BoardState>,
    BoardState,
    SerializeCommandExtras
  >(
    this.history.history,
    (cmd: Command<BoardState>, _state: BoardState, extras?: SerializeCommandExtras): void => {
      let finalExtras = extras;
      if (cmd instanceof ZOrderCommand) {
        finalExtras = {
          ...(finalExtras ?? {}),
          toIndex: this.boardState.getObjectIndex(cmd.objectId),
        };
      }
      this._dispatchCommandEvent(cmd, finalExtras);
    },
  );
  private readonly toolCtrl = new ToolController(this);
  private readonly selectionCtrl = new SelectionController(this);
  /** Per-element slice-plugin registry (TR8). Slices register in firstUpdated. */
  private readonly registry = new ObjectRegistry();
  /**
   * Per-element line path-strategy registry (PRD-F5 §TR3/TR8b/TR14).
   * Created in `firstUpdated` and populated by `lineSlice.registerStrategies`.
   * Held on the element so future line-options UI (T26) can read available
   * strategies without re-deriving them from the slice.
   */
  private linePathStrategyRegistry?: LinePathStrategyRegistry;
  // PRD-X §T8.5: stored refs so the `readonly`-property change handler can
  // propagate the lock state to every guard surface (TR5/TR6/TR7/TR9).
  #interactionCtrl?: InteractionController;
  #editModeCtrl?: EditModeController;
  #stickyEditorCtrl?: StickyEditorController;
  #textboxEditorCtrl?: TextboxEditorController;
  #plainEditCtrl?: PlainEditController;
  #viewportCtrl?: ViewportController;
  // PRD-H §T12/T15: refs to the wrapper-rendered sub-elements. Populated
  // in firstUpdated() after the bus is initialized so the assignments
  // `.board = this` cascade the bus reference into the children.
  #toolbar?: O2dWhiteboardToolbarElement;
  #optionsbar?: O2dSvgOptionsbarElement;

  private stage?: Konva.Stage;
  private backgroundLayer?: Konva.Layer;
  /** Zeichnet das Raster in die Hintergrund-Ebene (Anke, 2026-07-16). */
  #gridCtrl?: GridController;
  /**
   * Dedicated layer for the Transformer (resize handles) and line endpoint
   * handles.  Sits *below* contentLayer so selection visuals never cover
   * board objects that happen to lie on top of the selected one.
   */
  private selectionLayer?: Konva.Layer;
  private contentLayer?: Konva.Layer;
  private overlayLayer?: Konva.Layer;
  private transformer?: Konva.Transformer;
  /** Owns anchored-line live/commit sync (core/anchor/anchor-sync.controller.ts).
   *  Stored so `onTransformLive` can drive its resize-specific live path
   *  (`applyLiveResize`) directly instead of going through the generic
   *  `drag/move` UiAction, which only models a pure translation.
   *
   *  Definite-assignment (`!`), not optional (`?`): it is always constructed
   *  synchronously in `firstUpdated()`, before the transformer wiring that
   *  is the only caller of `applyLiveResize` (same method body, see
   *  `wireTransformerEvents`/the `transformer.on('transform', …)` block
   *  below). Making this non-optional is deliberate (Review-Lauf 01,
   *  Linse A, MINOR): with `?:` + `?.` at the call site, a future reordering
   *  of that initialisation could silently drop the call — no error, no
   *  warning, just the exact live-resize-freeze bug this field exists to
   *  fix, reintroduced without a trace. A missing assignment now surfaces as
   *  a real `undefined` access instead. */
  private anchorSync!: AnchorSyncController;
  /** Live preview shape shown while the user drags to create a new object.
   *  Rect for most tools, Ellipse when the ellipse tool is active. */
  private dragPreviewRect?: Konva.Shape;
  /** Live preview line shown while the user draws a new line. */
  private linePreviewLine?: Konva.Line;
  /** Small dot shown while dragging a line endpoint over a snap target. */
  private snapIndicator?: Konva.Circle;
  /** Candidate anchor captured during the current line-preview drag (drag-to-create). */
  private pendingLineAnchor: { endpoint: 'start' | 'end'; anchor: LineAnchor; x: number; y: number } | null = null;
  /** Dashed rectangle shown while the user drags a marquee selection. */
  private marqueeRect?: Konva.Rect;
  /**
   * Anke, 2026-07-21 — Einzelrahmen bei ungruppierter Mehrfachauswahl.
   *
   * Der Transformer zeichnet EINEN Rahmen um die gesamte Auswahl. Bei mehreren
   * NICHT gruppierten Objekten sagt der aber nicht, WAS eigentlich ausgewaehlt
   * ist — zwischen "drei Objekte einzeln markiert" und "eine Gruppe" war kein
   * Unterschied zu sehen. Deshalb zusaetzlich ein Rahmen je beteiligter Einheit.
   * Bei einer Gruppe bleibt es beim gemeinsamen Rahmen: Dort IST die Auswahl
   * eine Sache, und Einzelrahmen wuerden genau das wieder aufloesen.
   */
  #selectionOutlines?: Konva.Group;

  /** The object + corner the transformer is currently previewing on hover. */
  private hoverCornerTarget?: { id: string; corner: TransformerAnchor };
  /** After a hover-preview resize ends, promote this id to the real selection. */
  private promoteOnTransformEnd?: string;
  /** True while the transformer is actively resizing — hover updates pause. */
  private isTransforming = false;
  /** HTML contentEditable element positioned over the sticky being edited. */
  private textEditor?: HTMLDivElement;
  private textEditorTargetId?: string;
  /**
   * Anke, 2026-09-11 — a second plain click (not a drag) on an ALREADY
   * selected textbox enters edit mode, matching "click to select, click
   * again to edit" — see `onClickAlreadySelected` below and
   * `InteractionController.onStageClick`, which is what identifies "already
   * selected" and routes here.
   *
   * Deferred by `Konva.dblClickWindow` (the same window Konva itself uses
   * to decide whether two clicks become a `dblclick`) and cancelled if a
   * genuine double-click on the SAME textbox follows within it — mirrors
   * `wireObjectNode`'s own checkbox-click deferral for the identical
   * reason: the second click of a real double-click on an unselected
   * textbox is ALSO "a click on an already-selected object" (the first
   * click just selected it), so without this guard a plain double-click
   * would fire `edit/enter-textbox` twice in a row (once from here, once
   * from `onNodeDoubleClick`) — harmless end-state (both converge on the
   * same editor), but a wasteful, visibly redundant editor-DOM rebuild.
   */
  #textboxClickTimer: ReturnType<typeof setTimeout> | null = null;

  /** Mapping id → Konva node, so BoardState events can update/remove surgically. */
  private readonly konvaNodes = new Map<string, Konva.Node>();

  /** Snapshots captured on dragstart / transformstart, keyed by object id. */
  private dragStartPositions = new Map<string, { x: number; y: number }>();
  /** Per-object snapshots captured at transformstart; supports single and multi-select. */
  private transformStartSnapshots = new Map<string, ResizeSnapshot>();

  /** Event-unsubscribe callbacks, all torn down in `disconnectedCallback`. */
  private readonly teardown: Array<() => void> = [];

  private resizeObserver?: ResizeObserver;

  /** Central RxJS bus. Created in connectedCallback(), destroyed in disconnectedCallback() last. */
  #bus?: WhiteboardBus;
  #boardStateBusAdapter?: BoardStateBusAdapter;
  #selectionBusAdapter?: SelectionBusAdapter;
  #toolBusAdapter?: ToolBusAdapter;
  #viewportBusAdapter?: ViewportBusAdapter;

  /**
   * Tracks whether `connectedCallback()` is running for the first time on this
   * element instance (W1 PRD-H1, T09). Used to skip the reconnect-only
   * `requestUpdate()` trigger on the initial mount — Lit's regular render
   * pipeline already fires `updated()` after `firstUpdated()` places the
   * sub-elements in Light-DOM, so a premature `requestUpdate()` would run
   * `#injectBusIntoChildren()` against a still-empty render root.
   */
  #firstConnect = true;

  /**
   * PRD-011 §G3: baseline content signature captured on `config` assignment
   * and on `markSaved()`. Compared against the live signature on every
   * debounced `changed` emit to derive `isDirty`.
   *
   * Initialized to the signature of an empty board (review-finding M4) so a
   * fresh element that hosts treat as "saved-empty" (no `config` set) reports
   * `isDirty=false` after a push+undo round-trip returns to `[]`. Methods are
   * on the prototype and safe to call from a class-field initializer.
   */
  #savedContentSignature: string = this.#computeSignature([]);

  /**
   * PRD-011 §G2 / consumer PRD-077 contract: tracks the last `readonly
   * BoardObject[]` reference assigned via the `config` setter. Used for
   * the reference-equality short-circuit (identical reference re-assign =
   * no-op, no `replaceAll` / no `history.clear`).
   */
  #lastAssignedConfigRef: readonly BoardObject[] | null = null;

  /**
   * PRD-011 §G3: deterministic content signature for dirty-tracking.
   *
   * **Call-site invariant (PRD-011 T1.11 + review-finding M2):** this helper
   * is invoked from EXACTLY five call sites (declaration + four callers) and
   * NEVER from a mutation path, a Lit reactive getter, or the `config`
   * getter. Each caller is a per-event or per-user-action work unit; adding
   * a fifth caller or invoking from any per-mutation hot path is a failure
   * condition — the perf contract (Goal 3: p95 < 5ms on 200 plain-geometry
   * BoardObjects) holds by construction only as long as signatures are
   * computed at most once per debounced cycle / per save click.
   *
   *   1. `#savedContentSignature` field initializer (empty-board baseline, M4)
   *   2. `set config(...)` legitimate-assignment path (baseline rebase)
   *   3. `markSaved(snapshot?)` (baseline rebase)
   *   4. `_emitChanged()` (live signature for `isDirty` derivation)
   *   5. `getSnapshot()` (M2 — synchronous save-button bypass)
   *
   * Verify with:
   *   grep -nE '#computeSignature\(' packages/whiteboard/src
   * → must return exactly 5 call sites + the declaration line inside
   * `o2d-whiteboard.element.ts`.
   *
   * `JSON.stringify` is sufficient because `BoardObject` is a plain-object
   * discriminated union — no `Map`/`Set`/`Date` members. If that
   * invariant changes, switch to a sorted-key serializer (tracked as a
   * follow-up; see PRD failure conditions).
   */
  #computeSignature(objects: readonly BoardObject[]): string {
    return JSON.stringify(objects);
  }

  /**
   * Public read-only accessor for the central RxJS bus (TR12).
   *
   * Returns `undefined` before `firstUpdated()` runs and after
   * `disconnectedCallback()` tears the bus down. Consumers (e.g. the toolbar)
   * MUST handle the `undefined` case and degrade gracefully — typically by
   * skipping the dispatch when the board is not yet wired.
   */
  get bus(): WhiteboardBus | undefined {
    return this.#bus;
  }

  /**
   * O2D.W-PRD-035 §TR2 — whether a textbox rich-text editor is currently open.
   * Surfaces the `TextboxEditorController`'s `#editingId` (via its public
   * `editingId` accessor) so the optionsbar can route a list-button click to
   * the live editor while editing, and to the whole-textbox `setListType`
   * command when not editing (D2).
   */
  get isTextboxEditing(): boolean {
    return this.#textboxEditorCtrl?.editingId != null;
  }

  /**
   * PRD-G §T07/G1: public read-only snapshot of all board objects.
   *
   * Returns the current `BoardState.allObjects()` view as a `readonly`
   * array — equivalent to the future `WhiteboardConfig` alias. The setter
   * (T08) replaces the entire content atomically via `replaceAll`. Reads
   * stay cheap because `allObjects()` already returns a `ReadonlyArray`.
   */
  get config(): readonly BoardObject[] {
    // _configVersion read keeps Lit's reactivity dependency-tracking happy
    // when the getter is consumed inside a render expression.
    void this._configVersion;
    return this.boardState.allObjects();
  }

  /**
   * PRD-G §T08/G1: atomic content replacement entry-point for host apps.
   *
   * Order of operations matches PRD-G FC-8 / FC-15:
   *   1. Commit any in-flight textbox editor (otherwise the editor would
   *      outlive its now-detached BoardObject).
   *   2. `boardState.replaceAll()` swaps the object map and emits
   *      `state-replaced`, which the renderer subscriber (T06) uses to tear
   *      and rebuild Konva nodes.
   *   3. `history.clear()` drops undo/redo stacks — replacement is not a
   *      reversible user action.
   *   4. Bump `_configVersion` so Lit re-renders any template that reads
   *      `config`, and explicitly notify Lit via `requestUpdate('config')`.
   */
  set config(value: readonly BoardObject[]) {
    // PRD-011 T1.8: reference-equality short-circuit. Assigning the SAME
    // array reference twice is a no-op — no rehydration, no replaceAll, no
    // history.clear(). This protects consumer patterns (e.g. PRD-077) that
    // re-assign the current snapshot defensively without intent to reset.
    if (value === this.#lastAssignedConfigRef) return;
    this.#textboxEditorCtrl?.commitActive();
    // PRD-RTE-TIPTAP-document-model §T2.5: run each object through its
    // slice's optional `hydrate` hook so legacy payloads (e.g. textbox
    // `text` HTML) are up-converted into the canonical in-memory shape
    // (TipTap `document`) before they enter BoardState.
    const hydrated = value.map((obj) => {
      const plugin = this.registry.get(obj.type);
      if (plugin?.hydrate) return plugin.hydrate(obj);
      return obj;
    });
    this.boardState.replaceAll(hydrated);
    this.history.clear();
    this._configVersion++;
    this.requestUpdate('config');
    // PRD-011 T1.8: rebase dirty-tracking baseline AFTER the legitimate
    // assignment path. The signature is computed over the post-hydration
    // snapshot (via `this.config`) so subsequent `changed` emits compare
    // apples to apples.
    this.#lastAssignedConfigRef = value;
    this.#savedContentSignature = this.#computeSignature(this.config);
    // Anke, 2026-07-16: Beim Oeffnen wird immer auf alles gezoomt — eine
    // gespeicherte Ansicht wird nicht wiederhergestellt. Dieser Setter ist der
    // gemeinsame Weg BEIDER Oeffnen-Arten (Anwendung setzt Inhalte;
    // `importBoard` setzt sie ebenfalls hierueber), deshalb genuegt eine
    // Stelle. Nur vormerken statt sofort zoomen — siehe #tryFitOnOpen.
    this.#fitOnOpenPending = true;
    this.#tryFitOnOpen();
  }

  /**
   * Offen: "auf alles zoomen" ist vorgemerkt, konnte aber noch nicht laufen.
   *
   * Zwei Dinge muessen dafuer stimmen, und beide koennen beim Oeffnen fehlen:
   *  - Die Konva-Buehne muss existieren. Setzt eine Anwendung `config` in ihrem
   *    eigenen `willUpdate`, laeuft das VOR `firstUpdated()` (derselbe Fall, den
   *    die Initial-Mount-Nachzieh-Schleife in `firstUpdated` abdeckt).
   *  - Die Buehne muss eine ECHTE Groesse haben. Ohne Layout legt `firstUpdated`
   *    sie mit `Math.max(1, ...)` als 1x1 an; ein Zoom auf 1x1 wuerde auf
   *    `minScale` klemmen und das Board winzig oeffnen. Die echte Groesse kommt
   *    erst per ResizeObserver nach.
   */
  #fitOnOpenPending = false;

  #tryFitOnOpen(): void {
    if (!this.#fitOnOpenPending) return;
    if (!this.stage) return;
    if (this.stage.width() <= 1 || this.stage.height() <= 1) return;
    // Ab hier ist geoeffnet — auch wenn nichts da war, worauf man zoomen kann
    // (`fitToContent` steigt bei leerem Board aus). Die Vormerkung faellt
    // trotzdem, sonst wuerde das erste spaeter angelegte Objekt die Ansicht
    // ungefragt springen lassen.
    this.#fitOnOpenPending = false;
    this.fitToContent();
  }

  /**
   * PRD-011 §G3: rebase the dirty-tracking baseline to a known-saved
   * snapshot. Hosts call this after a successful persist round-trip — the
   * NEXT debounced `changed` emit recomputes `isDirty` against this new
   * baseline.
   *
   * Race-protection overload (PRD-011 Failure Conditions): pass the exact
   * `readonly BoardObject[]` snapshot that was persisted as `snapshot`.
   * Without it, the baseline is captured from the LIVE `this.config` — if
   * the user kept editing during the in-flight save, those unsaved edits
   * would silently become the new baseline and `isDirty` would falsely
   * report `false`. The `snapshot` overload makes the host explicit:
   * "rebase against THIS data, not whatever is currently live".
   *
   * @param snapshot Optional persisted snapshot. When omitted, the current
   *   `this.config` is used as the baseline.
   */
  markSaved(snapshot?: readonly BoardObject[]): void {
    this.#savedContentSignature = this.#computeSignature(snapshot ?? this.config);
  }

  /**
   * PRD-011 / review-finding M2: synchronous snapshot getter that bypasses
   * the 300ms `changed`-event debounce. Consumers MUST call this at
   * save-button-click time to avoid a stale-cache race — the cached
   * `changed` event detail may lag up to 300ms behind active editing,
   * which means a click that immediately follows a keystroke would
   * persist the pre-keystroke snapshot.
   *
   * Returns the SAME detail shape as the `whiteboard-changed` event (computed
   * via the same `#computeSignature` helper, so the call-site invariant on
   * that method holds — this is the fifth permitted call site and is
   * documented there).
   *
   * **Shared-reference hazard:** The returned `objects` array is a fresh
   * array, but the contained `BoardObject` instances are LIVE references
   * aliased with the whiteboard's internal `BoardState` map. Mutating a
   * returned entry (e.g. `snap.objects[0].x = 999`) corrupts board state
   * silently — bypasses bus events, history push, and renderer notify.
   * Treat the result as read-only; clone before mutation if needed.
   */
  public getSnapshot(): WhiteboardChangedEventDetail {
    const objects = this.config;
    const signature = this.#computeSignature(objects);
    return {
      objects,
      isDirty: signature !== this.#savedContentSignature,
    };
  }

  /**
   * PRD-011 §G2/T1.10: emits the `whiteboard-changed` CustomEvent. Computes
   * the content signature ONCE per debounced cycle and compares against the
   * baseline to derive `isDirty`. Invoked only by the debounced subscriber
   * in `#initBus()` — never per mutation.
   *
   * PRD-011 §T1.12 invariants (clean-code budget — single level of abstraction):
   *   - Body MUST stay ≤ 15 lines.
   *   - Reads ONLY `this.config` and `this.#savedContentSignature`.
   *   - Delegates ALL serialization to `#computeSignature`. NEVER inline
   *     `JSON.stringify` here — that would split serialization across two
   *     call sites and break the call-site invariant documented on
   *     `#computeSignature` (exactly 4 permitted call sites).
   *
   * @internal — review-finding m5: this is a package-internal hot path
   *   exposed as `protected` so the perf microbenchmark in
   *   `whiteboard-dirty-tracking.test.ts` can invoke it directly without
   *   the `as unknown as { … }` cast. Consumers MUST NOT call this — use
   *   `getSnapshot()` (M2) or listen for `whiteboard-changed` instead.
   */
  protected _emitChanged(): void {
    // Cache `this.config` once: the getter materializes a fresh array from
    // BoardState's internal Map on every call, so reading it twice would
    // double the per-emit allocation cost on the perf hot path (PRD-011 §T1.4).
    const objects = this.config;
    const signature = this.#computeSignature(objects);
    this.dispatchEvent(new CustomEvent<WhiteboardChangedEventDetail>(WHITEBOARD_CHANGED_EVENT, {
      detail: {
        objects,
        isDirty: signature !== this.#savedContentSignature,
      },
      bubbles: true,
      composed: true,
    }));
  }

  protected override render(): TemplateResult {
    // PRD-H §T13: wrapper renders Toolbar / board-root / Optionsbar as
    // Light-DOM children. No `part` attributes (Light-DOM has no
    // ShadowRoot::part() boundary). Lit ChildPart-diff keeps the custom
    // element instances stable across re-renders, so subscribeToBoard()
    // subscriptions are not duplicated.
    //
    // Stefan, 2026-08-29 — `<o2d-whiteboard-actions>` removed: its remaining
    // callers (`group`/`image`/the legacy `rect` fixture — every OTHER type
    // had already migrated to its own options circle) already fall back to
    // the always-visible `default` options circle (part1: board commands +
    // z-order + align), which is generic and type-agnostic — verified in
    // `tests/components/optionsbar-default-circle-unregistered-types.test.ts`
    // before this removal, per Stefan's explicit request.
    return html`
      <o2d-whiteboard-toolbar .injectedButtons=${this.injectedButtons} .injectedButtonsStart=${this.injectedButtonsStart} .readonly=${this.readonly}></o2d-whiteboard-toolbar>
      ${this.fileName ? html`<div class="o2d-whiteboard-filename ${this.fileNameUnsaved ? 'o2d-whiteboard-filename--unsaved' : ''}">${this.fileName}${this.fileNameUnsaved ? html`<sup>*</sup>` : nothing}</div>` : nothing}
      <div class="board-root"></div>
      <o2d-svg-optionsbar></o2d-svg-optionsbar>
    `;
  }

  protected override firstUpdated(): void {
    // Keep node draggability in sync with the active tool. The ToolController
    // fires this event for every change — including internal `resetToSelect`
    // calls that bypass the host's setTool wrapper.
    this.addEventListener(TOOL_CHANGED_EVENT, () => this.applyNodeDraggability());

    const container = this.querySelector<HTMLDivElement>('.board-root');
    if (!container) throw new Error('o2d-whiteboard: board-root container not found.');

    const rect = container.getBoundingClientRect();
    this.stage = new Konva.Stage({
      container,
      width: Math.max(1, rect.width),
      height: Math.max(1, rect.height),
    });
    this.backgroundLayer = new Konva.Layer({ listening: false });
    this.selectionLayer = new Konva.Layer();
    this.contentLayer = new Konva.Layer();
    this.overlayLayer = new Konva.Layer();
    // Reihenfolge von unten nach oben. `selectionLayer` (der Transformer, also
    // die Eck-Anfasser) liegt ÜBER `contentLayer`.
    //
    // Anke, 2026-07-16 (Fehlerbericht "Anfasser verschwinden manchmal"): Sie lag
    // frueher DARUNTER, mit der Absicht, dass Anfasser nie Objekte ueberdecken,
    // die das ausgewaehlte ueberlappen. Der Preis dafuer war zu hoch: Sobald ein
    // Objekt ueber dem ausgewaehlten lag — von selbst oder durch "ganz nach
    // oben/unten" — verschwanden die Anfasser darunter. Und weil Konva Treffer
    // von der obersten Ebene abwaerts sucht, waren sie dort auch nicht mehr
    // anklickbar: Der Griff landete auf dem verdeckenden Objekt.
    //
    // Die Absicht von damals war ohnehin schon aufgegeben: Linien-Endpunkte,
    // Kurven-, Mittelstueck- und Verbinder-Anfasser liegen laengst in
    // `overlayLayer`, also ueber dem Inhalt. Der Transformer war der letzte
    // Anfasser unter dem Inhalt — die Ausnahme, nicht die Regel.
    // Anke, 2026-07-18: `selectionLayer` liegt jetzt ÜBER `overlayLayer`, nicht
    // mehr darunter. Grund sind die neuen Kanten-Anfasser: Sie sitzen auf der
    // Kantenmitte, genau dort, wo die runden Andock-Anfasser (in `overlayLayer`)
    // ihre grosszuegige unsichtbare Greiffläche haben — deren Greifradius von
    // rund 16 Punkten reicht ueber die Kantenmitte hinweg. Weil Konva Treffer von
    // der OBERSTEN Ebene abwaerts sucht, gewann der Andock-Anfasser, und die
    // neuen Anfasser waren zwar sichtbar, aber praktisch nicht greifbar.
    //
    // Mit dieser Reihenfolge gewinnt der Kanten-Anfasser in seinem eigenen
    // Quadrat (Transformer-Anker haben am Rechner keine zusaetzliche
    // Greifreserve, ihre Trefferflaeche IST das sichtbare Quadrat).
    //
    // Anke, 2026-09-29: Die Ueberlappung gibt es nicht mehr. Die Andock-Punkte
    // haben keinen unsichtbaren Greifrand mehr (Trefferflaeche = sichtbarer
    // Punkt, `LINE_HANDLE_SIZE_PX` = 14) und sitzen 22 statt 12 Punkte vor der
    // Kante; zwischen Kanten-Anfasser und Punkt liegen rund 10 Punkte Luft. Die
    // Reihenfolge bleibt trotzdem so — als zweite Absicherung, falls die beiden
    // wieder zusammenruecken.
    //
    // Fuer Linien entsteht kein Konflikt: dort haengt der Transformer gar nicht
    // am Objekt (`nodes([])`), seine Anker werden also weder gezeichnet noch
    // getroffen — die Endpunkt-, Kurven- und Mittelstueck-Anfasser behalten die
    // Oberhand wie bisher.
    this.stage.add(this.backgroundLayer);
    this.stage.add(this.contentLayer);
    this.stage.add(this.overlayLayer);
    this.stage.add(this.selectionLayer);

    // Raster in die Hintergrund-Ebene (unter allem Inhalt). Zustand aus der
    // Property uebernehmen — sie kann schon vor firstUpdated() gesetzt sein.
    this.#gridCtrl = new GridController(this.backgroundLayer);
    this.#gridCtrl.setEnabled(this.gridEnabled);

    this.transformer = new Konva.Transformer({
      // Corners scale freely on each axis; hold Shift during drag to lock
      // the ratio (Konva's built-in Shift modifier).
      keepRatio: false,
      rotateEnabled: false,
      enabledAnchors: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
      // Shared handle look (see HANDLE_* constants): the same white square with
      // the grey-blue outline as the line handles. Size differs on purpose (Anke,
      // 2026-09-29): resize anchors stay HANDLE_SIZE_PX (10), line handles are
      // LINE_HANDLE_SIZE_PX (14).
      anchorSize: HANDLE_SIZE_PX,
      anchorStroke: HANDLE_STROKE,
      anchorFill: HANDLE_FILL,
      borderStroke: HANDLE_STROKE,
      borderStrokeWidth: 1,
      // Anke, 2026-07-16: der Dreh-Anfasser ist ein Punkt mit Kreispfeil statt
      // eines Quadrats — er tut etwas anderes als die vier Eck-Anfasser und
      // soll das auch zeigen. Konva ruft diese Funktion am Ende jedes update()
      // fuer JEDEN Anker auf; die Eck-Anfasser bleiben bewusst unberuehrt.
      anchorStyleFunc: (anchor: Konva.Shape) => {
        if (isRotateAnchor(anchor)) styleRotateAnchor(anchor);
      },
    });
    this.selectionLayer.add(this.transformer);
    // Unter dem Transformer, damit dessen Anfasser obenauf bleiben.
    this.#selectionOutlines = new Konva.Group({ name: 'selection-outlines', listening: false });
    this.selectionLayer.add(this.#selectionOutlines);
    this.#selectionOutlines.moveToBottom();
    // Hover affordance for the square resize anchors: fill them with the
    // themeable hover colour while hovered. Anchors persist across selections,
    // so wiring once here is enough (the helper re-arms idempotently).
    this.wireTransformerAnchorHover();

    // Keep the stage sized to the host.
    this.resizeObserver = new ResizeObserver(() => {
      if (!this.stage) return;
      const r = container.getBoundingClientRect();
      this.stage.size({ width: Math.max(1, r.width), height: Math.max(1, r.height) });
      this.stage.batchDraw();
      // Neue Groesse = neuer sichtbarer Ausschnitt: Raster nachziehen.
      this.#gridCtrl?.redraw();
      // Erst hier hat die Buehne oft ihre echte Groesse — der Moment, in dem
      // ein vorgemerktes "auf alles zoomen" endlich sinnvoll rechnen kann.
      this.#tryFitOnOpen();
    });
    this.resizeObserver.observe(container);

    // Wire state → Konva nodes.
    this.teardown.push(
      this.boardState.on('object-added',     ({ object }) => this.onObjectAdded(object)),
      this.boardState.on('object-removed',   ({ id })     => this.onObjectRemoved(id)),
      this.boardState.on('object-updated',   ({ object }) => this.onObjectUpdated(object)),
      this.boardState.on('object-reordered', ({ id })     => this.onObjectReordered(id)),
      // Viewport changes (pan/zoom) trigger smiley emoji bitmap re-rasterisation
      // via the slice's updateNode (which honours the 5%-Re-rasterise-Schwelle
      // from PRD-F3 §TR6).
      this.boardState.on('viewport-changed', () => {
        // Die Buehne folgt dem Zustand. BoardState ist die Wahrheit fuer die
        // Ansicht (siehe viewport.controller.ts) — nur zog die Buehne das
        // bisher nicht von selbst nach, sondern jeder Aufrufer musste daran
        // denken. Wer es vergass, bekam eine Anzeige, die 150 % meldet, waehrend
        // das Bild in der alten Stufe steht. Genau das ist hier passiert
        // (importBoard trug einen eigenen Kommentar darueber) und wuerde beim
        // Rueckgaengigmachen von NormaliseZoomCommand wieder passieren: Ein
        // Befehl sieht nur den BoardState, nicht die Buehne.
        this.#syncStageToViewport();
        // Raster ist in Weltkoordinaten gezeichnet — bei Zoom/Verschiebung muss
        // der sichtbare Ausschnitt neu gefuellt und die Punktgroesse
        // nachgerechnet werden.
        this.#gridCtrl?.redraw();
        this.refreshAllSmileyBitmaps();
        // Die Einzelrahmen tragen eine zoom-unabhaengige Strichstaerke — die
        // muss beim Zoomen nachgerechnet werden.
        this.#refreshSelectionOutlines(this.selectionCtrl.selectedIds);
        this.selectionLayer?.batchDraw();
      }),
      // PRD-G §T06: react to BoardState.replaceAll() by tearing the Konva
      // scene down and rebuilding it from the new object list. 6-step order
      // (M10): clear selection → detach transformer → off+destroy nodes →
      // clear node map → re-add via onObjectAdded → batchDraw.
      this.boardState.on('state-replaced', ({ objects }) => {
        this.selectionCtrl.clear();
        this.transformer?.nodes([]);
        for (const node of this.konvaNodes.values()) {
          node.off();
          node.destroy();
        }
        this.konvaNodes.clear();
        for (const obj of objects) this.onObjectAdded(obj);
        this.contentLayer?.batchDraw();
      }),
    );

    // Selection → Transformer.
    const onSelectionChanged = (e: Event): void => {
      const detail = (e as CustomEvent<SelectionChangedDetail>).detail;
      this.refreshTransformer(detail.ids);
    };
    this.addEventListener(SELECTION_CHANGED_EVENT, onSelectionChanged as EventListener);
    this.teardown.push(() => this.removeEventListener(SELECTION_CHANGED_EVENT, onSelectionChanged as EventListener));

    // Controllers
    const viewportCtrl = new ViewportController(this, this.stage, this.boardState);
    this.#viewportCtrl = viewportCtrl;
    // Releases focus left on a toolbar button when the user presses the board — see the
    // controller for why the canvas keeps it in the first place.
    new BoardFocusController(this, container);
    this.#interactionCtrl = new InteractionController(this, this.stage, this.boardState, this.selectionCtrl, this.toolCtrl, {
      // Space-drag means pan, whatever tool is selected — see InteractionCallbacks.
      isPanArmed: () => viewportCtrl.isPanArmed,
      placeStickyAt: (x, y, size) => this.placeStickyAt(x, y, size),
      placeTextAt:   (x, y, size) => {
        // PRD-F6 §T16: dispatch `place/textbox` UiAction; the
        // TextboxLifecycleController owns AddObjectCommand + selection +
        // edit-mode entry. Bare-click centring and viewport-scale defaults
        // are computed here so the rect carried on the bus is always the
        // exact placement rectangle (top-left + size).
        if (!this.#bus) return;
        const cd = creationDefaults(this.boardState.viewport.scale);
        const w = size ? Math.max(40, size.width) : cd.textWidth;
        const h = size ? Math.max(28, size.height) : cd.textHeight;
        const wx = size ? x : x - w / 2;
        const wy = size ? y : y - h / 2;
        this.#bus.uiActions$.next({
          type: 'place/textbox',
          rect: { x: wx, y: wy, width: w, height: h },
        });
      },
      placeLineAt:   (x1, y1, x2, y2) => {
        // PRD-F5 §T19: dispatch `place/line` UiAction; LineLifecycleController
        // owns AddObjectCommand + selection emission. Forward the
        // preview-captured snap as `pendingStartAnchor` / `pendingEndAnchor`
        // so the committed line matches the preview overlay 1:1.
        const pending = this.pendingLineAnchor;
        this.pendingLineAnchor = null;
        const pendingStartAnchor = pending?.endpoint === 'start' ? pending.anchor : undefined;
        const pendingEndAnchor   = pending?.endpoint === 'end'   ? pending.anchor : undefined;
        this.#bus?.uiActions$.next({
          type: 'place/line',
          from: { x: x1, y: y1 },
          to:   { x: x2, y: y2 },
          ...(pendingStartAnchor ? { pendingStartAnchor } : {}),
          ...(pendingEndAnchor   ? { pendingEndAnchor }   : {}),
        });
      },
      placeCheckboxAt: (wx, wy) => this.placeCheckboxAt(wx, wy),
      snapWorldPoint:  (p) => this.#snapWorldPoint(p),
      placePersonAt:      (wx, wy, size) => this.placePersonAt(wx, wy, size),
      placeRectangleAt:   (wx, wy, size) => this.placeRectangleAt(wx, wy, size),
      placeEllipseAt:     (wx, wy, size) => this.placeEllipseAt(wx, wy, size),
      placeSmileyAt:      (wx, wy, size) => this.placeSmileyAt(wx, wy, size),
      showDragPreview: (box) => this.renderDragPreview(box),
      showLinePreview: (x1, y1, x2, y2) => this.renderLinePreview(x1, y1, x2, y2),
      hideLinePreview: () => this.removeLinePreview(),
      showMarquee: (box) => this.renderMarquee(box),
      selectInRect: (box) => this.selectInRect(box),
      // Anke, 2026-07-21 — ein Klick auf ein Gruppenmitglied wählt die GANZE
      // Gruppe ("dafür gruppiert man"). Die Auflösung passiert hier an der
      // Auswahl-Grenze, nicht im Auswahl-Controller: Der soll weiterhin nur
      // Ids verwalten und nichts über Gruppen wissen.
      // Ein Klick auf ein Gruppenmitglied waehlt die GANZE Gruppe — konkret:
      // alle ihre zeichenbaren Mitglieder. Die Gruppe selbst kommt NICHT in die
      // Auswahl; sie hat keinen Knoten auf der Buehne, und eine Auswahl ohne
      // Knoten kann weder gerahmt noch mitgezogen werden (Ankes Fehlerbericht).
      selectOnly: (id) => this.selectionCtrl.select(this.#clickSelection(id)),
      addToSelection: (id) => {
        for (const memberId of this.#clickSelection(id)) this.selectionCtrl.add(memberId);
      },
      toggleSelection: (id) => this.#toggleSelection(id),
      clearSelection: () => this.selectionCtrl.clear(),
      onClickAlreadySelected: (id, clientX, clientY) => this.#onClickAlreadySelected(id, clientX, clientY),
    });

    // Pressing the mouse button anywhere OUTSIDE the active editing overlay
    // commits and closes the editor.  Using the document capture phase means
    // this fires before Konva processes the event, so the editor is cleanly
    // committed before the subsequent stage-click handler calls clearSelection.
    const onDocMouseDown = (e: MouseEvent): void => {
      const target = e.target as Node | null;
      // Sticky editor: click inside the overlay → stay in edit mode.
      if (this.textEditor && this.textEditor.contains(target)) return;
      // Format toolbar: clicks on action buttons (colour picker, weight,
      // size, …) must NOT close the editor — they apply formatting to the
      // current text selection. The action buttons already preventDefault
      // on mousedown to keep the caret in the contentEditable, but this
      // listener runs in the capture phase BEFORE that handler fires, so
      // we have to whitelist the action elements here as well. The
      // SVG-driven options-circle bar lives outside the whiteboard host
      // (a sibling in the demo / consumer page), so we look at the
      // composed event path.
      if (target instanceof Element && target.closest('o2d-svg-optionsbar')) return;
      // Click is outside any active editor — close and commit.
      this.closeTextEditor(true);
    };
    document.addEventListener('mousedown', onDocMouseDown, true);
    this.teardown.push(() => document.removeEventListener('mousedown', onDocMouseDown, true));

    // Keyboard: Ctrl+Z / Ctrl+Y handled by @o2d/history (kept until P3 wires the
    // bus consumer for history actions). All other shortcuts (Delete/Backspace,
    // Ctrl+C/X/V, Escape) are dispatched to `bus.uiActions$` by the
    // KeyboardShortcutsController and bridged to existing element methods below
    // until P3 brings real slice-plugin consumers.
    // Bugfix, Anke 2026-09-20 (Review 0023): auch diese beiden Tastenwege
    // verändern das Board, und `registerUndoRedoShortcuts` kennt `readonly`
    // nicht — der Guard gehört deshalb in die Rückrufe, die wir selbst
    // stellen. Ohne ihn räumt Strg+Z ein gesperrtes Board um.
    this.teardown.push(
      registerUndoRedoShortcuts(
        () => { if (!this.readonly) this.undo(); },
        () => { if (!this.readonly) this.redo(); },
      ),
    );

    // Bus + adapters are created in `connectedCallback()` (PRD-H1 T09) which
    // runs before `firstUpdated()` on every (re)connect. We only consume the
    // already-mounted instance here for bridge subscriptions.
    //
    // C1 (review-finding): the bus-lifetime `whiteboard-changed` subscription
    // MUST live in `#initBus()` (called from connectedCallback) so it
    // re-attaches on every reconnect — keeping it here would tie it to
    // `firstUpdated()` which only fires once per element instance.
    const bus = this.#bus!;

    // KeyboardShortcutsController self-registers via host.addController(); the
    // host manages its lifecycle (hostConnected / hostDisconnected). No need to
    // store the reference.
    new KeyboardShortcutsController(this, bus);

    // Slice registration (TR8/TR10): register the sticky slice so the renderer
    // delegates `obj.type === 'sticky'` to the plugin. Add new slices here as
    // they migrate (Textbox in P5.6, ...).
    this.registry.register(stickySlice);
    // PRD-F1 §TR8: register rectangle and ellipse slices so the renderer
    // delegates these object types to their plugins.
    this.registry.register(rectangleSlice);
    this.registry.register(ellipseSlice);
    // PRD-F2 §T28: register checkbox and person slices.
    this.registry.register(checkboxSlice);
    this.registry.register(personSlice);
    // PRD-F3 §TR4: register smiley slice so the renderer routes
    // `obj.type === 'smiley'` to the slice plugin (replaces the legacy
    // createSmileyNode/updateSmileyNode branch in board-object-renderer.ts).
    this.registry.register(smileySlice);
    // PRD-F4 §TR4: register image slice so the renderer routes
    // `obj.type === 'image'` to the slice plugin (replaces the legacy
    // createImageNode/updateImageNode branch in board-object-renderer.ts).
    this.registry.register(imageSlice);
    // PRD-F6 §TR11/T25: register textbox slice so the renderer routes
    // `obj.type === 'text'` to the textbox slice plugin (replaces the
    // legacy createTextBoxNode/updateTextBoxNode branch removed in T24).
    this.registry.register(textboxSlice);
    // PRD-F5 §TR14/T25: register line slice + wire its per-element
    // LinePathStrategyRegistry. The slice's `registerStrategies` populates
    // the registry with `StraightPathStrategy` and binds the renderer.
    this.linePathStrategyRegistry = new LinePathStrategyRegistry();
    this.registry.register(lineSlice);
    lineSlice.registerStrategies?.(this.linePathStrategyRegistry);

    // PRD-F5 §TR7/TR15: AnchorSyncController consolidates the live and
    // commit anchor-sync paths previously inlined on this element. It
    // registers itself via host.addController.
    this.anchorSync = new AnchorSyncController(
      this,
      bus,
      this.boardState,
      {
        konvaNodeFor: (id) => this.konvaNodes.get(id),
        // Live anchor-follow preview routes through the single state→canvas
        // geometry routine, so the caps track the drag exactly like the stroke.
        renderLine: (line) => this.onObjectUpdated(line),
      },
      (o) => isAnchorTarget(o, this.registry),
      (o) => anchorBoundsOf(o, this.registry),
    );

    // Slice controllers (PRD-E §TR6, §TR15): instantiate after the bus exists.
    // StickyPlacementController is bus-lifetime (not a Lit ReactiveController);
    // EditModeController is registered as a ReactiveController so its
    // hostConnected fires now and hostDisconnected on element teardown.
    new StickyPlacementController(bus, this.boardState, this.history.history);
    // PRD-F1 §TR6: rectangle/ellipse placement controllers subscribe to
    // `place/rectangle` and `place/ellipse` UiActions on the bus.
    new RectanglePlacementController(bus, this.boardState, this.history.history);
    new EllipsePlacementController(bus, this.boardState, this.history.history);
    // PRD-F2 §T28: checkbox/person placement controllers subscribe to
    // `place/checkbox` / `place/person` UiActions on the bus.
    new CheckboxPlacementController(bus, this.boardState, this.history.history);
    new PersonPlacementController(bus, this.boardState, this.history.history);
    // PRD-F3 §TR4: smiley placement controller subscribes to `place/smiley`
    // and owns AddObjectCommand + selection emission.
    new SmileyPlacementController(bus, this.boardState, this.history.history);
    // PRD-F4 §TR4: image placement controller subscribes to `place/image`
    // and owns AddObjectCommand + selection emission (synchronous).
    new ImagePlacementController(bus, this.boardState, this.history.history);
    // PRD-F5 §TR12/T19: LineLifecycleController subscribes to `place/line`
    // and `line/swap-caps`, owns AddObjectCommand + SwapLineCapsCommand +
    // selection emission. Lifetime tied to `bus.destroy$`.
    new LineLifecycleController(bus, this.boardState, this.history.history, this.registry);
    // PRD-F6 §TR8/T16: TextboxLifecycleController moved AFTER
    // TextboxEditorController construction (Z.611) so it can be wired
    // with the editor bridge. Without the bridge, `apply-format`
    // and `apply-fontsize` are silent no-ops via Optional-Chaining.
    // PRD-F5 §TR6/T23: LineEndpointHandlesController owns the per-line
    // endpoint drag handles. Registered as ReactiveController so its
    // hostConnected fires now and selection-driven handle creation/removal
    // is automatic; teardown on element disconnect.
    new LineEndpointHandlesController(this, bus, this.boardState, this.history.history, {
      selectionLayer: () => this.selectionLayer,
      contentLayer: () => this.contentLayer,
      overlayLayer: () => this.overlayLayer,
      konvaNodeFor: (id) => this.konvaNodes.get(id),
      getReadonly: () => this.readonly,
      registry: () => this.registry,
      // Live endpoint-drag preview. `onObjectUpdated` is the single
      // state→canvas geometry routine and takes the object to draw as an
      // argument, so a transient (uncommitted) line paints through exactly the
      // same path as a committed one — no second, drifting render branch.
      renderLine: (line) => this.onObjectUpdated(line),
    });
    // Orthogonal Stage 2 (increment 1): the middle segment of an elbow line
    // gets one draggable handle. Same live-preview + single-command-on-release
    // pattern as the endpoint handles; reads the per-element path registry to
    // find the segment geometry.
    new LineMidSegmentHandleController(this, bus, this.boardState, this.history.history, {
      overlayLayer: () => this.overlayLayer,
      selectionLayer: () => this.selectionLayer,
      konvaNodeFor: (id) => this.konvaNodes.get(id),
      getReadonly: () => this.readonly,
      pathRegistry: () => this.linePathStrategyRegistry,
      renderLine: (line) => this.onObjectUpdated(line),
    });
    // Curve control: one draggable handle at a curved line's control point;
    // dragging bends the curve (stored chord-relative). Same live-preview +
    // single-command-on-release pattern as the other line handles.
    new LineCurveHandleController(this, bus, this.boardState, this.history.history, {
      overlayLayer: () => this.overlayLayer,
      selectionLayer: () => this.selectionLayer,
      konvaNodeFor: (id) => this.konvaNodes.get(id),
      getReadonly: () => this.readonly,
      renderLine: (line) => this.onObjectUpdated(line),
    });
    // Connector handles: a round handle on each edge midpoint of a selected
    // non-line object; dragging one pulls a new line out of that edge (start
    // anchored to the object). Dispatches `place/line` on the bus like the line
    // tool, so LineLifecycleController owns creation + selection.
    new ObjectConnectorHandlesController(this, bus, this.boardState, {
      overlayLayer: () => this.overlayLayer,
      selectionLayer: () => this.selectionLayer,
      konvaNodeFor: (id) => this.konvaNodes.get(id),
      getReadonly: () => this.readonly,
      registry: () => this.registry,
      hoverFill: () => this.handleHoverFill(),
    });
    // PRD-F4 §TR4 / FC10: ImagePasteHandler turns native paste events into
    // `place/image` UiActions. Wired explicitly via addController so the
    // listener is registered exactly once on element connect.
    const imagePasteHandler = new ImagePasteHandler(
      this,
      bus,
      () => ({ width: this.stage?.width() ?? 0, height: this.stage?.height() ?? 0 }),
      () => this.boardState.viewport,
      // O2D.W-PRD-042 §TR4: resolved per paste, NOT captured here — Lit assigns
      // properties after the controllers are constructed.
      () => this.imageUploadHandler,
      // Same as above: resolved per paste, not captured.
      () => this.pasteChoiceHandler,
    );
    this.addController(imagePasteHandler);
    this.#editModeCtrl = new EditModeController(this, bus);
    this.addController(this.#editModeCtrl);
    // PRD-F2 §T24/T28: PlainEditController owns the inline plain-text editor
    // for Checkbox-Label (`field='label'`) and Person-Name (`field='name'`).
    this.#plainEditCtrl = new PlainEditController(this, bus, this.boardState, this.history.history);
    this.addController(this.#plainEditCtrl);
    // StickyEditorController owns the sticky contentEditable overlay (TR7).
    // Bindings expose konvaNode lookup + content-layer redraw so the
    // controller can hide the underlying Konva text while editing.
    this.#stickyEditorCtrl = new StickyEditorController(this, bus, this.boardState, this.history.history, {
      konvaNodeFor: (id) => this.konvaNodes.get(id),
      redrawContent: () => this.contentLayer?.batchDraw(),
    });
    this.addController(this.#stickyEditorCtrl);
    // PRD-F6 §TR4 / §T14: TextboxEditorController owns the textbox
    // contentEditable overlay (Variant A — temporary <div> over Konva).
    // Bindings expose konvaNode lookup, content-layer redraw, and the
    // host-relative screenBboxFor() so the controller can position the
    // editor over the underlying Konva textbox shape.
    this.#textboxEditorCtrl = new TextboxEditorController(this, bus, this.boardState, this.history.history, {
      konvaNodeFor: (id) => this.konvaNodes.get(id),
      redrawContent: () => this.contentLayer?.batchDraw(),
      screenBboxFor: (id) => this.screenBboxFor(id),
    });
    this.addController(this.#textboxEditorCtrl);
    // PRD-F6 §TR8/T16: TextboxLifecycleController subscribes to
    // `place/textbox`, `textbox/toggle-checklist-item`, and the
    // format/font-size delegations; lifetime tied to `bus.destroy$`.
    // Wired AFTER the editor controller so the bridge is non-null —
    // otherwise apply-format / apply-fontsize go to a silent no-op.
    new TextboxLifecycleController(bus, this.boardState, this.history.history, this.#textboxEditorCtrl);

    // PRD-X §T8.5: initial readonly sync — when the host mounts with the
    // attribute already set, propagate the lock to every guard surface.
    if (this.readonly) this.#applyReadonly(true);

    // Bridge: route migrated UiActions to existing element methods until P3
    // brings slice-plugin consumers. history/undo|redo are handled by
    // registerUndoRedoShortcuts above (intentionally not double-fired).
    // clipboard copy/cut/paste map to the same element methods the
    // options-circle buttons call, so keyboard and buttons behave identically.
    const uiSub = bus.uiActions$.subscribe((action) => {
      switch (action.type) {
        case 'clipboard/paste-text': {
          // Eigene Objekte erkennen wir an der Kennung; alles andere ist fremder
          // Text und wird zur Textbox (Ankes Entscheidung).
          const objs = decodeClipboardPayload(action.text);
          if (objs) {
            clipboard.write(objs); // Spiegel nachziehen, damit beide Wege gleich sind
            this.#pasteFromMirror();
          } else {
            this.pasteTextAsTextbox(action.text);
          }
          break;
        }
        case 'clipboard/copy':
          this.copySelected();
          break;
        case 'clipboard/cut':
          this.cutSelected();
          break;
        case 'clipboard/paste':
          // Kommt AUSSCHLIESSLICH aus dem nativen paste-Ereignis, und zwar erst,
          // nachdem dieses die Rechner-Zwischenablage bereits gelesen und dort
          // weder Bild noch Text gefunden hat. Deshalb direkt der Spiegel und
          // NICHT `pasteSelected()`: das würde die Ablage ein zweites Mal lesen
          // — asynchron, womöglich mit Erlaubnisfrage, und für nichts.
          this.#pasteFromMirror();
          break;
        case 'object/delete':
          if (action.ids.length > 0) {
            this._historyAdapter.push(new RemoveObjectWithAnchorsCommand(action.ids), this.boardState);
          }
          this.selectionCtrl.clear();
          break;
        case 'object/nudge': {
          if (action.ids.length === 0 || (action.dx === 0 && action.dy === 0)) break;
          const moves: Move[] = [];
          for (const nudgeId of action.ids) {
            const obj = this.boardState.getObject(nudgeId);
            if (!obj) continue;
            moves.push({
              id: nudgeId,
              from: { x: obj.x, y: obj.y },
              to: { x: obj.x + action.dx, y: obj.y + action.dy },
            });
          }
          if (moves.length > 0) {
            this._historyAdapter.push(
              new NudgeObjectsCommand(moves, Math.sign(action.dx), Math.sign(action.dy)),
              this.boardState,
            );
          }
          break;
        }
        case 'selection/clear':
          this.selectionCtrl.clear();
          break;
        case 'selection/select-all':
          this.selectAll();
          break;
        // TR6 pre-condition: bridge `selection/set` (e.g. emitted by
        // StickyPlacementController) to the SelectionController until the
        // SelectionController itself subscribes to the bus in P5/P6.
        case 'selection/set':
          this.selectionCtrl.select(action.ids as ReadonlyArray<string>);
          break;
        // TR12 bridge: ToolController is constructed at field-init (before
        // the bus exists), so it cannot attach its own bus-subscription in
        // hostConnected. Route `tool/set` UiActions emitted by the toolbar
        // (and other publishers) through the controller's setTool() until a
        // future refactor moves the controller behind a deferred-init that
        // can receive the bus reference at construction time.
        case 'tool/set':
          this.toolCtrl.setTool(action.tool);
          break;
        // TR18: Explicit no-op cases for slice-owned UiActions. The
        // EditModeController + StickyEditorController subscribe to these
        // directly — handling them here would cause double-dispatch.
        case 'edit/enter-sticky':
        case 'edit/commit':
        case 'edit/cancel':
          break;
        // PRD-F2 §TR6/T28: PlainEditController owns these — element no-ops.
        case 'edit/enter-plain':
        case 'edit/commit-plain':
        case 'edit/cancel-plain':
          break;
        // PRD-F5 §TR11/TR12: LineLifecycleController owns these — element no-ops.
        case 'line/swap-caps':
        case 'line/set-path':
          break;
        // PRD-F6 §TR13: TextboxEditorController + TextboxLifecycleController
        // own these — element no-ops to avoid double-dispatch.
        case 'edit/enter-textbox':
        case 'textbox/toggle-checklist-item':
        case 'textbox/apply-format':
        case 'textbox/apply-fontsize':
        // O2D.W-PRD-035 §TR1: TextboxLifecycleController owns apply-list —
        // element no-op to avoid double-dispatch.
        case 'textbox/apply-list':
        case 'place/textbox':
          break;
        // O2D.W-PRD-027 §TR6: live selection-state actions are consumed by the
        // radial optionsbar (which subscribes the bus directly) — element no-ops
        // to avoid double-dispatch, consistent with the edit-lifecycle block.
        case 'edit/selection-changed':
        case 'edit/selection-cleared':
          break;
        default:
          break;
      }
    });
    this.teardown.push(() => uiSub.unsubscribe());

    // PRD-F4 §TR4 / FC14: paste handling moved to ImagePasteHandler
    // (registered as ReactiveController above).

    // Transformer transform lifecycle: capture snapshot on start, live-
    // normalise scale during transform so text doesn't distort, commit
    // the resize via command on end.
    this.transformer.on('transformstart', () => this.onTransformStart());
    this.transformer.on('transform', () => this.onTransformLive());
    this.transformer.on('transformend', () => this.onTransformEnd());

    // When the user drags the multi-select group via the Transformer's
    // internal bounding box, the individual node drag events do NOT fire.
    // Listen at the Transformer level so we can commit the move command.
    this.transformer.on('dragstart', () => this.onTransformerDragStart());
    this.transformer.on('dragend',   () => this.onTransformerDragEnd());

    // Stage-wide pointer tracking for the on-hover corner affordance:
    // when the cursor gets close to any corner of a hoverable object
    // (sticky / text / person / rectangle / ellipse / smiley / image),
    // show a small handle at that corner that can be clicked and dragged
    // to resize the object directly — no prior selection required.
    this.stage.on('mousemove.hovercorner', () => this.updateHoverCorner());
    this.stage.on('mouseleave.hovercorner', () => this.hideHoverCorner());

    // PRD-H §TR2/T15: wrapper bus-injection. MUST run AFTER bus-init
    // (Z.467-474) and AFTER slice/controller-init so that when we set
    // `child.board = this`, the children's `subscribeToBoard()` sees a
    // fully initialized `board.bus`. The sub-elements were rendered in
    // render() (T13) into the Light-DOM renderRoot (= this), so a plain
    // querySelector resolves them. Children's `board`-property is
    // @deprecated (T09) but still functional — it is the documented
    // bridge for tests AND for this internal hand-off.
    // ESLint `no-direct-board-method-call` only filters CallExpression
    // nodes; an AssignmentExpression like `.board = this` is not flagged
    // (PRD-H §TR2 paragraph "AssignmentExpression").
    // Note: sub-element bus injection has migrated to `updated()` (W1 PRD-H1,
    // T11) — single source of truth handles initial mount AND reconnect.

    // Initial-mount catch-up (PRD-077 / hosts that load content in their own
    // willUpdate): when `config` was assigned BEFORE firstUpdated() ran,
    // `boardState.replaceAll()` emitted `state-replaced` with no listener
    // attached yet. The event was dropped and Konva stayed empty even though
    // BoardState already holds the objects. MUST run after slice/controller
    // registration (Z.724-749) so the slices' `onObjectAdded` rendering
    // strategies are available.
    const initialObjects = this.boardState.allObjects();
    if (initialObjects.length > 0) {
      for (const obj of initialObjects) this.onObjectAdded(obj);
      this.contentLayer?.batchDraw();
    }
    // Wurde `config` vor `firstUpdated()` gesetzt, lief `#tryFitOnOpen` damals
    // ins Leere (keine Buehne). Jetzt gibt es sie — und die Objekte darueber
    // sind eben erst gezeichnet worden, ohne sie waere nichts zu messen.
    this.#tryFitOnOpen();

    // Sobald die Board-Hausschrift (D-DIN) geladen ist, alle Knoten einmal neu
    // aufbauen — bis dahin zeichnet Konva Canvas-Text mit der Ersatzschrift und
    // misst Breite/Umbruch entsprechend falsch.
    this.#redrawOnFontsReady();
  }

  /**
   * One-shot rebuild once the board's webfonts have finished loading.
   *
   * Konva renders canvas text with whatever font is available at draw time.
   * Before D-DIN (the board house font) has loaded, the glyphs use the system
   * fallback AND the measured width/wrapping is computed against it — so auto-
   * width text (person names) and wrapped text (sticky notes) sit slightly off
   * once the real font swaps in. Rebuilding every node after `fonts.ready`
   * re-measures each text against the loaded font. In consumers that already
   * ship D-DIN (horstplan) the promise resolves (near-)immediately; where the
   * font is absent it resolves against the fallback and the rebuild is a
   * harmless no-op. `document.fonts` is absent under happy-dom, so the guard
   * also keeps the unit tests untouched.
   */
  #redrawOnFontsReady(): void {
    const fonts: FontFaceSet | undefined = document.fonts;
    if (!fonts?.ready) return;
    void fonts.ready.then(() => {
      // The element may have disconnected while the promise was pending;
      // disconnectedCallback nulls the layers, so this guard makes it a no-op.
      if (!this.contentLayer) return;
      this.#rerenderAllNodes();
    });
  }

  /**
   * Tears every object node down and rebuilds it from BoardState, preserving
   * the current selection. Mirrors the `state-replaced` rebuild but keeps the
   * existing object list — used by {@link #redrawOnFontsReady} to re-measure
   * text against a font that loaded after the first draw.
   */
  #rerenderAllNodes(): void {
    if (!this.contentLayer) return;
    const selected = [...this.selectionCtrl.selectedIds];
    this.transformer?.nodes([]);
    for (const node of this.konvaNodes.values()) {
      node.off();
      node.destroy();
    }
    this.konvaNodes.clear();
    for (const obj of this.boardState.allObjects()) this.onObjectAdded(obj);
    this.contentLayer.batchDraw();
    if (selected.length > 0) this.refreshTransformer(selected);
  }

  /**
   * PRD-X §T8.5 / AC4-AC12: react to `readonly` property changes by
   * propagating the lock state to every guard surface (TR5/TR6/TR7/TR9 plus
   * TR8 via the optionsbar reading `board.readonly`).
   *
   * W1 PRD-H1 / T11: re-inject wrapper child refs after reconnect. The
   * condition triggers when (a) the ref is undefined (post-disconnect or
   * initial mount) OR (b) the ref points to a detached node (defensive
   * against future template churn that could orphan the ref while keeping
   * the field non-undefined). Only `#toolbar` is checked because the three
   * refs are set and cleared atomically.
   */
  protected override updated(changed: PropertyValues<this>): void {
    super.updated(changed);
    if (changed.has('readonly')) {
      this.#applyReadonly(this.readonly);
    }
    if (changed.has('gridEnabled')) {
      this.#gridCtrl?.setEnabled(this.gridEnabled);
    }
    const toolbarStale = !!this.#toolbar && !this.#toolbar.isConnected;
    if (!this.#toolbar || toolbarStale) {
      this.#injectBusIntoChildren();
    }
  }

  #applyReadonly(value: boolean): void {
    // Review 0023, Lauf 02: der Guard sitzt jetzt im Bus, an `next()` —
    // also an der einen Stelle, durch die jede Veröffentlichung läuft.
    // Zuerst setzen, damit während des Aufräumens unten nichts mehr
    // durchkommt.
    this.#bus?.setReadonly(value);
    if (value) {
      this.toolCtrl.lockToSelect();
      // Bugfix, Anke 2026-09-16: a selection made just before the board
      // locked (e.g. the User had an object selected, then clicked
      // "Speichern") otherwise stayed visible — selection handles + the SVG
      // options circle kept showing on an object that can no longer be
      // dragged, edited, or re-selected/toggled (the other fixes in this
      // same change), which read as broken rather than as "locked."
      this.selectionCtrl.clear();
    } else {
      this.toolCtrl.unlockTool();
    }
    this.#interactionCtrl?.setReadonly(value);
    this.#editModeCtrl?.setReadonly(value);
    this.#stickyEditorCtrl?.setReadonly(value);
    this.#textboxEditorCtrl?.setReadonly(value);
    this.#plainEditCtrl?.setReadonly(value);
    // Bugfix, Anke 2026-09-16: `lockToSelect()`/`unlockTool()` above only
    // fire `TOOL_CHANGED_EVENT` (the OTHER trigger for
    // `applyNodeDraggability()`, wired in `firstUpdated()`) when the tool
    // ACTUALLY changes — `ToolController.applyTool()` no-ops when the tool
    // was already `select` (the common case: readonly toggles happen while
    // sitting on the default `select` tool). Without this explicit call,
    // already-draggable nodes would stay draggable the moment `readonly`
    // turns on, and newly-unlocked nodes would stay non-draggable the
    // moment it turns off, in that exact case.
    this.applyNodeDraggability();
    // Lauf 04: der Rückweg. Beim Entsperren ändern sich die Auswahl-IDs nicht,
    // der SelectionController meldet also nichts — ohne diesen Aufruf bliebe
    // eine über `selectIds()` gesetzte Auswahl bis zur nächsten
    // Auswahländerung ohne Griffe stehen („ausgewählt, aber nicht
    // bearbeitbar"). Beim Sperren ist der Aufruf überflüssig, aber harmlos:
    // `selectionCtrl.clear()` oben hat dann ohnehin schon aufgeräumt.
    this.refreshTransformer(this.selectionCtrl.selectedIds);
    // Bugfix, Anke 2026-10-17: der Optionskreis hört nur auf Auswahl und
    // Werkzeug. War beim Speichern nichts ausgewählt, ändert sich keins von
    // beiden — der Kreis muss den Sperrwechsel deshalb ausdrücklich erfahren.
    this.#optionsbar?.refreshForReadonly();
  }

  /**
   * Raster umschalten (Anke, 2026-07-16). Von der Werkzeugleiste aufgerufen.
   * Setzt die Property; `updated()` schaltet die Sichtbarkeit, und
   * #snapWorldPoint liest denselben Zustand fuers Einrasten.
   */
  toggleGrid(): void {
    this.gridEnabled = !this.gridEnabled;
  }

  /**
   * Faengt einen Weltpunkt aufs Raster — aber nur, wenn das Raster AN ist.
   * Ist es aus, kommt der Punkt unveraendert zurueck; die Fang-Nahtstellen
   * (Erstellen, Ziehen, Skalieren) rufen deshalb bedingungslos hierher und
   * brauchen die Zustandspruefung nicht selbst.
   */
  #snapWorldPoint(point: { x: number; y: number }): { x: number; y: number } {
    return this.gridEnabled ? snapPointToGrid(point) : point;
  }

  /**
   * Initializes the central bus + bridge adapters on every (re)connect
   * (W1 PRD-H1, T09). Defensive pre-disposal handles edge cases like
   * `adoptNode` or `replaceChild` with the same node where
   * `disconnectedCallback()` may not have fired — without it, the old bus
   * would leak (no `destroy()`) when a new one is created.
   *
   * On reconnect (`#firstConnect === false`) a `requestUpdate()` is triggered
   * so the `updated()` hook re-runs `#injectBusIntoChildren()` and re-wires
   * the sub-elements to the NEW bus instance. On the initial mount this is
   * skipped — Lit's regular render pipeline already fires `updated()` after
   * `firstUpdated()` populates the Light-DOM children.
   */
  override connectedCallback(): void {
    super.connectedCallback();
    // PRD-010 §T2.3 — install the History.push wrapper for dispatch.
    // Idempotent: a remount cycle calls attach() again, but the adapter's
    // `_attached` guard prevents double-wrapping (ADR-003).
    this._historyAdapter.attach();
    if (this.#bus) this.#disposeBusAndAdapters();
    this.#initBus();
    if (!this.#firstConnect) this.requestUpdate();
    this.#firstConnect = false;
  }

  override disconnectedCallback(): void {
    // PRD-010 §T2.3 — restore the original History.push to prevent the
    // PRD-009 memory leak on SPA remount (ADR-003). MUST run before bus
    // teardown so any final dispatches still find a live element.
    this._historyAdapter.detach();
    for (const off of this.teardown.splice(0)) off();
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.stage?.destroy();
    this.stage = undefined;
    this.backgroundLayer = this.selectionLayer = this.contentLayer = this.overlayLayer = undefined;
    this.transformer = undefined;
    this.konvaNodes.clear();
    // Reset wrapper-child refs so updated() re-injects on reconnect (W1 PRD-H1 T09b).
    this.#toolbar = undefined;
    this.#optionsbar = undefined;
    this.#disposeBusAndAdapters();
    super.disconnectedCallback();
  }

  /**
   * Create the central bus and mount the four bridge adapters in canonical
   * order (TR8/FC-4): BoardState → Selection → Tool → Viewport. Idempotent at
   * the call sites — both `firstUpdated()` (initial mount) and (after T09)
   * `connectedCallback()` (reconnect) guard with `#bus === undefined`.
   */
  #initBus(): void {
    this.#bus = new WhiteboardBus();
    // Review 0023: `#initBus()` läuft bei JEDEM Remount, die
    // Erstsynchronisation in `firstUpdated()` nur einmal pro Element — ohne
    // diese Zeile käme ein wiederverbundenes, weiterhin gesperrtes Board mit
    // einem frischen, UNGESPERRTEN Bus zurück.
    //
    // Maßhalten (Lauf 03): Das ist eine Zustands-Invariante, kein akut
    // ausnutzbares Leck. Die Publisher aus `firstUpdated()`
    // (KeyboardShortcutsController, ImagePasteHandler, …) halten nach einem
    // Remount ohnehin den ALTEN Bus — bekannte PRD-I-Lücke, siehe
    // `tests/components/reconnect-lifecycle.test.ts`. Auf dem NEUEN Bus
    // veröffentlichen danach nur Werkzeugleiste und Optionsbar, und deren
    // Aktionen stehen nicht im Sperr-Set. Die Zeile hält den Bus trotzdem im
    // richtigen Zustand, statt sich darauf zu verlassen.
    this.#bus.setReadonly(this.readonly);
    this.#boardStateBusAdapter = new BoardStateBusAdapter(this.boardState, this.#bus);
    this.#selectionBusAdapter = new SelectionBusAdapter(this, this.#bus);
    this.#toolBusAdapter = new ToolBusAdapter(this, this.#bus);
    this.#viewportBusAdapter = new ViewportBusAdapter(this.boardState, this.#bus);

    // C1 (review-finding): bus-lifetime DOM-event bridge. MUST be wired in
    // `#initBus()` (not `firstUpdated()`) so it re-attaches on every reconnect
    // — `firstUpdated()` only runs once per element instance, so a remount
    // would leave a NEW bus without any `whiteboard-changed` subscriber.
    // The subscription is tied to `bus.destroy$` and tears down with the bus,
    // so no additional cleanup is required.
    const bus = this.#bus;

    // PRD-011 §G2/T1.10: debounced `whiteboard-changed` event pipeline.
    // Operator order MUST be `debounceTime` BEFORE `takeUntil(bus.destroy$)`
    // — otherwise a pending debounced emit could fire AFTER destroy when the
    // source completes.
    bus.domain$
      .pipe(debounceTime(300), takeUntil(bus.destroy$))
      .subscribe(() => this._emitChanged());
  }

  /**
   * Dispose all four bridge adapters in REVERSE mount order (FC-5), then
   * destroy the bus, then null out all five fields. Symmetric counterpart to
   * `#initBus()`. Safe to call when fields are already `undefined`.
   */
  #disposeBusAndAdapters(): void {
    this.#viewportBusAdapter?.dispose();
    this.#viewportBusAdapter = undefined;
    this.#toolBusAdapter?.dispose();
    this.#toolBusAdapter = undefined;
    this.#selectionBusAdapter?.dispose();
    this.#selectionBusAdapter = undefined;
    this.#boardStateBusAdapter?.dispose();
    this.#boardStateBusAdapter = undefined;
    this.#bus?.destroy();
    this.#bus = undefined;
  }

  /**
   * Resolve the three Light-DOM wrapper children, set their `board` property,
   * and force-refresh their bus subscriptions (W1 PRD-H1, T10b).
   *
   * Lit's `updated('board')` hook is deduplicated when the same board reference
   * is re-assigned (post-reconnect: child.board still points to `this`), so we
   * cannot rely on the property-change path to re-wire children to the NEW bus
   * instance after a disconnect/reconnect cycle. The explicit
   * `child.refreshBusBindings()` call sequence guarantees idempotent rebind.
   *
   * Called from `firstUpdated()` (initial mount) and (post T11) the `updated()`
   * hook when the wrapper refs are missing or stale.
   */
  #injectBusIntoChildren(): void {
    this.#toolbar = this.renderRoot.querySelector<O2dWhiteboardToolbarElement>(':scope > o2d-whiteboard-toolbar') ?? undefined;
    this.#optionsbar = this.renderRoot.querySelector<O2dSvgOptionsbarElement>(':scope > o2d-svg-optionsbar') ?? undefined;
    if (this.#toolbar) {
      this.#toolbar.board = this;
      this.#toolbar.refreshBusBindings();
    }
    if (this.#optionsbar) {
      this.#optionsbar.board = this;
      this.#optionsbar.refreshBusBindings();
      // Ein Sperrwechsel, während das Board abgehängt war, lief in
      // `#applyReadonly()` ins Leere (`#optionsbar` war undefined) — hier
      // holt der Kreis ihn nach (Review 0040, Lauf 01).
      this.#optionsbar.refreshForReadonly();
    }
  }

  /**
   * PRD-009 §T5.2 — Dispatch one `whiteboard-command` CustomEvent for an
   * applied package command. Serializes via {@link serializeCommand}; if
   * the helper returns `null` (unknown command type, no-op guard) the
   * event is skipped and `_commandVersion` is NOT incremented.
   *
   * MUST be called AFTER `history.push(cmd)` returns (the apply path
   * completes synchronously). Wiring into every push site lands in T5.3
   * (element-direct pushes) and T5.6 (placement/lifecycle controllers).
   *
   * `extras` carries call-site context that cannot be derived from the
   * command instance: `sourceUids` for DuplicateObjectsCommand (captured
   * before push), `toIndex` for ZOrderCommand (read from BoardState after
   * push returns).
   */
  private _dispatchCommandEvent(
    cmd: Command<BoardState>,
    extras?: SerializeCommandExtras,
  ): void {
    const serialized = serializeCommand(cmd, extras);
    if (serialized === null) return;
    const detail: WhiteboardCommandEventDetail = {
      command: serialized,
      version: ++this._commandVersion,
      payloadVersion: 1,
    };
    this.dispatchEvent(
      new CustomEvent<WhiteboardCommandEventDetail>('whiteboard-command', {
        detail,
        bubbles: true,
        composed: true,
      }),
    );
  }

  // ---- Public API -------------------------------------------------------

  addObject(obj: BoardObject): void {
    this._historyAdapter.push(new AddObjectCommand(obj), this.boardState);
  }

  removeObject(id: string): void {
    if (!this.boardState.getObject(id)) return;
    this._historyAdapter.push(new RemoveObjectWithAnchorsCommand([id]), this.boardState);
  }

  undo(): boolean { return this.history.undo(); }
  redo(): boolean { return this.history.redo(); }
  get canUndo(): boolean { return this.history.canUndo; }
  get canRedo(): boolean { return this.history.canRedo; }

  /** The current viewport (read-only snapshot). */
  get viewport(): Readonly<{ x: number; y: number; scale: number }> {
    return this.boardState.viewport;
  }

  /**
   * True while a pan is armed or running (Space held, or a pan drag under
   * way) — a read-only mirror of `ViewportController.isPanArmed`. Exposed
   * for consumers that need to distinguish "the user is navigating the
   * canvas" from "the user is interacting with an object", e.g. a readonly
   * viewer that wants pan/zoom (view-only, no selection semantics) but must
   * itself suppress a bare click from reaching Konva's own click-to-select
   * handling (`InteractionController.onStageClick` is intentionally NOT
   * readonly-gated, see its own doc comment — TR9's contract only ever
   * covered stage-DOWN gestures). `undefined` before the controller exists
   * (not yet connected). */
  get isPanArmed(): boolean | undefined {
    return this.#viewportCtrl?.isPanArmed;
  }

  /**
   * Zoomstufe auf 100 % setzen. Aktualisiert BoardState UND die Konva-Buehne.
   *
   * Gezoomt wird um die BILDMITTE (Anke, 2026-07-16): Wer nach "auf alles
   * zoomen" auf 100 % klickt, will naeher heran — aber auf dieselbe Stelle.
   * Frueher blieben `x`/`y` einfach stehen; das haelt in Wahrheit die linke
   * obere ECKE fest, und je staerker der Sprung, desto weiter wandert das Bild
   * weg. Die Mitte als Anker ist derselbe Gedanke wie beim Mausrad, wo der
   * Punkt unter dem Zeiger stehen bleibt — nur ist der Anker hier die Mitte,
   * weil kein Zeiger im Spiel ist.
   */
  resetZoom(): void {
    if (!this.stage) return;
    const centre = { x: this.stage.width() / 2, y: this.stage.height() / 2 };
    // Die Buehne zieht der `viewport-changed`-Horcher nach.
    this.boardState.setViewport(zoomToScaleAt(this.boardState.viewport, centre, 1));
  }

  /**
   * Anke, 2026-07-16 — "Auf 100 % normalisieren": dieselbe Ansicht behalten,
   * aber die Prozentstufe auf 100 % bringen. Jedes Objekt waechst dafuer um die
   * aktuelle Stufe (Rechnung siehe normalise-zoom.helper.ts).
   *
   * Anders als die uebrigen Ansichtstasten aendert das die OBJEKTE — deshalb
   * ein Befehl mit Rueckgaengig, und deshalb nimmt er die Ansicht mit zurueck.
   */
  normaliseZoom(): void {
    if (!this.stage) return;
    const cmd = NormaliseZoomCommand.from(this.boardState);
    if (!cmd) return; // steht schon auf 100 % — kein leerer Verlaufsschritt
    this._historyAdapter.push(cmd, this.boardState);
  }

  /** Buehne auf den Ansichts-Zustand nachziehen. Einzige Stelle, die das tut. */
  #syncStageToViewport(): void {
    if (!this.stage) return;
    const vp = this.boardState.viewport;
    this.stage.position({ x: vp.x, y: vp.y });
    this.stage.scale({ x: vp.scale, y: vp.scale });
    this.stage.batchDraw();
  }

  /**
   * Zoom & pan so that ALL objects fit into the visible board area
   * ("auf alles zoomen"). No-op on an empty board.
   *
   * The fit box is the union of every object's WORLD-space bounding box.
   * `node.getClientRect({ relativeTo: stage })` yields coordinates in the
   * stage's UNTRANSFORMED space (= world units, since the layers carry no
   * transform and the stage holds the pan/zoom) and already accounts for
   * rotation, line geometry and image bounds — so we don't reimplement any
   * per-type bounds maths here.
   */
  fitToContent(): void {
    if (!this.stage) return;
    const nodes = this.boardState
      .allObjects()
      .map((o) => this.konvaNodes.get(o.id))
      .filter((n): n is Konva.Node => !!n);
    if (nodes.length === 0) return;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const node of nodes) {
      const r = node.getClientRect({ relativeTo: this.stage });
      if (r.width === 0 && r.height === 0) continue;
      minX = Math.min(minX, r.x);
      minY = Math.min(minY, r.y);
      maxX = Math.max(maxX, r.x + r.width);
      maxY = Math.max(maxY, r.y + r.height);
    }
    if (!Number.isFinite(minX)) return;

    const boxW = Math.max(maxX - minX, 1);
    const boxH = Math.max(maxY - minY, 1);
    const stageW = this.stage.width();
    const stageH = this.stage.height();

    // Leave a comfortable margin around the content, then clamp to the same
    // zoom limits the wheel handler respects (so fit can't exceed them).
    const MARGIN = 0.9;
    const rawScale = Math.min(stageW / boxW, stageH / boxH) * MARGIN;
    const scale = Math.max(
      DEFAULT_ZOOM.minScale,
      Math.min(DEFAULT_ZOOM.maxScale, rawScale),
    );

    // Centre the content box in the viewport: screen = world * scale + offset,
    // so the box centre maps to the stage centre.
    const cx = minX + boxW / 2;
    const cy = minY + boxH / 2;
    const next = {
      x: stageW / 2 - cx * scale,
      y: stageH / 2 - cy * scale,
      scale,
    };
    // Die Buehne zieht der `viewport-changed`-Horcher nach.
    this.boardState.setViewport(next);
  }

  get tool(): Tool { return this.toolCtrl.tool; }
  setTool(tool: Tool): void {
    this.toolCtrl.setTool(tool);
  }

  /**
   * Enable dragging on board objects only in select mode. In placement
   * modes (line, rectangle, sticky, …) a mousedown on an existing object
   * must not move it — the drag-to-create gesture wins instead.
   * Also clears any hover preview when leaving select mode.
   *
   * ALSO gated on `readonly` (bugfix, Anke 2026-09-16 — "kann ich elemente
   * verschieben, ohne dass ich vorher auf bearbeiten geklickt habe"):
   * `#applyReadonly()` forces the tool to `select` while readonly (so
   * placement tools can't be picked), which — before this fix — made
   * `inSelect` true and therefore left every node fully draggable exactly
   * while the board was supposed to be locked. `readonly` is checked
   * separately from `tool`, not folded into `lockToSelect()`'s own state,
   * because the two answer different questions (which TOOL is active vs.
   * whether existing objects may move at all).
   */
  private applyNodeDraggability(): void {
    const canDrag = this.toolCtrl.tool === 'select' && !this.readonly;
    for (const node of this.konvaNodes.values()) {
      (node as Konva.Node).draggable(canDrag);
    }
    // Anke, 2026-07-19: hing frueher am (nun entfernten) Hover-Wachstum, das
    // dabei zurueckgesetzt wurde. Der Aufraeum-Teil bleibt noetig und haengt
    // jetzt an der Sache, um die es wirklich geht: die Eck-Vorschau. Sie wird
    // sonst erst bei der naechsten Mausbewegung abgebaut (`updateHoverCorner`
    // laeuft nur auf `mousemove`) und haengt beim Werkzeugwechsel sichtbar nach.
    if (!canDrag) this.clearHoverCornerPreview();
  }

  get selectedIds(): ReadonlyArray<string> { return this.selectionCtrl.selectedIds; }
  selectIds(ids: ReadonlyArray<string>): void { this.selectionCtrl.select(ids); }
  clearSelection(): void { this.selectionCtrl.clear(); }

  /**
   * Bounding box of an object in pixels, relative to the **board host's
   * border-box**. Reads the Konva node's current transform (not
   * BoardState) so the result is correct mid-drag / mid-transform, where
   * Konva updates the node live but state only commits on release.
   *
   * Accounts for any border/padding between the host element and the
   * stage container, so consumers placed next to the board can align
   * themselves directly to the result without off-by-border glitches.
   *
   * Returns null if the id isn't on the board.
   */
  screenBboxFor(id: string): { x: number; y: number; width: number; height: number; rotation: number } | null {
    const node = this.konvaNodes.get(id);
    if (!node) return null;
    const vp = this.boardState.viewport;
    const { dx, dy } = this.stageContainerOffset();
    return {
      x: dx + node.x() * vp.scale + vp.x,
      y: dy + node.y() * vp.scale + vp.y,
      width:  node.width()  * vp.scale,
      height: node.height() * vp.scale,
      rotation: node.rotation(),
    };
  }

  /**
   * Pixel offset of `.board-root` (the Konva stage container) from this
   * host element's own origin. With a 1px border on o2d-whiteboard, for
   * example, the stage sits 1px to the right and down of the host.
   */
  private stageContainerOffset(): { dx: number; dy: number } {
    const container = this.querySelector('.board-root');
    if (!container) return { dx: 0, dy: 0 };
    const host = this.getBoundingClientRect();
    const inner = container.getBoundingClientRect();
    return { dx: inner.left - host.left, dy: inner.top - host.top };
  }

  /**
   * Change one property on the currently-selected object.
   *
   * Special case — rich-text edit mode on a text box:
   *   - `color`, `fontSize`, `fontWeight` apply to the current text
   *     selection (wrap in an inline span). No history entry is pushed
   *     per keystroke; the whole edit session commits as one entry on
   *     blur.
   *   - Layout-level properties (`hAlign`, `vAlign`, `background`) still
   *     fall through to the whole object — partial alignment doesn't
   *     make UX sense inside a single box.
   */
  updateSelectedProperty(key: MutableObjectKey, value: unknown): void {
    const ids = this.selectionCtrl.selectedIds;
    if (ids.length === 0) return;

    // Anke, 2026-07-23 — MEHRFACHAUSWAHL: dieselbe Einstellung auf ALLE
    // Ausgewählten anwenden, als EIN Rückgängig-Schritt. Der delikate
    // Einzelauswahl-Pfad unten (Live-Bearbeiten einer Textbox, Bus-Dispatch)
    // bleibt unangetastet — eine Mehrfachauswahl ist nie im Bearbeiten-Modus,
    // darum genügt hier der Befehls-Weg pro Objekt.
    if (ids.length > 1) {
      const commands = ids
        .map((id) => this.#buildSelectionPropertyCommand(id, key, value))
        .filter((c): c is Command<BoardState> => c !== null);
      if (commands.length === 0) return;
      this._historyAdapter.push(
        commands.length === 1
          ? commands[0]!
          : new CompositeCommand(commands, `${commands.length} Objekte ändern`),
        this.boardState,
      );
      return;
    }

    const id = ids[0]!;
    const obj = this.boardState.getObject(id);

    // Textbox inline-format keys. While EDITING, format the live selection
    // per-run via TipTap (existing behaviour). While merely SELECTED (not
    // editing), apply the format to the WHOLE box — every run — so the box takes
    // the chosen colour/weight/etc. across all its text (Anke's request), and
    // return so we don't ALSO write a container-only property below.
    if (obj?.type === 'text' && isSelectionFormatKey(key)) {
      if (!this.isTextboxEditing) {
        this.#applyWholeTextboxFormat(id, key, value);
        return;
      }
      this.#bus?.uiActions$.next({
        type: 'textbox/apply-format',
        id: objectId(id),
        formatKey: key,
        value: value as string | number,
      });
    }

    // Horizontal alignment on a SELECTED (not editing) textbox: apply to every
    // paragraph of the whole box. (While editing, the optionsbar routes align
    // through `textbox/apply-align` instead, so this path is not hit.)
    if (obj?.type === 'text' && key === 'hAlign' && !this.isTextboxEditing) {
      this.#applyWholeTextboxFormat(id, key, value);
      return;
    }

    // Selection-scoped inline formats (fontWeight / fontStyle / textDecoration /
    // textTransform / verticalScript) are applied as TipTap inline marks via the
    // UiAction above. They must NOT also push an UpdatePropertyCommand: an
    // object-level write would make the format sticky for the whole textbox.
    // (obj.fontWeight etc. stay as container-level defaults — see DD3.)
    //
    // Only a TEXTBOX routes them through TipTap. Objects that paint their label
    // with Konva instead (person) have no editor and no inline marks, so for
    // them these keys are ordinary object properties and must fall through to
    // the UpdatePropertyCommand below — otherwise the write is silently dropped.
    if (
      obj?.type === 'text' &&
      (key === 'fontWeight' ||
        key === 'fontStyle' ||
        key === 'textDecoration' ||
        key === 'textTransform' ||
        key === 'verticalScript')
    )
      return;

    this._historyAdapter.push(new UpdatePropertyCommand(id, key, value), this.boardState);
  }

  /**
   * Change SEVERAL properties on the currently-selected object(s) as ONE
   * undo step (Stefan/review-01, 2026-08-29 — the checkbox size picker sets
   * `width` AND `height` from a single click; calling `updateSelectedProperty`
   * twice pushed two separate history entries for what the user experiences
   * as one action).
   *
   * Deliberately narrower than `updateSelectedProperty`: no textbox
   * live-edit-mode branching (inline formats, `hAlign`, TipTap dispatch) —
   * none of today's callers (checkbox size) ever target a textbox. Reuses
   * the same per-object command builder the multi-selection fan-out above
   * already relies on, just over `entries × ids` instead of `1 × ids`.
   */
  updateSelectedProperties(entries: ReadonlyArray<readonly [MutableObjectKey, unknown]>): void {
    const ids = this.selectionCtrl.selectedIds;
    if (ids.length === 0 || entries.length === 0) return;

    const commands = ids
      .flatMap((id) => entries.map(([key, value]) => this.#buildSelectionPropertyCommand(id, key, value)))
      .filter((c): c is Command<BoardState> => c !== null);
    if (commands.length === 0) return;
    this._historyAdapter.push(
      commands.length === 1
        ? commands[0]!
        : new CompositeCommand(commands, `${entries.length} Eigenschaften ändern`),
      this.boardState,
    );
  }

  /**
   * Setzt die Schriftgröße (Feinstufen-Index 1–24) auf dem ausgewählten Textfeld.
   *
   * Ist der Nutzer gerade am Tippen und hat Text markiert, wird die Größe nur auf
   * die Markierung angewandt — als em-Wert relativ zur Container-Schriftgröße, damit
   * der Text beim Zoomen korrekt mit der Box mitskaliert.
   * Ist nichts markiert (oder das Objekt nicht im Bearbeitungsmodus), wird die
   * Größe auf den GANZEN Text (alle Runs) + den Container-Default angewandt.
   */
  applyFontLevelToSelection(level: number): void {
    const ids = this.selectionCtrl.selectedIds;
    if (ids.length === 0) return;

    // Anke, 2026-07-23 — MEHRFACHAUSWAHL mehrerer Textboxen: die Schriftgröße
    // auf jede in EINEM Rückgängig-Schritt setzen. (Personen laufen über
    // `updateSelectedProperty`, das ohnehin auffächert; hierher kommt nur der
    // Textbox-Fall.) Nie im Bearbeiten-Modus, darum reiner Befehls-Weg.
    if (ids.length > 1) {
      // Kein Cluster-Faktor mehr (Anke, 2026-08-13 — Bugreport): Stufe N muss
      // auf jedem Zoom denselben Welteinheiten-Wert ergeben, siehe die
      // Einzelauswahl unten für die vollständige Begründung.
      const clampedWu = fontIndexToWorldUnits(level);
      const commands = ids
        .map((id) => this.boardState.getObject(id))
        .filter((o): o is TextBox => o?.type === 'text')
        .map((o) => {
          const doc = o.document ?? EMPTY_PARAGRAPH_DOC;
          const newDoc = applyWholeDocumentFormat(doc, { kind: 'textStyle', attrs: { fontSize: `${clampedWu}px` } });
          return new ApplyWholeTextFormatCommand(o.id, newDoc, 'fontSize', clampedWu);
        });
      if (commands.length === 0) return;
      this._historyAdapter.push(
        commands.length === 1
          ? commands[0]!
          : new CompositeCommand(commands, `${commands.length} Textgrößen ändern`),
        this.boardState,
      );
      return;
    }

    const id = ids[0]!;
    const obj = this.boardState.getObject(id);
    if (!obj || obj.type !== 'text') return;

    // KEIN Cluster-Faktor mehr (Anke, 2026-08-13 — Bugreport): eine gewählte
    // Schriftgrößenstufe muss auf JEDEM Zoom denselben Welteinheiten-Wert
    // ergeben, sonst sehen zwei mit "derselben" Stufe gezeichnete/gesetzte
    // Textboxen bei gleichem Betrachtungszoom unterschiedlich groß aus, je
    // nachdem in welchem Zoom-Band sie gesetzt wurden (gemessen: 38–77px
    // Schwankung bei identisch gewählter Stufe). Dass Text beim Reinzoomen
    // größer wirkt, ist normales, erwartetes Zoomverhalten — kein Grund mehr,
    // die Welteinheit selbst zu verkleinern.
    const scale = this.boardState.viewport.scale;
    const clampedWu = fontIndexToWorldUnits(level);

    // PRD-F6 §T20: Slice-owned dispatch for selection-based font-size.
    // TextboxEditorController applies wu/scale to the selection if the
    // editor is open; the UpdatePropertyCommand below updates the whole-object
    // fontSize.
    this.#bus?.uiActions$.next({
      type: 'textbox/apply-fontsize',
      id: objectId(id),
      wu: clampedWu,
      scale,
    });

    // Whole-box sizing ONLY when the box is merely selected (NOT being edited):
    // push the new size onto every run + the container default, overriding any
    // per-run marks. While EDITING, the `textbox/apply-fontsize` dispatch above
    // is authoritative — it sizes a non-empty selection, or (collapsed caret)
    // sets a TipTap stored mark that applies to the NEXT typed character and is
    // discarded on commit if nothing is typed. So a size picked with a collapsed
    // caret and no further input must NOT persist (Anke's bug).
    if (!this.isTextboxEditing) {
      const doc = (obj as TextBox).document ?? EMPTY_PARAGRAPH_DOC;
      const newDoc = applyWholeDocumentFormat(doc, { kind: 'textStyle', attrs: { fontSize: `${clampedWu}px` } });
      this._historyAdapter.push(
        new ApplyWholeTextFormatCommand(id, newDoc, 'fontSize', clampedWu),
        this.boardState,
      );
    }
  }

  /**
   * Apply a text format to the WHOLE textbox (every run / block) plus its
   * container default, in one undoable step. Used when a textbox is selected but
   * NOT being edited — the options-circle then formats the entire box instead of
   * a live selection. Maps the optionsbar property key to the matching document
   * transform (see `text-document-format`). Unsupported keys are a no-op.
   */
  /**
   * Anke, 2026-07-23 — den Befehl bauen, der `key=value` auf EIN Objekt der
   * Mehrfachauswahl anwendet (oder `null`, wenn nichts zu tun ist).
   *
   * Spiegelt den Einzelauswahl-Pfad für den NICHT-bearbeitenden Fall (eine
   * Mehrfachauswahl bearbeitet nie live): Bei einer Textbox gehen Zeichen- und
   * Absatz-Formate über die Ganz-Dokument-Formatierung; die reinen Inline-Marken
   * (fontWeight/fontStyle/…) sind darin enthalten (`wholeDocFormatSpec`). Alle
   * anderen Objekte bekommen ein gewöhnliches Property-Update.
   */
  #buildSelectionPropertyCommand(
    id: string,
    key: MutableObjectKey,
    value: unknown,
  ): Command<BoardState> | null {
    const obj = this.boardState.getObject(id);
    if (!obj) return null;
    if (obj.type === 'text' && (isSelectionFormatKey(key) || key === 'hAlign')) {
      // Nur Formate mit Ganz-Dokument-Entsprechung; der Rest (z. B. eine reine
      // Container-Eigenschaft ohne Doc-Spiegel) fällt auf `null`.
      return this.#buildWholeTextboxFormat(id, key, value);
    }
    return new UpdatePropertyCommand(id, key, value);
  }

  #applyWholeTextboxFormat(id: string, key: MutableObjectKey, value: unknown): void {
    const cmd = this.#buildWholeTextboxFormat(id, key, value);
    if (cmd) this._historyAdapter.push(cmd, this.boardState);
  }

  /**
   * Wie `#applyWholeTextboxFormat`, aber gibt den Befehl ZURÜCK statt ihn zu
   * schieben — damit eine Mehrfachauswahl mehrere Boxen in EINEM
   * Rückgängig-Schritt formatieren kann (Anke, 2026-07-23).
   */
  #buildWholeTextboxFormat(
    id: string,
    key: MutableObjectKey,
    value: unknown,
  ): Command<BoardState> | null {
    const obj = this.boardState.getObject(id);
    if (!obj || obj.type !== 'text') return null;
    const spec = wholeDocFormatSpec(key, value);
    if (!spec) return null;
    const doc = (obj as TextBox).document ?? EMPTY_PARAGRAPH_DOC;
    const newDoc = applyWholeDocumentFormat(doc, spec.fmt);
    return new ApplyWholeTextFormatCommand(id, newDoc, spec.containerKey, spec.containerValue);
  }

  /**
   * Duplicate the current selection. Each clone gets a fresh id and is
   * offset by (20, 20) in world space so it's visible next to the source.
   * The new objects become the new selection.
   */
  duplicateSelected(): void {
    // Gruppen werden samt Mitgliedern geklont, und die Verweise zeigen danach auf
    // die KLONE — sonst landete das Duplikat in der Gruppe des Originals und
    // beide bewegten sich gemeinsam.
    const ids = expandForTransfer(this.selectionCtrl.selectedIds, this.boardState.allObjects());
    if (ids.length === 0) return;
    const sources = ids
      .map((id) => this.boardState.getObject(id))
      .filter((o): o is BoardObject => !!o);
    const clones = cloneWithGroups(sources, generateShortId, {
      x: DUPLICATE_OFFSET_PX,
      y: DUPLICATE_OFFSET_PX,
    });
    if (clones.length === 0) return;
    const cmd = new DuplicateObjectsCommand(clones);
    this._historyAdapter.push(cmd, this.boardState, { sourceUids: ids });
    this.selectionCtrl.select(cmd.cloneIds);
  }

  /**
   * Copy the selection (Ctrl+C und Optionskreis-Knopf).
   *
   * Anke, 2026-07-21 — schreibt jetzt in BEIDE Ablagen: die echte
   * Rechner-Zwischenablage (damit Kopieren zwischen Boards und Browser-Tabs
   * funktioniert) UND den board-eigenen Spiegel. Der Spiegel ist kein zweiter
   * Zwischenspeicher mehr, sondern der Rückfall für den Fall, dass der Browser
   * das LESEN der Zwischenablage verweigert (Firefox/Safari fragen dafür um
   * Erlaubnis, siehe `#readSystemClipboard`).
   */
  copySelected(): void {
    const objs = this.#selectedObjects();
    if (objs.length === 0) return;
    clipboard.write(objs);
    this.#writeSystemClipboard(objs);
  }

  /** Die ausgewählten Objekte, aufgelöst und ohne Lücken. */
  #selectedObjects(): BoardObject[] {
    // Auf Mitglieder ausgeweitet: Eine kopierte Gruppe ohne ihre Mitglieder waere
    // beim Einfuegen eine leere Klammer.
    return expandForTransfer(this.selectionCtrl.selectedIds, this.boardState.allObjects())
      .map((id) => this.boardState.getObject(id))
      .filter((o): o is BoardObject => !!o);
  }

  /**
   * Generation of the latest copy. Every copy/cut counts it up; an older copy whose picture
   * arrives late sees it has been overtaken and writes nothing (measured in review: without it
   * a slow image copy overwrote a newer copy of a sticky note).
   */
  #clipboardWriteGeneration = 0;

  /**
   * Puts the copied board objects on the system clipboard (ticket "bild kopieren ermöglichen",
   * Stefan, 2026-10-28).
   *
   * - Exactly one image: ONE entry with the picture as `image/png` and the board marker as
   *   `text/plain`. Programs that read pictures take the image (measured with LibreOffice Writer
   *   on Linux); the board recognises its marker on paste. The picture is loaded as a promise, so the clipboard call itself happens inside
   *   the user gesture.
   * - Two or more images: the marker only, plus the `whiteboard-clipboard-image-skipped` event
   *   so the host can tell the user why no picture was copied.
   * - Anything else: the marker only, as before.
   *
   * A failure is no longer silent: a failed picture falls back to the marker alone (the browser
   * drops the whole entry otherwise) and is reported with `console.warn`. Without a clipboard
   * API at all (no secure context) the board-internal mirror is all there is — also reported.
   */
  #writeSystemClipboard(objs: ReadonlyArray<BoardObject>): void {
    const generation = ++this.#clipboardWriteGeneration;
    const isCurrent = () => generation === this.#clipboardWriteGeneration;
    const text = encodeClipboardPayload(objs);
    const plan = planClipboardImage(objs);
    if (plan.kind === 'marker-only' && plan.reason === 'multiple-images') {
      this.dispatchEvent(new CustomEvent<WhiteboardClipboardImageSkippedEventDetail>(
        WHITEBOARD_CLIPBOARD_IMAGE_SKIPPED_EVENT,
        { detail: { reason: plan.reason, imageCount: plan.imageCount }, bubbles: true, composed: true },
      ));
    }
    const clipboardApi = navigator.clipboard;
    if (!clipboardApi) {
      console.warn('[o2d-whiteboard] no clipboard access (insecure context?) — only the board-internal copy is kept');
      return;
    }
    if (plan.kind === 'image' && typeof ClipboardItem !== 'undefined' && typeof clipboardApi.write === 'function') {
      // The source is read now, synchronously: a cut removes the object right after this call.
      const png = pngBlobForClipboard(plan.source, isCurrent);
      void writeImageWithFallback(
        {
          write: (picture, marker) => clipboardApi.write([
            new ClipboardItem({ 'image/png': picture, 'text/plain': new Blob([marker], { type: 'text/plain' }) }),
          ]),
          writeText: (marker) => clipboardApi.writeText(marker),
          isCurrent,
          warn: (message, error) => console.warn(message, error),
        },
        png,
        text,
      );
      return;
    }
    clipboardApi.writeText(text).catch((error: unknown) => {
      if (isCurrent()) console.warn('[o2d-whiteboard] copying to the clipboard failed — only the board-internal copy is kept', error);
    });
  }

  /** Cut: copy the selection into the clipboard, then remove it. */
  cutSelected(): void {
    const ids = this.selectionCtrl.selectedIds;
    const objs = this.#selectedObjects();
    if (objs.length === 0) return;
    clipboard.write(objs);
    this.#writeSystemClipboard(objs);
    this._historyAdapter.push(new RemoveObjectWithAnchorsCommand(ids), this.boardState);
    this.selectionCtrl.clear();
  }

  /**
   * Paste board objects from the in-memory clipboard (Ctrl+V).
   * Each object gets a fresh id and is shifted +20/+20 so it doesn't
   * land exactly on top of the original.
   */
  pasteSelected(): void {
    // Der KNOPF im Optionskreis. Anders als Strg+V hat er kein natives
    // paste-Ereignis, aus dem er die Zwischenablage synchron lesen könnte — er
    // muss `navigator.clipboard.readText()` fragen, und das verlangt in
    // Firefox/Safari eine ausdrückliche Erlaubnis. Anke, 2026-07-21: fragen,
    // und bei Verweigerung auf das zuletzt im Board Kopierte zurückfallen. In
    // Chrome ist es damit immer die echte Ablage, sonst wenigstens nie kaputt.
    void this.#pasteFromSystemClipboardOrMirror();
  }

  async #pasteFromSystemClipboardOrMirror(): Promise<void> {
    let text = '';
    try {
      text = (await navigator.clipboard?.readText()) ?? '';
    } catch {
      // Erlaubnis verweigert oder nicht unterstützt — Spiegel übernimmt.
    }
    if (text !== '') {
      const objs = decodeClipboardPayload(text);
      if (objs) {
        clipboard.write(objs); // Spiegel nachziehen, damit beide Wege übereinstimmen
        this.#pasteFromMirror();
        return;
      }
      // Fremder Text: als neue Textbox ablegen (Ankes Entscheidung) — was in der
      // Ablage liegt, soll auch herauskommen.
      this.pasteTextAsTextbox(text);
      return;
    }
    this.#pasteFromMirror();
  }

  /**
   * Fremden Zwischenablage-Text als neue Textbox in der Bildschirmmitte ablegen.
   *
   * Anke, 2026-07-21 — "was in der Ablage liegt, soll auch herauskommen".
   * Bilder machen das längst so; Text zog bisher als Einziger nicht mit, und
   * genau das erzeugte den Eindruck einer zweiten, taub gestellten Ablage.
   *
   * Öffentlich, weil beide Einfügewege (Taste und Knopf) hier landen.
   */
  pasteTextAsTextbox(text: string): void {
    const trimmed = text.trim();
    if (trimmed === '' || !this.stage) return;
    const vp = this.boardState.viewport;
    const scale = vp.scale || 1;
    const point = {
      x: (this.stage.width() / 2 - vp.x) / scale,
      y: (this.stage.height() / 2 - vp.y) / scale,
    };
    // Absätze direkt bauen statt über HTML: Zwischenablage-Text ist unbekannter
    // Herkunft, und ein HTML-Umweg müsste ihn erst maskieren — ein `<b>` aus
    // einer Mail würde sonst als Auszeichnung gelesen statt als Zeichen.
    const paragraphs = trimmed.split(/\r?\n/).map((line) => ({
      type: 'paragraph',
      ...(line === '' ? {} : { content: [{ type: 'text', text: line }] }),
    }));
    const box: TextBox = {
      ...createTextBoxDefaults(scale, point, null),
      document: { type: 'doc', content: paragraphs } as TextDocument,
      text: trimmed,
    };
    this._historyAdapter.push(new AddObjectCommand(box), this.boardState);
    this.selectionCtrl.select([box.id]);
  }

  /** Board-Objekte aus dem Spiegel einfügen (der bisherige Weg). */
  #pasteFromMirror(): void {
    if (clipboard.isEmpty) return;
    const sources = clipboard.read();
    const sourceUids = sources.map((o) => o.id);
    // Wie beim Duplizieren: Gruppen-Verweise auf die Klone umschreiben, sonst
    // haengt das Eingefuegte an der Gruppe des Originals.
    const clones = cloneWithGroups(sources, generateShortId, {
      x: DUPLICATE_OFFSET_PX,
      y: DUPLICATE_OFFSET_PX,
    });
    const cmd = new DuplicateObjectsCommand(clones);
    this._historyAdapter.push(cmd, this.boardState, { sourceUids });
    this.selectionCtrl.select(cmd.cloneIds);
  }

  /**
   * Anke, 2026-07-21 — die Auswahl zu einer Gruppe zusammenfassen.
   *
   * Gruppiert wird über die ÄUSSERSTEN Vorfahren der Auswahl: Wer eine Gruppe
   * anklickt, meint sie als Ganzes. Genau daraus entsteht Ankes Punkt 2 —
   * Gruppe plus weiteres Element ergibt eine Gruppe IN der Gruppe, statt die
   * alte aufzubrechen.
   */
  groupSelected(): void {
    const byId = new Map(this.boardState.allObjects().map((o) => [o.id, o]));
    const memberIds = groupingCandidates(this.selectionCtrl.selectedIds, byId);
    if (memberIds.length < 2) return;

    const members = memberIds
      .map((id) => this.boardState.getObject(id))
      .filter((o): o is BoardObject => !!o);
    const group: GroupObject = {
      id: generateShortId(),
      type: 'group',
      rotation: 0,
      // Die Box wird mitgeführt, damit alles, was eine Bounding-Box erwartet
      // (Auswahlrahmen, Ausrichten, "auf alles zoomen"), ohne Sonderfall läuft.
      ...boundingBox(members),
      // Die neue Gruppe erbt die Zugehörigkeit ihrer Mitglieder, wenn die alle
      // schon in derselben Übergruppe lagen — sonst fiele sie beim Gruppieren
      // aus ihr heraus.
      ...(members.every((m) => m.groupId === members[0]!.groupId) && members[0]!.groupId
        ? { groupId: members[0]!.groupId }
        : {}),
    };

    const cmd = new GroupObjectsCommand(group, memberIds, captureMemberships(memberIds, this.boardState));
    this._historyAdapter.push(cmd, this.boardState);
    // Die Mitglieder bleiben ausgewaehlt, nicht die Klammer — sonst haette die
    // Auswahl keinen einzigen Knoten auf der Buehne.
    this.selectionCtrl.select(clickSelectionFor(cmd.groupId, this.boardState.allObjects()));
    // Die Auswahl-IDS aendern sich dabei NICHT (dieselben Mitglieder), also
    // meldet der Auswahl-Controller keine Aenderung und nichts wird neu
    // gezeichnet. Was sich aendert, ist ihre ZUGEHOERIGKEIT — und genau davon
    // haengen die Einzelrahmen ab. Darum hier ausdruecklich anstossen.
    this.refreshTransformer(this.selectionCtrl.selectedIds);
  }

  /**
   * Anke, 2026-07-21 — die äusserste Gruppenebene der Auswahl auflösen.
   *
   * Bewusst nur EINE Ebene: Nach dem Auflösen sind die vormals inneren Gruppen
   * die neuen äussersten, und ein erneuter Druck erwischt sie. So schält sich
   * die Verschachtelung Schicht für Schicht ab, wie von Anke beschrieben.
   */
  ungroupSelected(): void {
    const byId = new Map(this.boardState.allObjects().map((o) => [o.id, o]));
    const targetIds = ungroupTargets(this.selectionCtrl.selectedIds, byId);
    if (targetIds.length === 0) return;

    const groups = groupsOf(targetIds, this.boardState);
    const freed = groups.flatMap((g) => directMemberIds(g.id, this.boardState));
    const cmd = new UngroupObjectsCommand(groups, captureMemberships(freed, this.boardState));
    this._historyAdapter.push(cmd, this.boardState);
    // Die freigewordenen Teile bleiben ausgewaehlt — die Gruppe gibt es nicht
    // mehr. War ein befreites Teil selbst eine Gruppe, treten deren Mitglieder
    // an seine Stelle: In der Auswahl steht nur, was auch gezeichnet wird.
    const objectsNow = this.boardState.allObjects();
    this.selectionCtrl.select([
      ...new Set(cmd.freedIds.flatMap((id) => clickSelectionFor(id, objectsNow))),
    ]);
    // Aus einer Einheit werden mehrere — dieselbe Nachziehpflicht wie beim
    // Gruppieren, denn die IDS koennen unveraendert bleiben.
    this.refreshTransformer(this.selectionCtrl.selectedIds);
  }

  /**
   * Die äusserste Gruppe über `id`, oder `id` selbst. Von aussen betrachtet ist
   * die Verschachtelung unsichtbar — man greift immer das Ganze.
   */
  #clickSelection(id: string): string[] {
    return clickSelectionFor(id, this.boardState.allObjects());
  }

  /**
   * Anke, 2026-07-22 — Shift/Strg-Klick schaltet die Auswahl eines Objekts um.
   *
   * Gruppenbewusst über dieselbe Auflösung wie ein normaler Klick: Es geht immer
   * um die ganze Einheit (alle zeichenbaren Mitglieder). Ist die Einheit KOMPLETT
   * ausgewählt, wird sie abgewählt, sonst hinzugefügt (ein teilweise gewähltes
   * Set vervollständigt sich also). In EINEM `select()`-Aufruf, damit die Anzeige
   * nur einmal nachzieht statt pro Mitglied.
   */
  #toggleSelection(id: string): void {
    const next = toggleUnitSelection(this.selectionCtrl.selectedIds, this.#clickSelection(id));
    this.selectionCtrl.select(next);
  }

  /** Lässt sich die aktuelle Auswahl gruppieren? Treibt den Knopf-Zustand. */
  get canGroupSelection(): boolean {
    const byId = new Map(this.boardState.allObjects().map((o) => [o.id, o]));
    return canGroup(this.selectionCtrl.selectedIds, byId);
  }

  /** Ist in der Auswahl eine Gruppe zum Auflösen? Treibt den Knopf-Zustand. */
  get canUngroupSelection(): boolean {
    const byId = new Map(this.boardState.allObjects().map((o) => [o.id, o]));
    return canUngroup(this.selectionCtrl.selectedIds, byId);
  }

  deleteSelected(): void {
    // Anke, 2026-07-21 — eine ausgewaehlte Gruppe nimmt ihre Mitglieder mit.
    // Ohne das verschwaende nur die Klammer und die Mitglieder blieben verwaist
    // liegen: unsichtbar gruppiert zu etwas, das es nicht mehr gibt.
    const ids = expandForTransfer(this.selectionCtrl.selectedIds, this.boardState.allObjects());
    if (ids.length === 0) return;
    this._historyAdapter.push(new RemoveObjectWithAnchorsCommand(ids), this.boardState);
    this.selectionCtrl.clear();
  }

  /**
   * Anke, 2026-07-16 — align the selected objects to a shared edge of the
   * selection (see `computeAlignMoves` for the reference rule).
   *
   * Silently does nothing with fewer than two objects selected, matching how
   * copy/delete/z-order behave with no selection (Anke's decision): the align
   * buttons sit in part1 and are therefore drawn in EVERY circle, including
   * single-object ones, where there is nothing to align against.
   *
   * Reuses `MoveObjectsCommand` rather than a command of its own — an alignment
   * IS a move, just with the target worked out instead of dragged, and that
   * command already owns the hard parts: rigid line translation and the anchor
   * detach/keep rules, all as ONE undo step.
   */
  alignSelected(mode: AlignMode): void {
    const objs = this.selectionCtrl.selectedIds
      .map((id) => this.boardState.getObject(id))
      .filter((o): o is BoardObject => o !== undefined);
    const moves = computeAlignMoves(objs, mode);
    if (moves.length === 0) return;
    this._historyAdapter.push(new MoveObjectsCommand(moves), this.boardState);
  }

  // ---- Save / Load --------------------------------------------------------

  /**
   * Anke, 2026-07-16 — Export als echte Vektorgrafik (SVG).
   *
   * Zwei Funktionen: das GANZE Board (`exportSvg`) und nur die AUSWAHL
   * (`exportSelectionSvg`). Beide schneiden passend zu, unabhaengig davon, wohin
   * gerade gezoomt ist — die Ansicht spielt fuer den Export keine Rolle.
   *
   * Gibt `false` zurueck, wenn es nichts zu exportieren gab (leeres Board bzw.
   * keine Auswahl); dann entsteht bewusst keine leere Datei.
   */
  exportSvg(filename = 'zeichenfläche.svg'): boolean {
    return this.#downloadSvg(filename, undefined);
  }

  /** Nur die ausgewaehlten Objekte als SVG. Siehe {@link exportSvg}. */
  exportSelectionSvg(filename = 'auswahl.svg'): boolean {
    const ids = this.selectionCtrl.selectedIds;
    if (ids.length === 0) return false;
    return this.#downloadSvg(filename, new Set(ids));
  }

  #downloadSvg(filename: string, only: ReadonlySet<string> | undefined): boolean {
    // Ohne Strategien-Register koennen Linien nicht gezeichnet werden (es
    // entsteht erst in firstUpdated) — dann gibt es auch nichts zu exportieren.
    if (!this.linePathStrategyRegistry) return false;
    const svg = buildBoardSvg(this.boardState.allObjects(), {
      ...(only ? { only } : {}),
      lineRegistry: this.linePathStrategyRegistry,
    });
    if (!svg) return false;
    // Gleicher Download-Weg wie exportBoard.
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    return true;
  }

  /**
   * Serialise the current board (viewport + all objects) to a JSON file and
   * trigger a browser download.
   */
  exportBoard(filename = 'zeichenfläche.json'): void {
    // Die Ansicht (Zoom/Verschiebung) wird bewusst NICHT mitgeschrieben (Anke,
    // 2026-07-16): Beim Oeffnen wird ohnehin auf alles gezoomt, ein
    // gespeicherter Wert waere ein Feld, das niemand mehr liest. Aeltere
    // Dateien mit `viewport` laden weiter — `importBoard` ignoriert das Feld.
    const data = {
      version: 1,
      objects: this.boardState.allObjects(),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Restore the board from a JSON string produced by `exportBoard`.
   * Clears the current board, resets undo/redo history, and loads the
   * serialised objects.
   *
   * Eine in aelteren Dateien enthaltene `viewport`-Angabe wird bewusst
   * IGNORIERT (Anke, 2026-07-16): Beim Oeffnen wird immer auf alles gezoomt.
   * Das erledigt der `config`-Setter unten, deshalb steht hier nichts dazu.
   */
  importBoard(json: string): void {
    try {
      const data = JSON.parse(json) as {
        version?: number;
        objects?: BoardObject[];
      } | BoardObject[];

      const objects: BoardObject[] = Array.isArray(data)
        ? data
        : (data.objects ?? []);

      // Replace board content via public config setter.
      // The setter handles editor-commit, replaceAll (which emits state-replaced
      // and triggers Konva-node rebuild), history.clear, and Lit reactivity.
      this.config = objects;
    } catch (e) {
      console.error('importBoard: JSON konnte nicht geladen werden:', e);
    }
  }

  /** Bring the selected object to the front (highest z-order). */
  bringToFront(): void {
    this.#moveToEdge('top');
  }

  /** Send the selected object to the back (lowest z-order). */
  sendToBack(): void {
    this.#moveToEdge('bottom');
  }

  /**
   * Anke, 2026-07-21 — "ganz nach vorn/hinten", auch fuer eine Gruppe.
   *
   * Eine ausgewaehlte Gruppe ist selbst nie gezeichnet; ihren eigenen Z-Platz zu
   * verschieben taete sichtbar gar nichts. Bewegt werden muessen die MITGLIEDER,
   * und zwar als Block: ihre Reihenfolge UNTEREINANDER muss erhalten bleiben,
   * sonst wuerde eine Gruppe beim Nach-vorn-Holen innerlich umsortiert.
   *
   * Der Trick ist die Reihenfolge der Einzelschritte. Schiebt man nacheinander
   * jedes Mitglied nach ganz oben, landet das ZULETZT geschobene obenauf — also
   * von unten nach oben abarbeiten. Fuer "ganz nach hinten" entsprechend
   * umgekehrt.
   */
  #moveToEdge(direction: 'top' | 'bottom'): void {
    const selected = this.selectionCtrl.selectedIds;
    if (selected.length === 0) return;
    const all = this.boardState.allObjects();
    // Die Gruppen-Klammer selbst wird nicht gezeichnet und darum auch nicht
    // einsortiert — nur was man sieht.
    const movable = expandToMembers(selected, all).filter(
      (id) => this.boardState.getObject(id)?.type !== 'group',
    );
    if (movable.length === 0) return;

    const byIndex = movable
      .map((id) => ({ id, idx: this.boardState.getObjectIndex(id) }))
      .filter((e) => e.idx >= 0)
      .sort((a, b) => a.idx - b.idx);
    if (byIndex.length === 0) return;

    const ordered = direction === 'top' ? byIndex : [...byIndex].reverse();
    // `ZOrderCommand.revert` setzt auf den gemerkten Index zurueck — und zwar
    // auf den, den das Objekt UNMITTELBAR VOR SEINEM EIGENEN Schritt hatte. Alle
    // Indizes vorab zu erfassen ist darum falsch: Jede Verschiebung ruecht die
    // uebrigen mit, und der Rueckweg landete daneben (von einem Test gefangen).
    //
    // Beim Nach-vorn-Holen wird von unten nach oben abgearbeitet; jedes bereits
    // verschobene Mitglied kam von UNTERHALB der noch offenen und zieht deren
    // Index um eins nach unten — daher `- j`. Beim Nach-hinten-Schicken ist es
    // spiegelbildlich `+ j`.
    const commands = ordered.map((e, j) =>
      new ZOrderCommand(e.id, direction, direction === 'top' ? e.idx - j : e.idx + j),
    );
    this._historyAdapter.push(
      commands.length === 1
        ? commands[0]!
        : new CompositeCommand(commands, direction === 'top' ? 'Nach vorn' : 'Nach hinten'),
      this.boardState,
    );
  }

  /** Move the selected object one step forward in the z-order. */
  bringForward(): void {
    const ids = this.selectionCtrl.selectedIds;
    if (ids.length !== 1) return;
    const id = ids[0]!;
    const idx = this.boardState.getObjectIndex(id);
    if (idx < 0) return;
    const cmd = new ZOrderCommand(id, 'forward', idx);
    this._historyAdapter.push(cmd, this.boardState);
  }

  /** Move the selected object one step backward in the z-order. */
  sendBackward(): void {
    const ids = this.selectionCtrl.selectedIds;
    if (ids.length !== 1) return;
    const id = ids[0]!;
    const idx = this.boardState.getObjectIndex(id);
    if (idx < 0) return;
    const cmd = new ZOrderCommand(id, 'backward', idx);
    this._historyAdapter.push(cmd, this.boardState);
  }

  // ---- List type --------------------------------------------------------

  /**
   * Change the list type of a TextBox (single undo step — converts text format
   * and updates `listType` atomically).
   *
   * Toggle behaviour: passing the same type as the current one resets to 'none'.
   */
  setListType(id: string, newType: ListType): void {
    const obj = this.boardState.getObject(id);
    if (!obj || obj.type !== 'text') return;

    const effectiveType = obj.listType === newType ? 'none' : newType;

    // Reshape the authoritative TextDocument (wrap/convert/unwrap, marks
    // preserved) — never the stale `obj.text` mirror, which is '' post-035
    // and would collapse the document to an empty paragraph (content loss).
    const newDocument = reshapeDocumentListType(obj.document ?? EMPTY_PARAGRAPH_DOC, effectiveType);
    this._historyAdapter.push(new SetListTypeCommand(id, effectiveType, newDocument), this.boardState);
  }

  // ---- Test-only hooks --------------------------------------------------

  /**
   * PRD-RTE-OVERFLOW Phase 5 (E2E): read-only test hook that reports whether
   * the overset "+" indicator on the given textbox is currently visible.
   *
   * The Konva `stage` and `konvaNodes` map are private, so a Playwright spec
   * cannot read a node's `.visible()` directly. This minimal accessor (access
   * path "(b)" from the PRD test-hook prerequisite) exposes ONLY the indicator
   * visibility flag — no node reference escapes. Returns `undefined` when the
   * textbox node or its indicator child does not exist.
   *
   * NOT part of the published Public API. Exposed exclusively for the
   * `textbox-overflow.spec.ts` E2E gate, mirroring the `__BOARD__` test
   * exposure in `apps/demo/src/whiteboard-demo.ts`.
   */
  __overflowIndicatorVisible(id: string): boolean | undefined {
    const node = this.konvaNodes.get(id);
    if (!(node instanceof Konva.Group)) return undefined;
    return node.findOne<Konva.Group>('.textbox-overflow-indicator')?.visible();
  }

  /**
   * PRD-RTE-OVERFLOW Phase 5 (E2E): test-only hook that fires a synthetic
   * `click` on the overset "+" indicator of the given textbox, driving the
   * REAL renderer click handler (→ `textbox/fit-to-content` UiAction carrying
   * the real measured `cachedRequiredHeight` → an undoable height command).
   *
   * The indicator is a Konva canvas node (no DOM locator) and both `stage` and
   * `konvaNodes` are private, so a Playwright spec cannot click it by pixel
   * deterministically. This accessor fires the exact event the handler binds
   * to — the pixel hit-test is Konva's concern, not this feature's. Returns
   * `true` when the indicator was found and fired, `false` otherwise. NOT part
   * of the published Public API.
   */
  __clickOverflowIndicator(id: string): boolean {
    const node = this.konvaNodes.get(id);
    if (!(node instanceof Konva.Group)) return false;
    const indicator = node.findOne<Konva.Group>('.textbox-overflow-indicator');
    if (!indicator) return false;
    indicator.fire('click', { target: indicator } as unknown as Konva.KonvaEventObject<MouseEvent>, false);
    return true;
  }

  /**
   * Bugreport 2026-08-16 (E2E): read-only test hook that reports the REAL
   * on-PAGE client rect of one checklist item's checkbox hotspot
   * (`textbox.renderer.ts`'s `rebuildChecklistHotspots`), so a spec can
   * `page.mouse.click` at its exact CENTER instead of hand-computing a world
   * position. A synthetic `.fire('click', ...)` (like `__clickOverflowIndicator`
   * above) would bypass Konva's own pixel hit-testing entirely, defeating the
   * point for a test that specifically proves a REAL PIXEL click lands
   * correctly — this returns real geometry instead, letting Playwright drive
   * a genuine `page.mouse.click`.
   *
   * `Konva.Node.getClientRect()` WITHOUT a `relativeTo` — unlike `relativeTo:
   * stage` (see `fitToContent` below, which deliberately wants WORLD units by
   * passing that option) — already returns canvas-relative pixels with pan
   * AND zoom applied; adding the stage container's OWN page position converts
   * that to page coordinates a Playwright mouse event can use directly.
   * Returns `undefined` when the textbox, its checklist hotspot holder, or
   * the item at `index` does not exist. NOT part of the published Public API.
   */
  __checklistCheckboxClientRect(
    id: string,
    index: number,
  ): { x: number; y: number; width: number; height: number } | undefined {
    const node = this.konvaNodes.get(id);
    if (!(node instanceof Konva.Group) || !this.stage) return undefined;
    const holder = node.findOne<Konva.Group>('.textbox-checklist-hotspots');
    if (!holder) return undefined;
    const hotspot = holder
      .getChildren()
      .find((c) => c.getAttr('o2dChecklistIndex') === index);
    if (!hotspot) return undefined;
    const canvasRect = hotspot.getClientRect();
    const containerRect = this.stage.container().getBoundingClientRect();
    return {
      x: containerRect.left + canvasRect.x,
      y: containerRect.top + canvasRect.y,
      width: canvasRect.width,
      height: canvasRect.height,
    };
  }
  /**
   * Test-only hook (review 0034, Anke 2026-10-03): the on-PAGE client rect of
   * the hotspot of a textbox's `index`-th MEASURED link launcher (`o2dLinkIndex`,
   * like `o2dChecklistIndex` for `__checklistCheckboxClientRect` above).
   * Returns `undefined` when the textbox or its hotspot holder does not exist,
   * or when that launcher is clipped and therefore has no hotspot. NOT part of
   * the published Public API.
   */
  __linkHotspotClientRect(
    id: string,
    index: number,
  ): { x: number; y: number; width: number; height: number } | undefined {
    const node = this.konvaNodes.get(id);
    if (!(node instanceof Konva.Group) || !this.stage) return undefined;
    const hotspot = node
      .findOne<Konva.Group>('.textbox-link-hotspots')
      ?.getChildren()
      .find((c) => c.getAttr('o2dLinkIndex') === index);
    if (!hotspot) return undefined;
    const canvasRect = hotspot.getClientRect();
    const containerRect = this.stage.container().getBoundingClientRect();
    return {
      x: containerRect.left + canvasRect.x,
      y: containerRect.top + canvasRect.y,
      width: canvasRect.width,
      height: canvasRect.height,
    };
  }

  /**
   * Test-only hook (review 0034): whether the box-local point (x, y) of textbox
   * `id` lies over measured text — `TextBoxGroup.__isOverText`, which the
   * hover cursor asks. `undefined` when the id is not a textbox node. NOT part
   * of the published Public API.
   */
  __isOverTextOf(id: string, x: number, y: number): boolean | undefined {
    return (this.konvaNodes.get(id) as TextBoxGroup | undefined)?.__isOverText?.(x, y);
  }

  /**
   * PRD-RTE-OVERFLOW Phase 5 (E2E, T5.5): test-only hook that sets an ABSOLUTE
   * zoom level (`scale`) while keeping the current pan position, so the
   * zoom-independence spec can raster a textbox at scale 0.5 and 2.0 and assert
   * the resulting WORLD-UNIT height is identical.
   *
   * No public absolute-zoom setter exists (`resetZoom()` is fixed to 1, and the
   * `viewport/zoom` UiAction is relative and applied only by the wheel handler),
   * so this mirrors `resetZoom`'s update path (BoardState + Konva stage) with an
   * arbitrary scale. The raster reads `boardState.viewport.scale` live in
   * `onObjectAdded`, so a box placed AFTER this call rasters at the new scale.
   * NOT part of the published Public API.
   */
  __setZoom(scale: number): void {
    if (!this.stage) return;
    const vp = this.boardState.viewport;
    const next = { x: vp.x, y: vp.y, scale };
    this.boardState.setViewport(next);
    this.stage.scale({ x: scale, y: scale });
    this.stage.batchDraw();
  }

  /**
   * O2D.W-PRD-038 (edit-overlay line box tracks the scaled font on zoom-out)
   * — read-only E2E hook reporting the LIVE edit-overlay (`.textbox-editor`)
   * layout metrics in REAL CSS layout, so the MANDATORY Chromium e2e can prove
   * the per-line line box scales with the viewport (and no longer stays pinned
   * at the 100% value on zoom-out). happy-dom does no CSS layout and reads
   * `scrollHeight`/line rects as 0, so this is exercised ONLY in real Chromium.
   *
   * - `scrollHeight` = the overlay's full content height (content + padding),
   *   independent of the visible clip.
   * - `clientHeight` = the overlay's visible inner box height (`overflow:hidden`
   *   clip). `scrollHeight <= clientHeight` ⇔ the content is NOT overflowing.
   * - `lineAdvance` = the per-line line-box advance in px: the vertical distance
   *   between the first two rendered LINE boxes of a `Range` over the first
   *   block. A Range yields one client rect per inline fragment, and a line with
   *   NESTED font-size spans (the outer unscaled strut wrapping the inner scaled
   *   span) produces several rects at slightly different tops on the SAME line —
   *   so the rects are first CLUSTERED into lines by vertical overlap (a rect
   *   joins the current line while it starts before that line's running bottom),
   *   and the advance is `lineTop[1] - lineTop[0]`. This measures the true CSS
   *   line box (= max inline strut on the line), independent of glyph height;
   *   it falls back to the single line's box height when the block is one line.
   * - `lineCount` = number of clustered rendered lines.
   *
   * Read-only, edit-mode only: returns `undefined` when no textbox editor is
   * open. No node reference escapes. NOT part of the published Public API —
   * exposed exclusively for `textbox-edit-overflow.spec.ts`, mirroring the
   * `__BOARD__` test exposure in `apps/demo/src/whiteboard-demo.ts`.
   */
  __editOverlayMetrics(): EditOverlayMetrics | undefined {
    const overlay = this.querySelector<HTMLElement>('.textbox-editor');
    if (!overlay) return undefined;
    // 2026-08-15 (Anke, "clean rewrite") — `.textbox-editor-content` is the
    // element that actually carries the object's world-unit layout AND the
    // single `transform: scale(...)` that maps it to on-screen pixels (see
    // `TextboxEditorController#applyContentTransform`). `.textbox-editor`
    // (the outer, screen-px POSITIONING box) never has font/layout content
    // of its own any more, so metrics must come from the content wrapper.
    const editContent = overlay.querySelector<HTMLElement>('.textbox-editor-content') ?? overlay;
    const content = editContent.querySelector<HTMLElement>('.ProseMirror') ?? editContent;
    const block = content.querySelector<HTMLElement>('p, li, h1, h2, h3') ?? content;

    const range = document.createRange();
    range.selectNodeContents(block);
    const rects = Array.from(range.getClientRects()).sort((a, b) => a.top - b.top);

    // Cluster inline-fragment rects into visual lines by vertical overlap: a
    // rect belongs to the current line while it starts before the line's running
    // bottom; otherwise it opens the next line. Each line's top is the smallest
    // rect top in its cluster (the outer strut on that line).
    const lineTops: number[] = [];
    let lineBottom = -Infinity;
    for (const r of rects) {
      if (r.top < lineBottom - 0.5) {
        lineBottom = Math.max(lineBottom, r.bottom);
        continue;
      }
      lineTops.push(r.top);
      lineBottom = r.bottom;
    }

    let lineAdvance = 0;
    if (lineTops.length >= 2) {
      lineAdvance = lineTops[1]! - lineTops[0]!;
    } else if (rects.length >= 1) {
      lineAdvance = rects[0]!.height;
    }

    // `scrollHeight`/`clientHeight` are DOM layout-space properties — a CSS
    // `transform: scale(...)` never changes them (transform is paint-only),
    // so reading them directly off `editContent` (whose own layout is always
    // in UNSCALED world units by design) would silently stop reflecting
    // zoom. Project both into the same on-screen pixel space `lineAdvance`
    // already uses (`getClientRects()`, paint-space) by multiplying the
    // world-unit `scrollHeight` by the live viewport scale, and reading
    // `clientHeight`'s on-screen equivalent via `getBoundingClientRect()`.
    const scale = this.boardState.viewport.scale || 1;
    return {
      scrollHeight: editContent.scrollHeight * scale,
      clientHeight: editContent.getBoundingClientRect().height,
      lineAdvance,
      lineCount: lineTops.length,
    };
  }

  // ---- Placement --------------------------------------------------------

  /**
   * Place a sticky note. If `size` is given, `worldX/worldY` is the
   * top-left corner of the dragged box. If `size` is null, it's a bare
   * click — we centre a default-sized sticky on the pointer.
   */
  /**
   * @deprecated Direct placement is now owned by `StickyPlacementController`,
   * which subscribes to the `place/sticky` UiAction. This wrapper exists only
   * because the InteractionController callback table still names this method;
   * once the InteractionController dispatches the UiAction itself (P5/P6), this
   * method can be removed.
   */
  private placeStickyAt(worldX: number, worldY: number, size: { width: number; height: number } | null): void {
    if (!this.#bus) return;
    this.#bus.uiActions$.next({
      type: 'place/sticky',
      worldX,
      worldY,
      size: size ?? undefined,
    });
  }

  /**
   * Show / update / hide the live drag-to-create preview rectangle in
   * the overlay layer. Passing `null` removes it. The preview is purely
   * visual — the final object is added on mouseup through a command.
   */
  private renderDragPreview(box: WorldBox | null): void {
    if (!this.overlayLayer) return;
    if (!box) {
      this.dragPreviewRect?.destroy();
      this.dragPreviewRect = undefined;
      this.overlayLayer.batchDraw();
      return;
    }
    if (!this.dragPreviewRect) {
      const tool = this.toolCtrl.tool;
      // Only tools whose object actually paints a filled area preview as one.
      // Everything else (person, checkbox, rectangle, ellipse, smiley) is an
      // outline: a tinted box would promise a background the object never gets.
      const isText = tool === 'text';
      const isSticky = tool === 'sticky';

      // Bugreport (Anke, 2026-09-03): pre-selecting a colour in the options
      // circle before drawing (see `stagedToolProperties$`,
      // `o2d-svg-optionsbar.element.ts`'s `stagePrePlacementProperty()`) had
      // no effect on this preview — it kept showing the hardcoded default
      // (sticky's yellow) for the ENTIRE drag, only actually applying the
      // staged colour once the real object was created on mouseup. `fill`'s
      // resolved property differs per type (`color` for sticky, `background`
      // for text — same mapping `resolveProperty()` already uses for the
      // real, post-creation editing path).
      //
      // Only for text/sticky — the ONLY two tools whose preview shows a fill
      // at all (see the comment above): other tools (person, checkbox, …)
      // stay outline-only even when a `fill`-mapped property happens to be
      // staged (e.g. person's `circleFill`) — this bounding box is not what
      // ends up on screen for them, a tint would misrepresent it.
      const stagedFillProperty = (isText || isSticky) ? resolveProperty('fill', tool as unknown as BoardObject['type']) : null;
      const stagedFill = stagedFillProperty
        ? this.bus?.stagedToolProperties$.value[tool]?.[stagedFillProperty] as string | undefined
        : undefined;

      const fill = stagedFill ?? (isText ? '#ffffff' : isSticky ? '#fde68a' : undefined);
      const opacity = isText ? 0.85 : 1;

      if (tool === 'ellipse') {
        this.dragPreviewRect = new Konva.Ellipse({
          radiusX: 0, radiusY: 0,
          fill,
          opacity,
          stroke: '#b8c4d1',
          strokeWidth: 1,
          strokeScaleEnabled: false,
          listening: false,
        });
      } else {
        this.dragPreviewRect = new Konva.Rect({
          fill,
          opacity,
          stroke: '#b8c4d1',
          strokeWidth: 1,
          strokeScaleEnabled: false,
          listening: false,
        });
      }
      this.overlayLayer.add(this.dragPreviewRect);
    }
    if (this.dragPreviewRect instanceof Konva.Ellipse) {
      this.dragPreviewRect.setAttrs({
        x: box.x + box.width / 2,
        y: box.y + box.height / 2,
        radiusX: box.width / 2,
        radiusY: box.height / 2,
      });
    } else {
      this.dragPreviewRect.setAttrs({ x: box.x, y: box.y, width: box.width, height: box.height });
    }
    this.overlayLayer.batchDraw();
  }

  /**
   * @deprecated PRD-F2 §T28: Bus-dispatch wrapper. CheckboxPlacementController
   * subscribes to `place/checkbox` and owns the AddObjectCommand + selection
   * + edit-enter-plain re-emit chain.
   */
  private placeCheckboxAt(worldX: number, worldY: number): void {
    this.#bus?.uiActions$.next({ type: 'place/checkbox', worldX, worldY });
  }

  /**
   * @deprecated PRD-F2 §T28: Bus-dispatch wrapper. PersonPlacementController
   * subscribes to `place/person` and owns placement + plain-edit-enter chain.
   */
  private placePersonAt(worldX: number, worldY: number, size: { width: number; height: number } | null): void {
    this.#bus?.uiActions$.next({
      type: 'place/person',
      worldX,
      worldY,
      ...(size ? { size } : {}),
    });
  }

  /**
   * @deprecated PRD-F1 §T25: Bus-dispatch wrapper. Centring for bare clicks
   * is computed here so the wire payload always carries a top-left rect;
   * `RectanglePlacementController` handles the rest.
   */
  private placeRectangleAt(worldX: number, worldY: number, size: { width: number; height: number } | null): void {
    const cd = creationDefaults(this.boardState.viewport.scale);
    const w = size ? size.width  : cd.rectWidth;
    const h = size ? size.height : cd.rectHeight;
    const x = size ? worldX : worldX - w / 2;
    const y = size ? worldY : worldY - h / 2;
    this.#bus?.uiActions$.next({ type: 'place/rectangle', rect: { x, y, width: w, height: h } });
  }

  /**
   * @deprecated PRD-F1 §T25: Bus-dispatch wrapper. See `placeRectangleAt`.
   */
  private placeEllipseAt(worldX: number, worldY: number, size: { width: number; height: number } | null): void {
    const cd = creationDefaults(this.boardState.viewport.scale);
    const w = size ? size.width  : cd.rectWidth;
    const h = size ? size.height : cd.rectHeight;
    const x = size ? worldX : worldX - w / 2;
    const y = size ? worldY : worldY - h / 2;
    this.#bus?.uiActions$.next({ type: 'place/ellipse', rect: { x, y, width: w, height: h } });
  }

  /**
   * @deprecated PRD-F3 §TR4: Bus-dispatch wrapper. SmileyPlacementController
   * subscribes to `place/smiley` and owns the AddObjectCommand + selection
   * emission. Centring for bare clicks is handled by `createSmileyDefaults`.
   */
  private placeSmileyAt(worldX: number, worldY: number, size: { width: number; height: number } | null): void {
    this.#bus?.uiActions$.next({
      type: 'place/smiley',
      worldX,
      worldY,
      ...(size ? { size } : {}),
    });
  }

  private renderLinePreview(x1: number, y1: number, x2: number, y2: number): void {
    if (!this.overlayLayer) return;
    if (!this.linePreviewLine) {
      this.linePreviewLine = new Konva.Line({
        stroke: '#b8c4d1',
        strokeWidth: 1,
        strokeScaleEnabled: false,
        listening: false,
      });
      this.overlayLayer.add(this.linePreviewLine);
    }

    // Snap the end point (the one the user is dragging) to a nearby edge.
    const snap = this.findSnapAt(x2, y2);
    if (snap) {
      x2 = snap.x;
      y2 = snap.y;
      this.pendingLineAnchor = { endpoint: 'end', anchor: snap.anchor, x: snap.x, y: snap.y };
      this.showSnapIndicator(snap.x, snap.y);
    } else {
      this.pendingLineAnchor = null;
      this.hideSnapIndicator();
    }

    // World coordinates — the stage transform handles world→screen.
    this.linePreviewLine.points([x1, y1, x2, y2]);
    this.overlayLayer.batchDraw();
  }

  private removeLinePreview(): void {
    if (!this.linePreviewLine) return;
    this.linePreviewLine.destroy();
    this.linePreviewLine = undefined;
    this.hideSnapIndicator();
    this.overlayLayer?.batchDraw();
  }

  // ---- Anchor snap helpers ---------------------------------------------

  /**
   * Look for a snap target near the given world point. Snap radius is
   * expressed in screen pixels so the affordance feels consistent at
   * every zoom level.
   */
  private findSnapAt(worldX: number, worldY: number, excludeLineId?: string) {
    const scale = this.boardState.viewport.scale || 1;
    const worldRadius = SNAP_SCREEN_PX / scale;
    return findSnap(this.boardState.allObjects(), this.registry, worldX, worldY, worldRadius, excludeLineId);
  }

  private showSnapIndicator(worldX: number, worldY: number): void {
    if (!this.overlayLayer) return;
    if (!this.snapIndicator) {
      this.snapIndicator = new Konva.Circle({
        radius: 5,
        fill: '#ffffff',
        stroke: '#2a5fd7',
        strokeWidth: 2,
        strokeScaleEnabled: false,
        listening: false,
      });
      this.overlayLayer.add(this.snapIndicator);
    }
    this.snapIndicator.position({ x: worldX, y: worldY });
    this.snapIndicator.visible(true);
    this.overlayLayer.batchDraw();
  }

  private hideSnapIndicator(): void {
    if (!this.snapIndicator) return;
    this.snapIndicator.visible(false);
    this.overlayLayer?.batchDraw();
  }

  // ---- BoardState → Konva bridge ----------------------------------------

  private onObjectAdded(obj: BoardObject): void {
    const plugin = this.registry.get(obj.type);
    let node: Konva.Node;
    if (plugin) {
      const ctx: SliceRenderContext = {
        viewport: this.boardState.viewport,
        bus: this.#bus,
        // Live-object lookup for a slice's zoom re-raster (resolveLiveObject):
        // reads the CURRENT object from BoardState so wheel-zoom re-rasters
        // fresh content, not the stale createNode snapshot (which renders a
        // just-created/edited box blank or pre-edit on zoom until it is moved).
        getObject: (id) => this.boardState.getObject(id),
      };
      node = plugin.createNode(obj, ctx);
    } else {
      console.warn(`[o2d-whiteboard] No slice plugin registered for type: ${obj.type}`);
      node = new Konva.Group({ id: obj.id });
    }
    this.wireObjectNode(node, obj.id);
    this.konvaNodes.set(obj.id, node);
    this.contentLayer?.add(node as Konva.Shape);
    this.contentLayer?.batchDraw();
  }

  private onObjectRemoved(id: string): void {
    const node = this.konvaNodes.get(id);
    if (!node) return;
    node.destroy();
    this.konvaNodes.delete(id);
    this.selectionCtrl.remove(id); // stay consistent
    this.contentLayer?.batchDraw();
    // Ebenfalls vom entfernten Hover-Wachstum auf die Eck-Vorschau umgehaengt:
    // Verschwindet das Objekt, an dem die Vorschau haengt, muss sie mit weg —
    // sonst zeigt der Transformer auf einen zerstoerten Knoten.
    if (this.hoverCornerTarget?.id === id) this.clearHoverCornerPreview();
  }

  private onObjectUpdated(obj: BoardObject): void {
    const node = this.konvaNodes.get(obj.id);
    if (!node) return;
    // Single state→canvas geometry routine: every kind of move (mouse-drag
    // commit, arrow-key nudge, undo/redo, programmatic update) flows through
    // here, so position/size/rotation stay uniform across ALL object types.
    // Slice `updateNode` only refreshes inner, type-specific visuals.
    applyBaseGeometry(node, obj);
    const plugin = this.registry.get(obj.type);
    if (plugin) {
      const ctx: SliceRenderContext = {
        viewport: this.boardState.viewport,
        bus: this.#bus,
        // Live-object lookup for a slice's zoom re-raster (resolveLiveObject):
        // reads the CURRENT object from BoardState so wheel-zoom re-rasters
        // fresh content, not the stale createNode snapshot (which renders a
        // just-created/edited box blank or pre-edit on zoom until it is moved).
        getObject: (id) => this.boardState.getObject(id),
      };
      plugin.updateNode(node, obj, ctx);
    } else {
      console.warn(`[o2d-whiteboard] No slice plugin registered for type: ${obj.type}`);
    }
    this.contentLayer?.batchDraw();
    // If the transformer is attached to this node, its bbox needs a refresh.
    this.transformer?.forceUpdate();
    // Anke, 2026-07-22 (Fehlerbericht) — "nach Strg+Z springen die Elemente
    // zurueck, aber ihre Markierungsrahmen bleiben stehen."
    //
    // Die Einzelrahmen mussten hier ebenso nachgezogen werden wie der
    // gemeinsame Rahmen eine Zeile darueber. Diese Routine ist laut ihrem
    // eigenen Kommentar der EINE Weg, ueber den jede Art von Bewegung laeuft —
    // Zieh-Commit, Pfeiltasten, Rueckgaengig, programmatische Aenderung. Wer
    // hier nicht nachzieht, haengt bei allen ausser der einen Bewegung
    // hinterher, die er zufaellig selbst behandelt hat.
    this.#refreshSelectionOutlines(this.selectionCtrl.selectedIds);
    this.selectionLayer?.batchDraw();

    // An open person overlay is a snapshot of the label taken when editing
    // began. Changing size/weight/colour from the options circle mid-edit
    // updates the (hidden) Konva label but would leave the overlay stale, so
    // the user only saw the change after committing. Re-apply it here, AFTER
    // `updateNode` — positionTextEditor reads the label's fresh geometry.
    if (this.textEditor && this.textEditorTargetId === obj.id && obj.type === 'person') {
      this.positionTextEditor(this.textEditor, obj);
    }
    // Anchor follow-up for sticky/text/etc. moves is now handled by
    // AnchorSyncController via bus.domain$ (see core/anchor/anchor-sync.controller.ts).
  }

  /**
   * Re-sync the Konva content layer's z-order with the BoardState object
   * order. Called whenever `object-reordered` fires. The simplest correct
   * approach: iterate objects in their BoardState order (bottom → top) and
   * call `moveToTop()` on each Konva node — the last iteration ends on top,
   * matching the last (= topmost) entry in BoardState.
   *
   */
  private onObjectReordered(_id: string): void {
    for (const obj of this.boardState.allObjects()) {
      this.konvaNodes.get(obj.id)?.moveToTop();
    }
    this.contentLayer?.batchDraw();
  }

  // ---- Marquee selection -----------------------------------------------

  /**
   * Show / update / hide the marquee selection rectangle in the overlay
   * layer. `box` is in world coordinates; we convert to screen here.
   * Passing `null` removes the visual.
   */
  private renderMarquee(box: WorldBox | null): void {
    if (!this.overlayLayer) return;
    if (!box) {
      this.marqueeRect?.destroy();
      this.marqueeRect = undefined;
      this.overlayLayer.batchDraw();
      return;
    }
    // No viewport conversion needed — the overlayLayer lives inside the Konva
    // stage which already applies the world→screen transform via
    // stage.position/scale. Shapes in the overlay layer use the same world
    // coordinates as shapes in the content layer.
    if (!this.marqueeRect) {
      this.marqueeRect = new Konva.Rect({
        fill: 'rgba(42, 95, 215, 0.06)',
        stroke: '#2a5fd7',
        strokeWidth: 1,
        dash: [4, 4],
        listening: false,
      });
      this.overlayLayer.add(this.marqueeRect);
    }
    this.marqueeRect.setAttrs({ x: box.x, y: box.y, width: box.width, height: box.height });
    this.overlayLayer.batchDraw();
  }

  /**
   * Select all board objects whose world bounding box intersects the given
   * world-space rectangle. Called when the user releases after a marquee drag.
   */
  private selectInRect(box: WorldBox): void {
    const objects = this.boardState.allObjects();
    const hits: string[] = [];
    for (const obj of objects) {
      // Die Gruppen-Klammer wird nie gezeichnet — sie darf auch nie getroffen
      // werden. Ihre Bounding-Box liegt aber ueber den Mitgliedern, das
      // Auswahlfenster haette sie also mitgenommen und eine Auswahl ohne Knoten
      // auf der Buehne erzeugt.
      if (isGroup(obj)) continue;
      if (rectsIntersect(obj, box)) hits.push(obj.id);
    }
    // Anke, 2026-07-21 (Fehlerbericht): Ein Auswahlfenster, das nur EIN Mitglied
    // erwischt, muss die ganze Gruppe waehlen — dieselbe Regel wie beim Klicken
    // und beim Klicken-und-Ziehen. Das Fenster war der letzte Weg in die
    // Auswahl, der noch an ihr vorbeilief.
    const ids = [...new Set(hits.flatMap((id) => clickSelectionFor(id, objects)))];
    this.selectionCtrl.select(ids);
  }

  /**
   * Select every board object (Ctrl+A, PRD-C — Bugreport 2026-08-15: without
   * this, Ctrl+A fell through unhandled and the browser's native "select all
   * page text" fired instead). Same shape as `selectInRect` above, minus the
   * rectangle filter: skip the never-drawn group bracket, then run every hit
   * through `clickSelectionFor` so a group with only some members "hit" still
   * selects the whole group, exactly like a marquee or a direct click would.
   */
  private selectAll(): void {
    const objects = this.boardState.allObjects();
    const hits = objects.filter((obj) => !isGroup(obj)).map((obj) => obj.id);
    const ids = [...new Set(hits.flatMap((id) => clickSelectionFor(id, objects)))];
    this.selectionCtrl.select(ids);
  }

  /**
   * Re-rasterises all smiley emoji bitmaps at the physical pixel size that
   * matches the current viewport scale + device pixel ratio. Delegates to the
   * smiley slice via `updateKonvaNode` so the 5%-Re-rasterise-Schwelle and
   * Bitmap-Cache (PRD-F3 §TR6) are honoured.
   */
  private refreshAllSmileyBitmaps(): void {
    let changed = false;
    for (const [id, node] of this.konvaNodes) {
      const obj = this.boardState.getObject(id);
      if (obj?.type === 'smiley') {
        const plugin = this.registry.get(obj.type);
        if (plugin) {
          const ctx: SliceRenderContext = {
        viewport: this.boardState.viewport,
        bus: this.#bus,
        // Live-object lookup for a slice's zoom re-raster (resolveLiveObject):
        // reads the CURRENT object from BoardState so wheel-zoom re-rasters
        // fresh content, not the stale createNode snapshot (which renders a
        // just-created/edited box blank or pre-edit on zoom until it is moved).
        getObject: (id) => this.boardState.getObject(id),
      };
          plugin.updateNode(node, obj, ctx);
          changed = true;
        }
      }
    }
    if (changed) this.contentLayer?.batchDraw();
  }

  private wireObjectNode(node: Konva.Node, id: string): void {
    // Same `readonly` gate as `applyNodeDraggability()` (bugfix, Anke
    // 2026-09-16) — an object added/re-mounted WHILE the board is already
    // readonly must not come up draggable just because the tool is (forced
    // to) `select`.
    node.draggable(this.toolCtrl.tool === 'select' && !this.readonly);
    // Live einrasten waehrend des Ziehens (Anke, 2026-07-16): Konva ruft diese
    // Funktion bei jeder Mausbewegung mit der ANGEPEILTEN absoluten Position
    // (Bildschirmkoordinaten). Wir rechnen sie in Weltkoordinaten, faengen sie
    // aufs Raster und zurueck — der Knoten springt so von Rasterpunkt zu
    // Rasterpunkt. Bei ausgeschaltetem Raster gibt #snapWorldPoint die Position
    // unveraendert zurueck. Bezugspunkt ist der Knoten-Ursprung (= gespeicherte
    // x/y), also faengt die linke obere Ecke.
    node.dragBoundFunc((pos) => {
      if (!this.gridEnabled) return pos;
      const vp = this.boardState.viewport;
      const world = { x: (pos.x - vp.x) / vp.scale, y: (pos.y - vp.y) / vp.scale };
      const snapped = snapPointToGrid(world);
      return { x: snapped.x * vp.scale + vp.x, y: snapped.y * vp.scale + vp.y };
    });
    node.on('dragstart', () => this.onNodeDragStart(id));
    node.on('dragmove',  () => this.onNodeDragMove(id));
    node.on('dragend',   () => this.onNodeDragEnd(id, node));
    node.on('dblclick dbltap', (e) => this.onNodeDoubleClick(id, e));

    // Anke, 2026-07-19: Objekte wuchsen beim Darueberfahren um 2 %. Der Effekt
    // ist ersatzlos entfernt — fuer ALLE Objekttypen, damit keiner optisch aus
    // der Reihe faellt. Die Anfasser und der Optionskreis wachsen weiterhin
    // beim Darueberfahren; dort ist es eine Zusage ueber ein Bedienelement,
    // beim Objekt selbst war es nur Dekoration.

    // Hand cursor over any object in select mode, signalling it is grabbable.
    wireHoverCursor(node, 'pointer', () => this.toolCtrl.tool === 'select');

    // Anke, 2026-09-12 — an I-beam cursor over an ALREADY-selected textbox
    // signals that the next plain click enters edit mode (see
    // `#onClickAlreadySelected`'s own doc comment). Registered AFTER the
    // 'pointer' rule above on purpose: `wireHoverCursor` fires its
    // `mouseenter` listeners in registration order, so when BOTH are
    // enabled (select tool AND this specific textbox already selected)
    // this one runs last and its cursor value is what actually sticks —
    // no change needed in `wireHoverCursor` itself for that to hold.
    //
    // Anke, 2026-09-12 (follow-up): "wenn [...] das kleine Plus-Icon
    // erscheint [...] dann soll bei Hover über diesem Plus der Cursor nicht
    // das Text-Edit-Icon haben, sondern 'pointer'" — the overset "+"
    // indicator (`.textbox-overflow-indicator`, a child of this Group) sets
    // its OWN 'pointer' cursor via `wireHoverFillGroup`, but `evt.target`
    // still bubbles this Group's own hover-cursor check too (see
    // `wireHoverCursor`'s doc comment) — without this guard the two would
    // race on every `mousemove` tick, and this 'text' rule usually wins
    // (registered after 'pointer' above, same reasoning as that comment).
    //
    // Anke, 2026-10-03: the I-beam only over TEXT; over empty space of the
    // selected textbox the 'pointer' rule above keeps the hand. The textbox
    // renderer measures its text line areas with every raster
    // (`TextBoxGroup.__isOverText`, box-local coordinates). A click on empty
    // space still enters edit mode, as before — only the cursor changes.
    wireHoverCursor(node, 'text', (evt) => {
      const obj = this.boardState.getObject(id);
      if (this.toolCtrl.tool !== 'select' || obj?.type !== 'text' || !this.selectionCtrl.has(id)) return false;
      if (evt.target?.findAncestor('.textbox-overflow-indicator', true) != null) return false;
      const local = node.getRelativePointerPosition();
      const isOverText = (node as TextBoxGroup).__isOverText;
      return !local || !isOverText || isOverText(local.x, local.y);
    });

    // Standalone checkbox: single click selects (like every other object
    // type), double-click toggles `checked` (Stefan, 2026-08-28 — a single
    // click used to toggle directly, with `e.cancelBubble = true`, which
    // made it impossible to ever SELECT a standalone checkbox by clicking
    // it). Textbox checklist items are unaffected — that toggle is owned by
    // textbox.renderer (PRD-F6 §T10), a completely separate code path.
    //
    // A rapid double-click must count as ONLY a double-click, not also as a
    // single click (Stefan, 2026-08-28, follow-up): every other object type
    // lets `click`/`dblclick` fire independently and relies on the two
    // intervening `click` events of a double-click being harmless (they just
    // select the object, same as the eventual double-click would anyway) —
    // fine for "select, then open an editor", but here the SECOND action is
    // itself a toggle, so an un-debounced click would flip `checked` an
    // instant before the intended single toggle from `onNodeDoubleClick`,
    // which reads as "it flickered"/double-fired even though the end STATE
    // is correct. So checkbox selection on click is deferred by
    // `Konva.dblClickWindow` (the exact window Konva itself uses to decide
    // whether two clicks become a `dblclick`) and cancelled outright if a
    // second click (i.e. an incoming double-click) arrives inside that
    // window — `onNodeDoubleClick`'s checkbox branch below does the
    // selecting itself in that case, so the end state is identical, just
    // never via two separate, individually-observable actions.
    let checkboxClickTimer: ReturnType<typeof setTimeout> | null = null;
    node.on('click tap', (e: Konva.KonvaEventObject<MouseEvent>) => {
      // Bugfix, Anke 2026-09-16: same readonly gap as `InteractionController
      // .onStageClick` (see that fix's own comment) — a checkbox's own click
      // handler bypasses the stage entirely (`e.cancelBubble = true` below),
      // so it needs its OWN `readonly` check; the stage-level fix alone
      // would not have covered checkboxes.
      if (this.readonly) return;
      const obj = this.boardState.getObject(id);
      if (obj?.type !== 'checkbox') return; // every other type: bubble to the stage-wide selection handler, unchanged.
      e.cancelBubble = true;

      if (checkboxClickTimer !== null) {
        // Second click within the double-click window — this IS becoming a
        // double-click; don't select here, `onNodeDoubleClick` handles it.
        clearTimeout(checkboxClickTimer);
        checkboxClickTimer = null;
        return;
      }

      const withModifier = e.evt.shiftKey || e.evt.ctrlKey || e.evt.metaKey;
      checkboxClickTimer = setTimeout(() => {
        checkboxClickTimer = null;
        // Zweite Prüfung beim FEUERN, nicht nur beim Klick: zwischen beidem
        // liegen `Konva.dblClickWindow` (400 ms). Wird das Board in diesem
        // Fenster gesperrt, räumt die Sperre die Auswahl ab — der noch
        // laufende Timer baute sie sonst wieder auf (Review 0023).
        if (!this.boardState.getObject(id) || this.readonly) return; // gelöscht/rückgängig oder gesperrt, während der Timer lief.
        if (withModifier) {
          this.#toggleSelection(id);
        } else if (!this.selectionCtrl.selectedIds.includes(id)) {
          this.selectionCtrl.select(this.#clickSelection(id));
        }
      }, (Konva as unknown as { dblClickWindow: number }).dblClickWindow);
    });
  }

  private onNodeDragMove(id: string): void {
    this.fireLiveTransform(id);
    // Anchored-line live sync is owned by AnchorSyncController, which
    // listens to the drag/move UiAction emitted below.
    const node = this.konvaNodes.get(id);
    if (node && this.#bus) {
      this.#bus.uiActions$.next({
        type: 'drag/move',
        worldX: node.x(),
        worldY: node.y(),
        targetId: objectId(id),
      });
    }

    // Multi-select: co-move all other selected nodes by the same delta.
    if (this.selectionCtrl.selectedIds.length > 1) {
      const node = this.konvaNodes.get(id);
      const startPos = this.dragStartPositions.get(id);
      if (node && startPos) {
        const dx = node.x() - startPos.x;
        const dy = node.y() - startPos.y;
        for (const selId of this.selectionCtrl.selectedIds) {
          if (selId === id) continue;
          const selNode = this.konvaNodes.get(selId);
          const selStart = this.dragStartPositions.get(selId);
          if (selNode && selStart) {
            selNode.x(selStart.x + dx);
            selNode.y(selStart.y + dy);
          }
        }
        this.contentLayer?.batchDraw();
      }
    }
    // Die Einzelrahmen sitzen auf den gezogenen Objekten — sie muessen mit.
    this.#refreshSelectionOutlines(this.selectionCtrl.selectedIds);
    this.selectionLayer?.batchDraw();
  }

  /**
   * Tell internal subscribers that the currently-selected object
   * moved/resized *live* — the Konva node already reflects the new
   * transform, but state hasn't committed yet.
   * PRD-H §TR4: emits via bus.liveTransform$ instead of DOM CustomEvent.
   */
  private fireLiveTransform(id: string): void {
    if (!this.#bus) return;
    this.#bus.liveTransform$.next({ id: objectId(id) });
  }

  // ---- Selection → Transformer -----------------------------------------


  // ---- Corner-on-hover resize affordance --------------------------------

  /**
   * Stage-level pointermove handler. Finds the closest corner of any
   * non-selected hoverable object within the trigger radius and attaches
   * the Transformer to that object showing only the one nearest anchor
   * (with its border hidden). This way the user can grab the anchor
   * directly — Konva's native resize logic takes over on mousedown. A
   * successful resize promotes the hovered object to the real selection.
   */
  private updateHoverCorner(): void {
    if (!this.stage || !this.transformer) return;
    if (this.isTransforming) return;
    // Bugfix, Anke 2026-09-20 (Review 0023, Lauf 03): Die beiden Bedingungen
    // darunter — Werkzeug `select`, keine Auswahl — sind genau die zwei
    // Zustände, die `#applyReadonly()` SELBST herstellt (`lockToSelect()` +
    // `selectionCtrl.clear()`). Ohne eigene Prüfung machte die Sperre dieses
    // Feature also nicht unerreichbar, sondern erst recht erreichbar: Ecke
    // überfahren, Griff greifen, Objekt skalieren. Der Frühausstieg in
    // `InteractionController.onStageDown` hilft dagegen nicht, weil Konvas
    // Transformer-Anker ihren eigenen `mousedown`-Listener mitbringen.
    if (this.readonly) { this.clearHoverCornerPreview(); return; }
    if (this.toolCtrl.tool !== 'select') { this.clearHoverCornerPreview(); return; }
    if (this.selectionCtrl.selectedIds.length > 0) { this.clearHoverCornerPreview(); return; }

    const pointer = this.stage.getPointerPosition();
    if (!pointer) { this.clearHoverCornerPreview(); return; }
    const worldX = (pointer.x - this.stage.x()) / (this.stage.scaleX() || 1);
    const worldY = (pointer.y - this.stage.y()) / (this.stage.scaleY() || 1);
    const worldRadius = HOVER_CORNER_SCREEN_PX / (this.boardState.viewport.scale || 1);

    const hit = this.findNearestCorner(worldX, worldY, worldRadius);
    if (!hit) { this.clearHoverCornerPreview(); return; }

    const existing = this.hoverCornerTarget;
    if (existing && existing.id === hit.id && existing.corner === hit.corner) return;

    const node = this.konvaNodes.get(hit.id);
    const obj = this.boardState.getObject(hit.id);
    if (!node || !obj) { this.clearHoverCornerPreview(); return; }

    this.transformer.borderEnabled(false);
    this.transformer.enabledAnchors([hit.corner]);
    this.transformer.rotateEnabled(false); // hover preview is a resize affordance only
    this.transformer.keepRatio(isRatioLocked(obj.type));
    this.transformer.nodes([node as Konva.Node]);
    this.selectionLayer?.batchDraw();
    this.hoverCornerTarget = { id: hit.id, corner: hit.corner };
  }

  private hideHoverCorner(): void { this.clearHoverCornerPreview(); }

  private clearHoverCornerPreview(): void {
    if (!this.hoverCornerTarget) return;
    this.hoverCornerTarget = undefined;
    // Only tear the transformer down if nothing is really selected;
    // otherwise refreshTransformer already has it correctly attached.
    if (this.transformer && this.selectionCtrl.selectedIds.length === 0) {
      this.transformer.nodes([]);
      this.transformer.borderEnabled(true);
      this.transformer.enabledAnchors(CORNER_ANCHORS as string[]);
      this.transformer.rotateEnabled(false);
      this.selectionLayer?.batchDraw();
    }
  }

  private findNearestCorner(
    worldX: number,
    worldY: number,
    maxDist: number,
  ): { id: string; corner: TransformerAnchor; x: number; y: number } | null {
    let best: { id: string; corner: TransformerAnchor; x: number; y: number; dist: number } | null = null;
    for (const obj of this.boardState.allObjects()) {
      if (isLine(obj) || obj.type === 'checkbox') continue;
      if (this.selectionCtrl.has(obj.id)) continue; // already selected — real transformer wins
      const corners: Array<{ corner: TransformerAnchor; x: number; y: number }> = [
        { corner: 'top-left',     x: obj.x,              y: obj.y },
        { corner: 'top-right',    x: obj.x + obj.width,  y: obj.y },
        { corner: 'bottom-left',  x: obj.x,              y: obj.y + obj.height },
        { corner: 'bottom-right', x: obj.x + obj.width,  y: obj.y + obj.height },
      ];
      for (const c of corners) {
        const dx = c.x - worldX;
        const dy = c.y - worldY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > maxDist) continue;
        if (!best || dist < best.dist) {
          best = { id: obj.id, corner: c.corner, x: c.x, y: c.y, dist };
        }
      }
    }
    return best;
  }

  /**
   * The hover fill for selection handles, read live from the
   * `--o2d-handle-hover-fill` CSS variable so it can be re-themed without
   * touching canvas code. Faellt auf HANDLE_HOVER_FILL zurueck, wenn die
   * Variable fehlt (z. B. in einer nackten Testumgebung ohne Stylesheet).
   * Dieser Rueckfall und der Wert in `whiteboard.css` muessen denselben Ton
   * nennen — `tests/unit/handle-hover-fill-sync.test.ts` haelt sie zusammen.
   */
  private handleHoverFill(): string {
    const v = getComputedStyle(this).getPropertyValue('--o2d-handle-hover-fill').trim();
    return v || HANDLE_HOVER_FILL;
  }

  /** Fill the transformer's square resize anchors with the hover colour while
   * hovered. Idempotent — safe to call whenever anchors may have changed. */
  private wireTransformerAnchorHover(): void {
    if (!this.transformer) return;
    for (const anchor of this.transformer.find<Konva.Shape>('._anchor')) {
      wireHoverFill(anchor, () => this.handleHoverFill(), HANDLE_FILL);
    }
  }

  private refreshTransformer(ids: ReadonlyArray<string>): void {
    if (!this.transformer || !this.selectionLayer) return;

    // Review 0023, Lauf 03: Die Host-API `selectIds()` ist bewusst NICHT
    // gesperrt — ein Host darf auf einem gesperrten Board etwas hervorheben.
    // Griffe dranzuhängen ist aber etwas anderes als Hervorheben: sie sind ein
    // interaktiver Bearbeitungsweg und ließen sich ziehen. Deshalb bleibt der
    // Transformer hier leer, statt die Auswahl selbst zu verbieten.
    if (this.readonly) {
      // Der Host darf auf einem gesperrten Board hervorheben — aber nichts
      // greifen. Deshalb bleibt der Transformer AM Objekt (der Rahmen ist bei
      // einer Einzelauswahl der einzige sichtbare Anhaltspunkt:
      // `#refreshSelectionOutlines` zeichnet bei genau einer Einheit bewusst
      // nichts, weil sonst der Rahmen doppelt spräche), aber ohne jeden Anker.
      // Gemessen: `resizeEnabled(false)` + `rotateEnabled(false)` ergibt 0
      // sichtbare Anker bei erhaltenem Rahmen. Ziehen ist ohnehin aus
      // (`applyNodeDraggability()`), und ohne Anker kann `onTransformStart`
      // gar nicht erst feuern.
      this.transformer.nodes(this.#konvaNodesFor(ids));
      this.transformer.resizeEnabled(false);
      this.transformer.rotateEnabled(false);
      this.transformer.borderEnabled(ids.length > 0);
      this.hoverCornerTarget = undefined;
      // Regression aus Lauf 03, gefunden in Lauf 04: Der frühe `return` hat
      // die letzten beiden Zeilen dieser Routine übersprungen. Zwei Dinge
      // gingen dabei kaputt. Erstens räumte das Sperren die Einzelrahmen
      // einer Mehrfachauswahl nicht mehr ab — `#applyReadonly()` ruft
      // `selectionCtrl.clear()`, das landet hier mit `ids = []`, und ohne
      // diesen Aufruf blieben die Rahmen stehen: genau das „stayed visible",
      // das der Bugfix vom 2026-09-16 behoben hatte. Zweitens zeichnete ein
      // `selectIds()` des Hosts gar nichts mehr, obwohl Kommentar und
      // CHANGELOG ausdrücklich versprechen, dass ein Host auf einem
      // gesperrten Board etwas hervorheben darf. Beides hängt an denselben
      // zwei Zeilen — die Griffe bleiben trotzdem weg, weil `nodes([])`
      // oben steht.
      this.#refreshSelectionOutlines(ids);
      this.selectionLayer.batchDraw();
      return;
    }

    // Lauf 04: `resizeEnabled` ausdrücklich zurücksetzen, weil der
    // readonly-Zweig unten es auf `false` schaltet und es sonst nirgends im
    // Paket wieder auf `true` gesetzt wird.
    //
    // Ehrlichkeitshalber: Gemessen kommen die Greifpunkte nach dem Entsperren
    // auch OHNE diese Zeile zurück — Konva stellt das beim nächsten
    // `enabledAnchors()` selbst her, und die Rot-Probe bleibt deshalb grün.
    // Die Zeile steht trotzdem hier, weil sich die Absicht sonst auf ein
    // Implementierungsdetail von Konva verlässt, das niemand zugesichert hat.
    this.transformer.resizeEnabled(true);

    // Any real selection change supersedes an in-flight hover preview.
    if (ids.length > 0) this.hoverCornerTarget = undefined;
    // Restore the normal 4-corner + border look for real selections;
    // hover previews override these attributes when they kick in.
    this.transformer.borderEnabled(true);

    if (ids.length === 0) {
      this.transformer.nodes([]);
      this.transformer.rotateEnabled(false);
    } else if (ids.length === 1) {
      const id = ids[0]!;
      const obj = this.boardState.getObject(id);
      const node = this.konvaNodes.get(id);

      // Bitmap-backed types (smiley, image) resize proportionally.
      const ratioLocked = obj ? isRatioLocked(obj.type) : false;
      this.transformer.keepRatio(ratioLocked);

      // Single selection: corner handles, PLUS the edge-midpoint handles for
      // width-only / height-only resizing (Anke, 2026-07-18). Types under the
      // 1:1 lock keep corners alone — an axis-only handle there would be a
      // control that visibly does nothing, because keepRatio overrules it. That
      // is keyed off `isRatioLocked`, NOT off `type === 'smiley'`, so the two
      // stay in step: whatever becomes ratio-locked (images did) automatically
      // loses the edge handles too. May have been reduced to corners by a
      // previous multi-select, so this always reassigns.
      this.transformer.enabledAnchors(
        (ratioLocked ? CORNER_ANCHORS : CORNER_AND_EDGE_ANCHORS) as string[],
      );

      if (obj && isLine(obj)) {
        // Lines use custom endpoint handles owned by LineEndpointHandlesController,
        // which subscribes to bus.selection$ and renders into selectionLayer.
        this.transformer.nodes([]);
        this.transformer.rotateEnabled(false);
      } else {
        this.transformer.nodes(node ? [node as Konva.Node] : []);
        // Rotation handle (dot with a circular arrow, above the top edge — see
        // `core/konva/rotate-anchor.ts`): drag = rotate the element around its
        // centre. The existing transform pipeline commits the new `rotation`
        // via ResizeObjectCommand, so undo/redo just work.
        this.transformer.rotateEnabled(true);
      }
    } else {
      // Multi-select: all nodes (including lines) go into the Transformer so the
      // bounding box correctly covers the full selection extent. The four corner
      // handles do proportional group scaling for every selection — notes/boxes
      // fold scale into width/height (M1), lines scale their free endpoints and
      // stroke width (M2, ScaleLineCommand); anchored endpoints stay target-
      // driven. Rotation is off for groups (rotating a heterogeneous group needs
      // per-node maths we don't do yet). Corners ONLY — the edge-midpoint
      // handles a single selection gets are deliberately withheld here (Anke,
      // 2026-07-18): squashing a group on one axis distorts every object inside
      // it, and a group is reduced to corners whenever the selection grows.
      // Anke, 2026-07-22 — Kanten-Anfasser auch hier, aber nur wenn nichts in
      // der Auswahl davon Schaden nimmt (Bild/Smiley wegen der 1:1-Sperre,
      // Linien wegen ihrer Strichstärken-Rechnung). Siehe
      // `selectionAllowsAxisResize`.
      //
      // `keepRatio` bleibt bewusst auf `true`: Konva fragt es NUR in den
      // Ecken-Zweigen ab, die Mittelanker verschieben ohnehin nur eine
      // Koordinate. Die proportionale Gruppen-Skalierung auf den Ecken bleibt
      // damit unverändert — es kommt nur etwas hinzu.
      const types = ids
        .map((id) => this.boardState.getObject(id)?.type)
        .filter((t): t is BoardObject['type'] => t !== undefined);
      this.transformer.enabledAnchors(
        (selectionAllowsAxisResize(types) ? CORNER_AND_EDGE_ANCHORS : CORNER_ANCHORS) as string[],
      );
      this.transformer.keepRatio(true);
      this.transformer.rotateEnabled(false);
      const nodes: Konva.Node[] = [];
      for (const id of ids) {
        const node = this.konvaNodes.get(id);
        if (node) nodes.push(node as Konva.Node);
      }
      this.transformer.nodes(nodes);
    }
    this.#refreshSelectionOutlines(ids);
    this.selectionLayer.batchDraw();
  }

  /**
   * Einen Rahmen je beteiligter EINHEIT zeichnen — aber nur, wenn es mehr als
   * eine gibt.
   *
   * "Einheit" heisst: das aeusserste, wonach ein Klick greifen wuerde. Zwei
   * einzelne Objekte sind zwei Einheiten und bekommen zwei Rahmen. Eine Gruppe
   * ist EINE Einheit — dann bleibt es beim gemeinsamen Rahmen des Transformers,
   * und hier wird nichts gezeichnet. Eine Gruppe plus ein freies Objekt sind
   * wieder zwei Einheiten: ein Rahmen um die Gruppe, einer um das Objekt.
   *
   * Die Box einer Gruppe wird aus ihren MITGLIEDERN gerechnet, nicht aus dem
   * gespeicherten Feld: Das steht noch auf dem Stand vom Gruppieren und waere
   * nach jedem Verschieben veraltet.
   */
  /** Die vorhandenen Konva-Knoten zu einer ID-Liste, fehlende ausgelassen. */
  #konvaNodesFor(ids: ReadonlyArray<string>): Konva.Node[] {
    const nodes: Konva.Node[] = [];
    for (const id of ids) {
      const node = this.konvaNodes.get(id);
      if (node) nodes.push(node);
    }
    return nodes;
  }

  #refreshSelectionOutlines(ids: ReadonlyArray<string>): void {
    const holder = this.#selectionOutlines;
    if (!holder) return;
    holder.destroyChildren();
    if (ids.length === 0) return;

    const objects = this.boardState.allObjects();
    const byId = new Map(objects.map((o) => [o.id, o]));
    const units = groupingCandidates(ids, byId);
    // Genau eine Einheit → der gemeinsame Rahmen sagt bereits alles.
    if (units.length < 2) return;

    const scale = this.boardState.viewport.scale || 1;
    for (const unitId of units) {
      const unit = byId.get(unitId);
      if (!unit) continue;
      const members = isGroup(unit) ? groupMembers(unitId, objects) : [unit];
      const drawable = members.filter((m) => !isGroup(m));
      if (drawable.length === 0) continue;
      // WAEHREND eines Zuges steht im Board-Zustand noch die alte Position — der
      // wird erst beim Loslassen fortgeschrieben. Also von der Buehne lesen, wo
      // vorhanden: sonst blieben die Rahmen beim Ziehen stehen.
      const box = boundingBox(drawable.map((m) => {
        const node = this.konvaNodes.get(m.id);
        return node ? { ...m, x: node.x(), y: node.y() } : m;
      }));
      holder.add(new Konva.Rect({
        ...box,
        stroke: HANDLE_STROKE,
        // Hairline bei jedem Zoom, wie der Transformer-Rahmen.
        strokeWidth: 1 / scale,
        strokeScaleEnabled: false,
        listening: false,
      }));
    }
  }

  // ---- Drag → MoveObjectsCommand ----------------------------------------

  private onNodeDragStart(id: string): void {
    if (!this.selectionCtrl.has(id)) {
      // "Click-and-drag in one gesture": bei leerer Auswahl beginnt der Zug,
      // BEVOR ein Klick die Auswahl setzen konnte — dieser Zweig holt das nach.
      //
      // Anke, 2026-07-21 (Fehlerbericht): Er waehlte frueher NUR das angefasste
      // Objekt und ging damit an der Gruppen-Aufloesung vorbei. Ergebnis: Nach
      // dem Deselektieren liess sich ein Gruppenmitglied einzeln herausziehen.
      // Hier muss dasselbe passieren wie bei einem Klick — bei einem Mitglied
      // also die ganze Gruppe.
      const ids = this.#clickSelection(id);
      this.selectionCtrl.select(ids);
      // Startpositionen fuer ALLE, nicht nur fuer das angefasste: Das Mitziehen
      // rechnet jede Mitbewegung gegen diese Grundlinie: ohne sie bliebe der Rest
      // stehen.
      for (const memberId of ids) {
        const memberNode = this.konvaNodes.get(memberId);
        if (memberNode) {
          this.dragStartPositions.set(memberId, { x: memberNode.x(), y: memberNode.y() });
        }
      }
      return;
    }
    // Multi-select: record start positions for ALL selected objects so every
    // node can be co-moved during the drag — but ONLY on the FIRST dragstart of
    // the gesture. In a multi-selection Konva runs an independent drag per
    // selected node, so a second node fires its OWN dragstart a few pixels into
    // the gesture (once the drag-distance threshold is crossed). Re-recording
    // baselines then would capture positions ALREADY shifted by that threshold;
    // the committed delta would be measured from the shifted baseline and fall
    // short of the grabbed node's true delta, while an anchored line's docked
    // end is re-derived from the target's true position — so the free end would
    // drift by that constant offset every gesture (Anke's bug). Record once.
    if (this.dragStartPositions.size > 0) return;
    for (const selId of this.selectionCtrl.selectedIds) {
      const selNode = this.konvaNodes.get(selId);
      if (selNode) this.dragStartPositions.set(selId, { x: selNode.x(), y: selNode.y() });
    }
  }

  private onNodeDragEnd(id: string, node: Konva.Node): void {
    if (this.selectionCtrl.selectedIds.length > 1) {
      // A group move is defined by the delta of the GRABBED node. We must NOT
      // read each co-moved node's own final Konva position: in a multi-selection
      // Konva runs an INDEPENDENT drag per selected node, and a non-grabbed
      // node's drag starts a few pixels late (Konva.dragDistance threshold), so
      // its final position lags the grabbed node by a constant, distance-
      // independent offset. Reading that leaked the offset into a docked line's
      // free endpoint, which drifted opposite the drag every gesture. So derive
      // ONE delta from the grabbed node and translate every co-moved node by it.
      const grabbedFrom = this.dragStartPositions.get(id);
      const dx = grabbedFrom ? node.x() - grabbedFrom.x : 0;
      const dy = grabbedFrom ? node.y() - grabbedFrom.y : 0;
      const moves: Move[] = [];
      if (dx !== 0 || dy !== 0) {
        for (const selId of this.selectionCtrl.selectedIds) {
          const from = this.dragStartPositions.get(selId);
          if (!from) continue;
          moves.push({ id: selId, from, to: { x: from.x + dx, y: from.y + dy } });
        }
      }
      this.dragStartPositions.clear();
      if (moves.length > 0) this._historyAdapter.push(new MoveObjectsCommand(moves), this.boardState);
      return;
    }

    const from = this.dragStartPositions.get(id);
    this.dragStartPositions.delete(id);
    if (!from) return;
    const to = { x: node.x(), y: node.y() };
    if (from.x === to.x && from.y === to.y) return;
    this._historyAdapter.push(new MoveObjectsCommand([{ id, from, to }]), this.boardState);
  }

  // ---- Transformer-level drag (multi-select group move) -----------------

  /**
   * When the Transformer's bounding box is dragged (not an anchor), Konva
   * moves all attached nodes but does NOT fire drag events on the individual
   * nodes. We capture start positions here so `onTransformerDragEnd` can
   * compute the deltas and push a single MoveObjectsCommand.
   */
  private onTransformerDragStart(): void {
    if (!this.transformer) return;
    for (const node of this.transformer.nodes()) {
      this.dragStartPositions.set(node.id(), { x: node.x(), y: node.y() });
    }
  }

  private onTransformerDragEnd(): void {
    if (!this.transformer) return;
    const moves: Move[] = [];
    for (const node of this.transformer.nodes()) {
      const id = node.id();
      const from = this.dragStartPositions.get(id);
      if (!from) continue;
      const to = { x: node.x(), y: node.y() };
      if (from.x !== to.x || from.y !== to.y) moves.push({ id, from, to });
    }
    this.dragStartPositions.clear();
    if (moves.length > 0) this._historyAdapter.push(new MoveObjectsCommand(moves), this.boardState);
  }

  // ---- Transform → ResizeObjectCommand ----------------------------------

  private onTransformStart(): void {
    if (!this.transformer) return;
    this.isTransforming = true;
    // If the transformer was a hover-preview (no real selection), remember
    // which id to promote once the drag ends. Don't call selection.select
    // now — it would refresh the transformer mid-drag and kill the gesture.
    if (this.hoverCornerTarget && this.selectionCtrl.selectedIds.length === 0) {
      this.promoteOnTransformEnd = this.hoverCornerTarget.id;
    }
    this.transformStartSnapshots.clear();
    for (const node of this.transformer.nodes()) {
      const id = node.id();
      const obj = this.boardState.getObject(id);
      if (!obj) continue;
      this.transformStartSnapshots.set(id, {
        x: obj.x, y: obj.y,
        width: obj.width, height: obj.height,
        rotation: obj.rotation,
      });
    }
  }

  /**
   * Konva's Transformer resizes by changing scaleX/scaleY. Left alone,
   * that stretches children like the sticky's text node. We continuously
   * normalise the scale back to 1 and fold the effective scale into
   * width/height; the object-renderer's updateKonvaNode re-runs auto-fit
   * for the sticky text, so typography stays crisp at any size.
   *
   * For multi-select, Konva may also update node positions (the group
   * bounding box scales from its center), so we read the live node x/y
   * rather than the committed state position.
   *
   * Recipe source: Konva docs — "Resize Text Demo".
   */
  private onTransformLive(): void {
    if (!this.transformer) return;
    // Multi-select group scaling is resolved once on transform-END (fold the
    // scale into width/height for every child in a single shot). Normalising
    // per frame here would reset each child's scale mid-gesture and confuse the
    // Transformer's group-scale maths, accumulating rounding drift — so let
    // Konva scale the group live and defer resolution to onTransformEnd.
    if (this.transformer.nodes().length > 1) return;
    let changed = false;
    for (const node of this.transformer.nodes()) {
      const sx = node.scaleX();
      const sy = node.scaleY();
      if (Math.abs(sx - 1) < 0.0001 && Math.abs(sy - 1) < 0.0001) continue;

      const newWidth  = Math.max(1, node.width()  * sx);
      const newHeight = Math.max(1, node.height() * sy);
      node.scaleX(1);
      node.scaleY(1);
      node.width(newWidth);
      node.height(newHeight);

      const id = node.id();
      const obj = this.boardState.getObject(id);
      if (!obj) continue;
      // Use live node position so multi-select positioning is preserved.
      const transient = { ...obj, x: node.x(), y: node.y(), width: newWidth, height: newHeight } as BoardObject;
      const plugin = this.registry.get(transient.type);
      if (plugin) {
        const ctx: SliceRenderContext = {
        viewport: this.boardState.viewport,
        bus: this.#bus,
        // Live-object lookup for a slice's zoom re-raster (resolveLiveObject):
        // reads the CURRENT object from BoardState so wheel-zoom re-rasters
        // fresh content, not the stale createNode snapshot (which renders a
        // just-created/edited box blank or pre-edit on zoom until it is moved).
        getObject: (id) => this.boardState.getObject(id),
      };
        plugin.updateNode(node, transient, ctx);
      } else {
        console.warn(`[o2d-whiteboard] No slice plugin registered for type: ${transient.type}`);
      }
      this.fireLiveTransform(id);
      // Anchored-line live sync for a RESIZE goes through AnchorSyncController's
      // dedicated `applyLiveResize` — NOT the generic `drag/move` UiAction (that
      // path derives a translation delta from worldX/Y vs. the target's STORED
      // x/y, so a resize that keeps a corner fixed produced a zero delta and
      // docked lines stayed frozen at the pre-resize box until release). This
      // hands over the live width/height directly, matching a resize's actual
      // semantics. See anchor-sync.controller.ts's `applyLiveResize` doc.
      this.anchorSync.applyLiveResize(objectId(id), node.x(), node.y(), newWidth, newHeight);
      changed = true;
    }
    if (changed) this.contentLayer?.batchDraw();
  }

  /**
   * Fold a node's residual Konva scale into its width/height, reset scaleX/Y to
   * 1, and repaint via its slice plugin (so e.g. sticky text auto-fits to the
   * new size). No-op when the scale is already ~1. Returns true if it changed.
   */
  #foldNodeScale(node: Konva.Node): boolean {
    const sx = node.scaleX();
    const sy = node.scaleY();
    if (Math.abs(sx - 1) < 0.0001 && Math.abs(sy - 1) < 0.0001) return false;

    const w = Math.max(1, node.width() * sx);
    const h = Math.max(1, node.height() * sy);
    node.scaleX(1);
    node.scaleY(1);
    node.width(w);
    node.height(h);

    const obj = this.boardState.getObject(node.id());
    const plugin = obj ? this.registry.get(obj.type) : undefined;
    if (obj && plugin) {
      const ctx: SliceRenderContext = {
        viewport: this.boardState.viewport,
        bus: this.#bus,
        getObject: (id) => this.boardState.getObject(id),
      };
      plugin.updateNode(node, { ...obj, x: node.x(), y: node.y(), width: w, height: h } as BoardObject, ctx);
    }
    return true;
  }

  /**
   * Resolve a line node's residual group scale (M2). Konva scaled the whole
   * multi-selection group; for a line that means its Konva group carries a
   * scaleX/scaleY and a shifted x/y. We map each endpoint through that transform
   * (world point = node.pos + node.scale * local), scale the stroke width by the
   * (uniform, keepRatio) factor, and reset the node scale to 1 so the committed
   * ScaleLineCommand redraws crisply. Anchored endpoints are left to the command
   * (it keeps them target-driven). Returns the command, or null for a no-op.
   */
  #foldLineScale(node: Konva.Node, line: LineObject): Command<BoardState> | null {
    const sx = node.scaleX();
    const sy = node.scaleY();
    if (Math.abs(sx - 1) < 0.0001 && Math.abs(sy - 1) < 0.0001) {
      node.scaleX(1);
      node.scaleY(1);
      return null;
    }
    const k = (Math.abs(sx) + Math.abs(sy)) / 2;
    // Map a world point through the group node's current transform (rotation 0).
    const tx = (px: number): number => node.x() + sx * (px - line.x);
    const ty = (py: number): number => node.y() + sy * (py - line.y);

    const hasControls = !!line.curveControls?.length;
    const from: LineScaleSnapshot = {
      x1: line.x1, y1: line.y1, x2: line.x2, y2: line.y2,
      strokeWidth: line.strokeWidth,
      ...(hasControls ? { curveControls: line.curveControls!.map((c) => ({ x: c.x, y: c.y })) } : {}),
    };
    // Endpunkte aufs Raster faengen, wenn es an ist (Anke, 2026-07-16). Die
    // Strichstaerke bleibt ungefangen — sie ist keine Position. Die
    // Kurven-Griffe ebenfalls, sie haengen relativ an der Linie.
    const snapEnd = this.gridEnabled
      ? (v: number): number => snapValueToGrid(v)
      : (v: number): number => v;
    const to: LineScaleSnapshot = {
      x1: snapEnd(tx(line.x1)), y1: snapEnd(ty(line.y1)),
      x2: snapEnd(tx(line.x2)), y2: snapEnd(ty(line.y2)),
      strokeWidth: line.strokeWidth * k,
      ...(hasControls ? { curveControls: line.curveControls!.map((c) => ({ x: tx(c.x), y: ty(c.y) })) } : {}),
    };

    node.scaleX(1);
    node.scaleY(1);
    return new ScaleLineCommand(node.id(), from, to);
  }

  private onTransformEnd(): void {
    if (!this.transformer) return;
    this.isTransforming = false;
    const snapshots = this.transformStartSnapshots;
    this.transformStartSnapshots = new Map();

    const commands: Command<BoardState>[] = [];
    for (const node of this.transformer.nodes()) {
      const id = node.id();
      const obj = this.boardState.getObject(id);
      if (!obj) continue;

      // Lines carry their geometry in world-space endpoints (x1..y2), not in
      // width/height, so folding scale into the box would draw nothing. Resolve
      // the group scale into scaled endpoints + stroke width instead (M2).
      if (isLine(obj)) {
        const cmd = this.#foldLineScale(node, obj as LineObject);
        if (cmd) commands.push(cmd);
        continue;
      }

      const start = snapshots.get(id);
      if (!start) continue;

      // Fold any residual Konva scale into width/height. For single-select
      // onTransformLive already normalised (scale ~1, so this is a no-op); for
      // multi-select we deferred normalisation, so this resolves the group scale
      // for every child in one shot — no per-frame rounding drift.
      this.#foldNodeScale(node);

      // Skalieren faengt aufs Raster (Anke, 2026-07-16): Position UND Groesse
      // werden auf Rastervielfache gebracht, damit alle vier Kanten auf
      // Rasterlinien liegen. Beim Ziehen war das Einrasten live; beim Skalieren
      // greift es beim Loslassen (die Groesse springt einmalig aufs Raster).
      // Bei ausgeschaltetem Raster bleiben die Rohwerte.
      const to: ResizeSnapshot = this.gridEnabled
        ? {
            x: snapValueToGrid(node.x()), y: snapValueToGrid(node.y()),
            width: snapSizeToGrid(node.width()), height: snapSizeToGrid(node.height()),
            rotation: node.rotation(),
          }
        : {
            x: node.x(), y: node.y(),
            width: node.width(), height: node.height(),
            rotation: node.rotation(),
          };
      if (!sameSnapshot(start, to)) {
        commands.push(new ResizeObjectCommand(id, start, to));
      }
    }
    // Commit as ONE undoable step: a single resize stays a ResizeObjectCommand;
    // a group scale becomes a CompositeCommand so one undo reverts the whole set.
    if (commands.length === 1) {
      this._historyAdapter.push(commands[0]!, this.boardState);
    } else if (commands.length > 1) {
      this._historyAdapter.push(
        new CompositeCommand(commands, `${commands.length} Objekte skalieren`),
        this.boardState,
      );
    }
    this.contentLayer?.batchDraw();

    // A hover-preview resize must NOT leave the object selected. Tear
    // the transformer down so the board returns to the neutral state.
    if (this.promoteOnTransformEnd) {
      this.promoteOnTransformEnd = undefined;
      this.hoverCornerTarget = undefined;
      this.transformer.nodes([]);
      this.transformer.borderEnabled(true);
      this.transformer.enabledAnchors(CORNER_ANCHORS as string[]);
      this.selectionLayer?.batchDraw();
    }
  }

  /**
   * @deprecated PRD-F4 §TR4: Bus-dispatch wrapper. ImagePlacementController
   * subscribes to `place/image` and owns the AddObjectCommand + selection
   * emission. Placement is fully synchronous (Variante C / H6 fix); the
   * renderer corrects the bbox to the natural aspect ratio out-of-history.
   */
  placeImageFromDataUrl(dataUrl: string): void {
    if (!this.#bus || !this.stage) return;
    const stage = this.stage;
    const img = new window.Image();
    const place = (size?: { width: number; height: number }): void => {
      if (!this.#bus) return;
      const vp = this.boardState.viewport;
      const worldX = (stage.width()  / 2 - vp.x) / vp.scale;
      const worldY = (stage.height() / 2 - vp.y) / vp.scale;
      this.#bus.uiActions$.next({ type: 'place/image', worldX, worldY, dataUrl, ...(size ? { size } : {}) });
    };
    img.onload = () => place(fitImageWorldSize(
      img.naturalWidth, img.naturalHeight, stage.width(), stage.height(), this.boardState.viewport.scale,
    ));
    img.onerror = () => place();
    img.src = dataUrl;
  }

  // ---- Text editing via contentEditable overlay ------------------------

  private onNodeDoubleClick(id: string, e?: Konva.KonvaEventObject<MouseEvent>): void {
    // Bugfix, Anke 2026-09-16: every branch below either opens an edit
    // surface or mutates board data directly. The `sticky`/`text` branches
    // are ALSO independently guarded downstream (`StickyEditorController`/
    // `TextboxEditorController.setReadonly`), but the curved-line
    // control-point-add, the `person` contentEditable overlay
    // (`openTextEditor` — a hand-rolled overlay with no readonly check of
    // its own) and the `checkbox` toggle (a DIRECT `UpdatePropertyCommand`
    // push, bypassing any editor controller entirely) had NO readonly
    // protection anywhere before this — a locked board's checkbox could
    // still be toggled, a person's text still edited, by double-click alone.
    if (this.readonly) return;
    const obj = this.boardState.getObject(id);
    if (!obj) return;
    // Curved line: double-click the stroke adds a control point at the click.
    if (isLine(obj) && obj.pathStrategy === 'curved') {
      const world = this.contentLayer?.getRelativePointerPosition();
      if (world) {
        this.#bus?.uiActions$.next({ type: 'line/add-curve-point', id: objectId(id), at: { x: world.x, y: world.y } });
      }
      return;
    }
    if (obj.type === 'sticky') {
      // Sticky editing is owned by StickyEditorController (TR7).
      this.#bus?.uiActions$.next({ type: 'edit/enter-sticky', id: objectId(id) });
    } else if (obj.type === 'text') {
      // A genuine double-click supersedes the deferred single-click-on-
      // already-selected timer (see `#onClickAlreadySelected`/the field's
      // own doc comment) — both converge on the same action, so just drop
      // the pending one rather than let it also fire moments later.
      if (this.#textboxClickTimer !== null) {
        clearTimeout(this.#textboxClickTimer);
        this.#textboxClickTimer = null;
      }
      // PRD-F6 §T19: rich-text editing owned by TextboxEditorController.
      // Anke, 2026-09-12: thread the native click coordinates through (when
      // present — synthetic test events may omit `evt`) so the caret lands
      // where the double-click actually happened, matching the deferred
      // single-re-click path below.
      const at = e?.evt ? { clientX: e.evt.clientX, clientY: e.evt.clientY } : undefined;
      this.#bus?.uiActions$.next({ type: 'edit/enter-textbox', id: objectId(id), at });
    } else if (obj.type === 'person') {
      this.openTextEditor(id);
    } else if (obj.type === 'checkbox') {
      // Moved here from the single-click handler in `wireObjectNode`
      // (Stefan, 2026-08-28) — single click now selects like every other
      // object type (deferred, see `wireObjectNode`); double-click toggles
      // `checked` instead. Deliberately does NOT also select (follow-up,
      // Stefan, 2026-08-28: "bei einem reinen Doppelklick soll die Box nur
      // ihren Zustand toggeln, nicht auch ausgewählt werden") — a genuine
      // double-click cancels the deferred single-click selection outright
      // (see `wireObjectNode`) and this branch never re-adds it, so a pure
      // double-click on an unselected checkbox toggles it while leaving
      // selection untouched (unlike every other object type's dblclick,
      // which opens an editor — there, being left selected first is what
      // makes the editor overlay make sense; a checkbox has no such editor).
      this._historyAdapter.push(new UpdatePropertyCommand(id, 'checked', !obj.checked), this.boardState);
    }
  }

  /**
   * Anke, 2026-09-11: "wenn ich einmal klicke auf die Textbox, dann wird
   * diese angewählt. wenn ich ein zweites Mal auf die bereits ausgewählte
   * Box klicke (ohne direkt mit der Maus zu ziehen, nur ein einziger
   * Klick), dann soll man direkt mit dem Cursor im Text landen." —
   * `InteractionController.onStageClick` calls this whenever a plain click
   * (no modifier key, and Konva's own `click` event already excludes a
   * drag gesture — see its doc comment) lands on an object that was
   * ALREADY selected before this click. Only textboxes react; every other
   * object type is a no-op here (a plain re-click on an already-selected
   * rectangle/sticky/etc. still does nothing, unchanged).
   *
   * Deferred via `#textboxClickTimer` — see that field's own doc comment
   * for why (avoids double-dispatching `edit/enter-textbox` together with
   * a genuine double-click's own `onNodeDoubleClick` handling).
   *
   * `clientX`/`clientY` (Anke, 2026-09-12) are captured NOW, at click time,
   * and carried into the deferred dispatch — not re-read when the timer
   * fires, since the pointer has typically moved on by then.
   */
  #onClickAlreadySelected(id: string, clientX: number, clientY: number): void {
    const obj = this.boardState.getObject(id);
    if (!obj || obj.type !== 'text') return;

    if (this.#textboxClickTimer !== null) clearTimeout(this.#textboxClickTimer);
    this.#textboxClickTimer = setTimeout(() => {
      this.#textboxClickTimer = null;
      // Re-check on fire, not just at schedule time: the object may have
      // been deleted, or the user may have since clicked away (selecting
      // something else / the empty canvas) while this was pending — either
      // way, entering edit mode now would be surprising, not "landing in
      // the text I just clicked".
      if (!this.selectionCtrl.has(id)) return;
      const current = this.boardState.getObject(id);
      if (!current || current.type !== 'text') return;
      this.#bus?.uiActions$.next({ type: 'edit/enter-textbox', id: objectId(id), at: { clientX, clientY } });
    }, (Konva as unknown as { dblClickWindow: number }).dblClickWindow);
  }

  /**
   * Open the contentEditable overlay for a person object. Sticky editing is
   * owned by `StickyEditorController` (`edit/enter-sticky`); textbox editing
   * by `TextboxEditorController` (`edit/enter-textbox`, PRD-F6 §T19) — see
   * `onNodeDoubleClick`, which already routes those two types elsewhere and
   * reaches this method for `person` only.
   */
  private openTextEditor(id: string): void {
    const obj = this.boardState.getObject(id);
    if (!obj || obj.type !== 'person') return;

    this.closeTextEditor(false); // close any previous editor without committing

    const rootContainer = this.querySelector<HTMLDivElement>('.board-root');
    if (!rootContainer) return;

    const editor = document.createElement('div');
    editor.className = 'person-editor';
    editor.contentEditable = 'true';
    editor.spellcheck = false;
    editor.textContent = obj.text;

    this.positionTextEditor(editor, obj);

    rootContainer.appendChild(editor);
    this.textEditor = editor;
    this.textEditorTargetId = id;

    // Hide the Konva text while editing so the user sees only the
    // contentEditable overlay (prevents the double-text ghosting).
    const node = this.konvaNodes.get(id);
    if (node instanceof Konva.Group) {
      node.findOne<Konva.Text>('.person-name')?.visible(false);
      this.contentLayer?.batchDraw();
    }

    const commit = (): void => this.closeTextEditor(true);
    const cancel = (): void => this.closeTextEditor(false);
    editor.addEventListener('blur', commit);
    editor.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.preventDefault(); cancel(); }
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commit(); }
    });

    // Select all so typing immediately replaces any previous content.
    editor.focus();
    const range = document.createRange();
    range.selectNodeContents(editor);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }

  /**
   * Position the person-name contentEditable overlay. Called from
   * `openTextEditor` and from `onObjectUpdated` — both call sites are
   * type-narrowed to `person` objects only; textbox editing has its own
   * overlay in `TextboxEditorController`.
   *
   * Cleanup (review 01, 2026-08-16 — WARNING, Linse A/B): this used to also
   * accept `TextBox` and position a textbox overlay, but that branch was
   * dead code (unreachable — `onNodeDoubleClick` already routes `text`
   * objects to `TextboxEditorController`, never here). Removed rather than
   * kept in sync with no live caller to catch drift.
   */
  private positionTextEditor(editor: HTMLDivElement, obj: PersonObject): void {
    // The editor sits INSIDE `.board-root`, whose origin is the stage
    // container itself, so we use stage-relative coords here — not the
    // host-relative bbox that screenBboxFor returns.
    const vp = this.boardState.viewport;

    // Read the label's placement straight off the Konva node instead of
    // recomputing it: the renderer centres the icon+name block using the
    // MEASURED text height, which cannot be derived from `obj` alone. Any
    // duplicated formula here would drift the moment the layout changes.
    const node = this.konvaNodes.get(obj.id);
    const nameText =
      node instanceof Konva.Group ? node.findOne<Konva.Text>(`.${PERSON_NAME_TEXT}`) : undefined;
    const localY = nameText?.y() ?? obj.height * PERSON_ICON_RATIO + PERSON_ICON_TEXT_GAP;
    const localH = nameText?.height() ?? Math.max(0, obj.height - localY);

    // Anke, 2026-07-22 — auf die PERSONMITTE zentriert statt an der linken
    // Objektkante verankert: Das Feld ist auto-breit (CSS `inline-block`),
    // wächst also mit dem Text, und `translateX(-50%)` hält seinen Mittelpunkt
    // auf der Personmitte — wie der Konva-Name über seine x-Position. So sitzt
    // der Text schon beim Schreiben mittig und ragt symmetrisch hinaus.
    const centerX = (obj.x + obj.width / 2) * vp.scale + vp.x;
    editor.style.left   = `${centerX}px`;
    editor.style.transform = 'translateX(-50%)';
    editor.style.top    = `${(obj.y + localY) * vp.scale + vp.y}px`;
    // Auto-Breite (kein festes `width`); eine Mindestbreite sorgt dafür, dass
    // ein leerer Name noch eine sichtbare Klickfläche/Caret hat.
    editor.style.width  = 'auto';
    editor.style.minWidth = `${obj.width * vp.scale}px`;
    // Grow downwards while typing rather than clipping at the current height.
    editor.style.height = 'auto';
    editor.style.minHeight = `${localH * vp.scale}px`;

    // Mirror the label's typography so edit mode looks like view mode — the
    // same line height the Konva label uses (`BOARD_TEXT_LINE_HEIGHT`).
    editor.style.fontSize       = `${(obj.fontSize ?? PERSON_FONT_SIZE) * vp.scale}px`;
    editor.style.lineHeight     = String(BOARD_TEXT_LINE_HEIGHT);
    editor.style.color          = obj.textColor ?? obj.color;
    editor.style.fontWeight     = String(obj.fontWeight ?? PERSON_DEFAULT_FONT_WEIGHT);
    editor.style.fontStyle      = obj.fontStyle ?? 'normal';
    editor.style.textDecoration = obj.textDecoration ?? 'none';
  }

  private closeTextEditor(commit: boolean): void {
    const editor = this.textEditor;
    const id = this.textEditorTargetId;
    if (!editor || !id) return;
    this.textEditor = undefined;
    this.textEditorTargetId = undefined;

    const nextText = editor.innerText.replace(/\n+$/, '');
    editor.remove();

    // Re-show whichever Konva text node matches this object's type.
    const node = this.konvaNodes.get(id);
    const current = this.boardState.getObject(id);
    if (node instanceof Konva.Group && current) {
      const selector = current.type === 'person' ? '.person-name' : '.textbox-text';
      node.findOne<Konva.Text>(selector)?.visible(true);
      this.contentLayer?.batchDraw();
    }

    if (!commit) return;
    if (!current || (current.type !== 'text' && current.type !== 'person')) return;
    if (current.text === nextText) return;
    this._historyAdapter.push(new UpdatePropertyCommand(id, 'text', nextText), this.boardState);
  }
}

function sameSnapshot(a: ResizeSnapshot, b: ResizeSnapshot): boolean {
  return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height && a.rotation === b.rotation;
}

/** True if two axis-aligned rectangles overlap (edges touching counts). */
function rectsIntersect(
  a: { x: number; y: number; width: number; height: number },
  b: { x: number; y: number; width: number; height: number },
): boolean {
  return (
    a.x < b.x + b.width  &&
    a.x + a.width  > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
}

declare global {
  interface HTMLElementTagNameMap {
    'o2d-whiteboard': O2dWhiteboardElement;
  }
}
